import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabase";
import { LocalNotifications } from "@capacitor/local-notifications";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { InAppReview } from "@capacitor-community/in-app-review";

// ─────────────────────────────────────────────────────────────
// FEATURE SWITCHES
// true = visible in the app, false = hidden. No data is deleted;
// flip a switch back to true to bring a feature back.
// ─────────────────────────────────────────────────────────────
const FEATURES = {
  community: false,       // Community tab, posts, "Publish entry", share-to-community
  cheers: false,          // Cheers inbox, "Send a cheer", cheers banner on Home
  circle: false,          // Your circle + mood sharing (Home share button, Profile links, 4 screens)
  hydration: false,       // Water tracker on Home
  medication: false,      // "Medication today?" in check-in, medication reminder
  dailyQuote: false,      // Daily quote card on Home (also stops the daily AI call)
  morningReminder: false, // "Morning motivation" reminder
  weeklyReminder: false,  // "Weekly reflection" reminder toggle
  todayPrompt: false,     // "Today's prompt" card on Home
  exerciseLog: false,     // "Exercise today?" in the check-in and on past entries
  homeShortcuts: false,   // "New entry" and "Sleep stories" cards on Home (the Tonight card covers both)
};
const SOCIAL_ON = FEATURES.community || FEATURES.cheers || FEATURES.circle;

// Screens that are unreachable while their feature is switched off (they fall back to Home)
const SCREEN_OFF = {
  share: !FEATURES.community, community: !FEATURES.community,
  cheers: !FEATURES.cheers, sendCheer: !FEATURES.cheers,
  circle: !FEATURES.circle, moodInbox: !FEATURES.circle, moodShare: !FEATURES.circle,
  myShares: !FEATURES.circle, myShareDetail: !FEATURES.circle,
};
const safeScreen = (s) => (SCREEN_OFF[s] ? "home" : s);

// Today's date in the person's own time zone (toISOString would give the UTC date, which is wrong in the evening for many time zones)
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function getWeekDates() {
  const d = new Date();
  const diff = (d.getDay() + 6) % 7;
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(d);
    date.setDate(d.getDate() - diff + i);
    return localDate(date);
  });
}

const C = {
  roseDk:"#2D5A4F",rose:"#4A7C6F",roseLt:"#E8F5F2",
  tealDk:"#085041",teal:"#1D9E75",tealLt:"#E1F5EE",
  amberDk:"#633806",amber:"#BA7517",amberLt:"#FAEEDA",
  purpleDk:"#3C3489",purple:"#7F77DD",purpleLt:"#EEEDFE",
  blueDk:"#0C447C",blue:"#378ADD",blueLt:"#E6F1FB",
  ink:"#2C2A28",mid:"#6b6760",paper:"#FAF8F5",
};

const css = `
*{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
body{font-family:'Nunito',sans-serif;background:#2D5A4F;display:flex;justify-content:center;min-height:100vh;}
#root{width:100%;max-width:390px;margin:0 auto;}
.phone{background:#FAF8F5;min-height:100vh;display:flex;flex-direction:column;}
.screen{flex:1;display:flex;flex-direction:column;animation:fi .3s ease;}
@keyframes fi{from{opacity:0;transform:translateX(10px)}to{opacity:1;transform:none}}
.hdr{padding:52px 20px 22px;}
.back{font-size:11px;color:rgba(255,255,255,.55);margin-bottom:12px;cursor:pointer;display:inline-block;}
.htitle{font-family:'Playfair Display',serif;font-size:22px;color:white;}
.hsub{font-size:11px;color:rgba(255,255,255,.5);margin-top:4px;}
.body{padding:14px 16px 100px;flex:1;overflow-y:auto;}
.nav{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:390px;display:flex;background:white;border-top:.5px solid #e8e2da;padding:10px 0 24px;z-index:100;}
.nav-dark{background:#0d1117;border-top:.5px solid #1e2633;}
.nav-dark .ni-l{color:#8b94a3;}
.nav-dark .ni.on .ni-l{color:#fff;}
.phone.night{background:#e6dfd3;}
.night .card,.night .tag,.night .ta{background:#efe9de;border-color:#d6cec0;}
.night .nav:not(.nav-dark){background:#e6dfd3;border-top-color:#d6cec0;}
.ni{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;padding:6px 0;}
.ni-i{font-size:24px;}
.ni-l{font-size:10px;color:#6b6760;margin-top:2px;}
.ni.on .ni-l{color:#2D5A4F;font-weight:600;}
.ni-d{width:4px;height:4px;border-radius:50%;background:#4A7C6F;display:none;margin:0 auto;}
.ni.on .ni-d{display:block;}
input,textarea{font-family:'Nunito',sans-serif;outline:none;}
button{font-family:'Nunito',sans-serif;cursor:pointer;border:none;}
.card{background:white;border-radius:14px;border:.5px solid #e8e2da;padding:14px;margin-bottom:10px;}
.tag{padding:6px 12px;border-radius:20px;font-size:11px;font-weight:600;border:.5px solid #e0dbd4;color:#6b6760;cursor:pointer;background:white;transition:all .15s;display:inline-flex;align-items:center;gap:5px;}
.tag.rz{background:#E8F5F2;border-color:#4A7C6F;color:#2D5A4F;}
.tag.bz{background:#E6F1FB;border-color:#378ADD;color:#0C447C;}
.pbtn{background:#2D5A4F;color:white;border-radius:12px;padding:13px;width:100%;font-size:14px;font-weight:600;transition:opacity .15s;}
.pbtn:disabled{opacity:.5;cursor:not-allowed;}
.slbl{font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:#6b6760;margin-bottom:8px;}
.spin{width:18px;height:18px;border:2px solid rgba(255,255,255,.3);border-top-color:white;border-radius:50%;animation:sp .7s linear infinite;display:inline-block;vertical-align:middle;}
@keyframes sp{to{transform:rotate(360deg)}}
.td{width:6px;height:6px;border-radius:50%;background:#1D9E75;animation:bo 1.2s infinite;display:inline-block;margin:0 2px;}
.td:nth-child(2){animation-delay:.2s}.td:nth-child(3){animation-delay:.4s}
@keyframes slideUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}} @keyframes bo{0%,80%,100%{transform:scale(.6);opacity:.4}40%{transform:scale(1);opacity:1}}
.err{background:#FCEBEB;border:.5px solid #F09595;border-radius:10px;padding:10px 13px;font-size:12px;color:#A32D2D;margin-bottom:12px;}
.ok{background:#E1F5EE;border:.5px solid #1D9E75;border-radius:10px;padding:10px 13px;font-size:12px;color:#085041;margin-bottom:12px;}
.inp{width:100%;padding:12px 14px;border-radius:10px;border:.5px solid #e0dbd4;background:white;font-size:14px;color:#2C2A28;margin-bottom:14px;}
.ta{width:100%;background:#FAF8F5;border:.5px solid #e0dbd4;border-radius:10px;padding:11px 13px;font-size:15px;color:#2C2A28;resize:none;line-height:1.6;}
`;

// Sends the signed-in user's token so the server can tell real users from strangers
async function aiHeaders() {
  const h = { "Content-Type": "application/json" };
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) h.Authorization = `Bearer ${data.session.access_token}`;
  } catch (e) {}
  return h;
}

// ---- AI consent: nothing is sent to the AI service until the person says yes ----
let _consentAsker = null;
const aiConsentKey = (id) => `harlo_ai_consent_${id}`;
const readAiConsent = (id) => { try { return localStorage.getItem(aiConsentKey(id)); } catch (e) { return null; } };
const writeAiConsent = (id, v) => { try { localStorage.setItem(aiConsentKey(id), v); } catch (e) {} };

// Resolves true if the person allows AI replies. Asks once, the first time it is needed.
// With { reask: true } (they tapped something that needs a reply) it asks again after an earlier "Not now".
async function ensureAiConsent(opts = {}) {
  let id = null;
  try { const { data } = await supabase.auth.getSession(); id = data?.session?.user?.id || null; } catch (e) {}
  if (!id) return false;
  const saved = readAiConsent(id);
  if (saved === "yes") return true;
  if (saved === "no" && !opts.reask) return false;
  if (!_consentAsker) return false;
  const ok = await _consentAsker();
  writeAiConsent(id, ok ? "yes" : "no");
  return ok;
}

// Returns the reply text, or null if the person has not allowed AI replies
async function getAI(text, opts = {}) {
  if (!(await ensureAiConsent(opts))) return null;
  try {
    const r = await fetch("https://harlo-app.vercel.app/api/ai-reflect", {
      method: "POST",
      headers: await aiHeaders(),
      body: JSON.stringify({ text })
    });
    const d = await r.json();
    return d.reply || "Thank you for sharing. Your feelings matter.";
  } catch (err) {
    return "Thank you for sharing that with me. Your feelings matter.";
  }
}

function Spinner({ color }) {
  return <div className="spin" style={color ? { borderTopColor: color } : {}}></div>;
}

function Nav({ active, go }) {
  const tabs = [
    ["home", "home", "🏠", "Home"],
    ["write", "entry", "✍️", "Write"],
    ["sleep", "sleep", "🌙", "Sleep"],
    ["reflect", "reflect", "📖", "Journal"],
    ["community", "community", "👥", "Community"],
    ["settings", "profile", "⚙️", "Settings"],
  ].filter(([k]) => k !== "community" || FEATURES.community);
  return (
    <nav className={`nav${active === "sleep" ? " nav-dark" : ""}`}>
      {tabs.map(([k, sc, ic, label]) => (
        <div key={k} className={`ni${active === k ? " on" : ""}`} onClick={() => go(sc)}>
          <span className="ni-i">{ic}</span>
          <span className="ni-l">{label}</span>
        </div>
      ))}
    </nav>
  );
}

function AuthScreen() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [load, setLoad] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const submit = async () => {
    if (!email.trim() || !pw.trim()) { setErr("Please enter your email and password."); return; }
    setLoad(true); setErr(""); setOk("");
    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: pw });
        if (error) setErr(error.message);
      } else {
        const { error } = await supabase.auth.signUp({ email: email.trim(), password: pw });
        if (error) setErr(error.message);
        else setOk("Account created! Check your email to confirm, then sign in.");
      }
    } catch (e) { setErr("Something went wrong. Please try again."); }
    setLoad(false);
  };

  return (
    <div className="screen" style={{ background: C.roseDk }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 0 20px" }}>
        <div style={{ textAlign: "center" }}><div style={{ fontFamily: "'Playfair Display',serif", fontSize: 56, color: "white", fontWeight: 600, letterSpacing: "-1px" }}>Harlo</div><div style={{ fontSize: 12, color: "rgba(255,255,255,.6)", letterSpacing: ".2em", textTransform: "uppercase", marginTop: 10, animation: "slideUp .8s ease .3s both" }}>Your evening wind-down</div></div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)", letterSpacing: ".1em", textTransform: "uppercase", marginTop: 6 }}>Check in · Unwind · Sleep</div>
      </div>
      <div style={{ background: C.paper, borderRadius: "28px 28px 0 0", padding: "28px 24px 48px" }}>
        <div style={{ display: "flex", background: "#eee9e2", borderRadius: 10, padding: 3, marginBottom: 20 }}>
          {[["login","Sign in"],["signup","Sign up"]].map(([m,l]) => (
            <button key={m} onClick={() => { setMode(m); setErr(""); setOk(""); }} style={{ flex: 1, padding: "8px 0", fontSize: 13, fontWeight: 600, borderRadius: 8, background: mode===m?"white":"transparent", color: mode===m?C.roseDk:C.mid, border: "none" }}>{l}</button>
          ))}
        </div>
        {err && <div className="err">{err}</div>}
        {ok && <div className="ok">{ok}</div>}
        <div className="slbl">Email</div>
        <input className="inp" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" />
        <div className="slbl">Password</div>
        <input className="inp" type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="at least 6 characters" style={{ marginBottom: 20 }} />
        <button className="pbtn" onClick={submit} disabled={load}>
          {load ? <><Spinner />&nbsp;{mode==="login"?"Signing in...":"Creating account..."}</> : mode==="login"?"Sign in":"Create account"}
        </button>
        {mode === "login" && (
          <div style={{ textAlign: "center", marginTop: 14 }}>
            <button onClick={async () => {
              if (!email.trim()) { setErr("Enter your email above first."); return; }
              setLoad(true); setErr(""); setOk("");
              const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: "https://harlo-app.vercel.app" });
              setLoad(false);
              if (error) setErr(error.message);
              else setOk("Password reset email sent! Check your inbox.");
            }} style={{ background: "none", border: "none", fontSize: 12, color: C.mid, cursor: "pointer", textDecoration: "underline" }}>
              Forgot password?
            </button>
          </div>
        )}
        {mode === "signup" && (
          <div style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: C.mid, lineHeight: 1.6 }}>
            By signing up you confirm you are 16 or older and agree to our Terms of Service and Privacy Policy
          </div>
        )}
      </div>
    </div>
  );
}

function HomeScreen({ go, user, cheers }) {
  const unread = cheers.filter(c => !c.is_read).length;
  const hr = new Date().getHours();
  const greet = hr < 12 ? "morning" : hr < 17 ? "afternoon" : "evening";
  const name = user?.user_metadata?.display_name || user?.email?.split("@")[0] || "there";
  const today = localDate();
  const [quote, setQuote] = useState("");
  const [streak, setStreak] = useState(0);
  const [nightDates, setNightDates] = useState([]);
  const [weekMoods, setWeekMoods] = useState([null,null,null,null,null,null,null]);
  const [todayMood, setTodayMood] = useState(null);
  const [savingMood, setSavingMood] = useState(false);
  const [pendingShareMood, setPendingShareMood] = useState(null);
  const [sharingMood, setSharingMood] = useState(false);
  const [shareToast, setShareToast] = useState("");
  const [circleCount, setCircleCount] = useState(0);
  const [quickToast, setQuickToast] = useState("");
  const [promptIdx, setPromptIdx] = useState(0);
  const [streakMilestone, setStreakMilestone] = useState(null);
  const [water, setWater] = useState(0);
  const [pick, setPick] = useState(null);
  useEffect(() => { getTonightPick(user.id).then(setPick); }, [user.id, todayMood]);

  const MILESTONES = [
    { days: 7,   emoji: "🔥", title: "7 day streak!", message: "One whole week. You're building something real." },
    { days: 14,  emoji: "✨", title: "2 weeks strong!", message: "Two weeks of showing up for yourself. That's not nothing." },
    { days: 30,  emoji: "🌿", title: "30 days!", message: "A full month. You've made this part of your life." },
    { days: 60,  emoji: "💚", title: "60 days!", message: "Two months of checking in. Your future self is grateful." },
    { days: 100, emoji: "🏆", title: "100 days!", message: "100 days. You are absolutely incredible." },
    { days: 365, emoji: "🌟", title: "One whole year!", message: "A year of showing up for yourself. This is who you are now." },
  ];

  const PROMPTS = [
    "What moment from today deserves to be remembered?",
    "What emotion have you been carrying without naming?",
    "What would you tell yourself one week ago?",
    "Where did you surprise yourself recently?",
    "What does 'enough' look like for you today?",
    "What's one thing you want to let go of this week?",
    "What made you smile genuinely today?",
  ];
  const MOODS = [
    { l:"Great", e:"😄", score:5 },
    { l:"Good",  e:"🙂", score:4 },
    { l:"Okay",  e:"😐", score:3 },
    { l:"Low",   e:"😔", score:2 },
    { l:"Sad",   e:"😢", score:1 },
  ];

  useEffect(() => {
    const weekDates = getWeekDates();
    setPromptIdx(Math.floor(Math.random() * PROMPTS.length));
    if (FEATURES.dailyQuote) supabase.from("profiles").select("daily_quote,quote_date").eq("id", user.id).single()
      .then(async ({ data }) => {
        if (data?.daily_quote && data?.quote_date === today) { setQuote(data.daily_quote); return; }
        const { data: entries } = await supabase.from("entries").select("tags").eq("user_id", user.id).order("created_at", { ascending: false }).limit(5);
        const moods = entries?.flatMap(e => e.tags || []).join(", ") || "general wellbeing";
        const r = await fetch("https://harlo-app.vercel.app/api/ai-reflect", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ text: "Write a short motivational quote (1 sentence, max 15 words) for someone with moods: " + moods + ". Be warm." }) });
        const d2 = await r.json();
        const q = d2.reply || "You are doing better than you think.";
        setQuote(q);
        supabase.from("profiles").update({ daily_quote: q, quote_date: today }).eq("id", user.id);
      });
    if (FEATURES.hydration) supabase.from("hydration_logs").select("glasses").eq("user_id", user.id).eq("log_date", today).single()
      .then(({ data }) => { if (data) setWater(data.glasses); });
    supabase.from("mood_logs").select("log_date,mood_score,mood_label")
      .eq("user_id", user.id).gte("log_date", weekDates[0]).lte("log_date", weekDates[6])
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(m => { map[m.log_date] = m; });
        setWeekMoods(weekDates.map(d => map[d] || null));
        if (map[today]) setTodayMood(map[today].mood_score);
      });
    supabase.from("mood_logs").select("log_date").eq("user_id", user.id)
      .order("log_date", { ascending: false }).limit(400)
      .then(({ data }) => {
        if (!data || data.length === 0) { setStreak(0); return; }
        const dates = data.map(d => d.log_date);
        setNightDates(dates);
        let count = 0;
        const check = new Date();
        if (!dates.includes(localDate(check))) check.setDate(check.getDate() - 1);
        while (true) {
          const s = localDate(check);
          if (dates.includes(s)) { count++; check.setDate(check.getDate() - 1); } else break;
        }
        setStreak(count);
        const milestone = MILESTONES.find(m => m.days === count);
        if (milestone) {
          const seenKey = `harlo_milestone_${user.id}_${count}`;
          if (!localStorage.getItem(seenKey)) {
            setStreakMilestone(milestone);
            localStorage.setItem(seenKey, "1");
          }
        }
      });
  }, [user.id]);

  useEffect(() => {
    supabase
      .from("mood_circles")
      .select("id", { count: "exact", head: true })
      .eq("owner_id", user.id)
      .eq("status", "accepted")
      .then(({ count }) => setCircleCount(count || 0));
  }, [user.id]);

  const logMood = async (mood) => {
    if (savingMood) return;
    if (navigator.vibrate) navigator.vibrate(10);
    setSavingMood(true);
    setTodayMood(mood.score);
    const weekDates = getWeekDates();
    const todayIdx = weekDates.indexOf(today);
    setWeekMoods(prev => prev.map((m, i) => i === todayIdx ? { mood_score: mood.score, mood_label: mood.l, log_date: today } : m));
    await supabase.from("mood_logs").upsert(
      { user_id: user.id, log_date: today, mood_score: mood.score, mood_label: mood.l },
      { onConflict: "user_id,log_date" }
    );
    setSavingMood(false);
  };

  const shareMoodWithCircle = async (mood) => {
    if (sharingMood) return;
    setSharingMood(true);
    try {
      // Save the share record
      const { data: share, error: shareErr } = await supabase
        .from("mood_shares")
        .insert({ user_id: user.id, mood: mood.l })
        .select("id")
        .single();
      if (shareErr) throw shareErr;

      // Look up circle members + sender username for the push body
      const { data: circle } = await supabase
        .from("mood_circles")
        .select("member_id")
        .eq("owner_id", user.id)
        .eq("status", "accepted");
      const { data: senderProfile } = await supabase
        .from("profiles").select("username, display_name").eq("id", user.id).single();
      const senderName = senderProfile?.username || senderProfile?.display_name || "Someone";

      // Fire push to each member via the Vercel function
      const memberIds = (circle || []).map(c => c.member_id);
      let sentCount = 0;
      await Promise.all(memberIds.map(async (mid) => {
        try {
          const res = await fetch("https://harlo-app.vercel.app/api/send-push", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: mid,
              title: `${senderName} shared a mood`,
              body: `${senderName} is feeling ${mood.l.toLowerCase()} ${mood.e}`,
              data: { share_id: share.id, type: "mood_share" },
              environment: "development"
            }),
          });
          if (res.ok) {
            const json = await res.json();
            if ((json.sent || 0) > 0) sentCount++;
          }
        } catch (e) { /* one member failing shouldn't block others */ }
      }));

      setShareToast(`Shared with ${memberIds.length} ${memberIds.length === 1 ? "person" : "people"} 🌿`);
      setTimeout(() => setShareToast(""), 3500);
    } catch (e) {
      setShareToast("Could not share — please try again");
      setTimeout(() => setShareToast(""), 3500);
    } finally {
      setPendingShareMood(null);
      setSharingMood(false);
    }
  };

  const logWater = async (delta) => {
    const newVal = Math.max(0, Math.min(12, water + delta));
    setWater(newVal);
    await supabase.from("hydration_logs").upsert({ user_id: user.id, log_date: today, glasses: newVal }, { onConflict: "user_id,log_date" });
  };

  const currentMilestone = MILESTONES.filter(m => streak >= m.days).pop();
  // Lifetime nights checked in: never resets, unlike the streak
  const nights = new Set(nightDates).size + (todayMood && !nightDates.includes(today) ? 1 : 0);
  const DAY_LABELS = ["M","T","W","T","F","S","S"];
  const weekDates = getWeekDates();
  const todayIdx = weekDates.indexOf(today);

  return (
    <div className="screen">
      {streakMilestone && (
        <div style={{ position:"fixed", top:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:390, height:"100%", background:"rgba(0,0,0,0.6)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setStreakMilestone(null)}>
          <div style={{ background:"white", borderRadius:24, padding:"36px 28px", margin:"0 20px", textAlign:"center", maxWidth:340 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:64, marginBottom:16 }}>{streakMilestone.emoji}</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:600, color:C.roseDk, marginBottom:10 }}>{streakMilestone.title}</div>
            <div style={{ fontSize:15, color:C.mid, lineHeight:1.7, marginBottom:24 }}>{streakMilestone.message}</div>
            <div style={{ background:C.roseLt, borderRadius:12, padding:"10px 16px", marginBottom:24, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <span style={{ fontSize:20 }}>🔥</span>
              <span style={{ fontSize:18, fontWeight:700, color:C.roseDk }}>{streak} day streak</span>
            </div>
            <button onClick={() => setStreakMilestone(null)} style={{ width:"100%", padding:"13px", borderRadius:12, border:"none", background:C.roseDk, color:"white", fontSize:15, fontWeight:600, cursor:"pointer" }}>Keep going ✨</button>
          </div>
        </div>
      )}
      <div style={{ background: C.roseDk, paddingTop: 52, paddingBottom: 20, paddingLeft: 20, paddingRight: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>Good {greet}</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, color: "white", fontWeight: 600 }}>Hello, {name}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.45)", marginTop: 2 }}>{new Date().toLocaleDateString("en-AU", { weekday: "long", month: "long", day: "numeric" })}</div>
          </div>
          {streak > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.15)", borderRadius: 99, padding: "6px 12px", cursor: currentMilestone ? "pointer" : "default" }} onClick={() => currentMilestone && setStreakMilestone(currentMilestone)}>
              <span style={{ fontSize: 16 }}>{currentMilestone ? currentMilestone.emoji : "🔥"}</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "white", lineHeight: 1 }}>{streak}</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,.7)", lineHeight: 1, marginTop: 2 }}>day streak</div>
              </div>
            </div>
          )}
        </div>
        <div style={{ background: "rgba(0,0,0,0.2)", borderRadius: 14, padding: "12px 14px" }}>
          <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".08em", color: "rgba(255,255,255,.55)", fontWeight: 600, marginBottom: 10 }}>How are you feeling today?</div>
          <div style={{ display: "flex", gap: 6 }}>
            {MOODS.map(m => (
              <button key={m.score} onClick={() => logMood(m)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "8px 4px", borderRadius: 10, border: "none", background: todayMood === m.score ? "rgba(255,255,255,.3)" : "rgba(255,255,255,.1)", cursor: "pointer" }}>
                <span style={{ fontSize: 20 }}>{m.e}</span>
                <span style={{ fontSize: 9, color: "rgba(255,255,255,.7)", fontWeight: 600 }}>{m.l}</span>
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 5, justifyContent: "center", marginTop: 10 }}>
            {weekMoods.map((m, i) => (
              <div key={i} style={{ width: i === todayIdx ? 10 : 8, height: i === todayIdx ? 10 : 8, borderRadius: "50%", background: m ? "#F4C0D1" : "rgba(255,255,255,.2)", outline: i === todayIdx ? "2px solid rgba(255,255,255,.5)" : "none" }} />
            ))}
          </div>
          {FEATURES.circle && (<>
          <button
            onClick={() => {
              if (!todayMood) {
                setQuickToast("Log your mood first 👆");
                setTimeout(() => setQuickToast(""), 2500);
                return;
              }
              if (circleCount === 0) {
                setQuickToast("Add friends to your circle first");
                setTimeout(() => setQuickToast(""), 2500);
                return;
              }
              const mood = MOODS.find(m => m.score === todayMood);
              if (mood) shareMoodWithCircle(mood);
            }}
            disabled={sharingMood}
            style={{
              width: "100%",
              marginTop: 12,
              padding: "10px 14px",
              borderRadius: 10,
              border: "none",
              background: "rgba(255,255,255,.18)",
              color: "white",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <span>🌿</span>
            <span>{sharingMood ? "Sharing..." : "Share with your circle"}</span>
          </button>
          <div onClick={() => go("circle")} style={{ marginTop: 8, textAlign: "center", fontSize: 10, color: "rgba(255,255,255,.55)", cursor: "pointer", textDecoration: "underline" }}>
            {circleCount === 0 ? "+ Add friends to your circle" : `Manage your circle (${circleCount})`}
          </div>
          </>)}
        </div>
      </div>
      <div className="body">
        <div style={{ background: "linear-gradient(135deg,#1a2a3a 0%,#2a1a3a 100%)", borderRadius: 16, padding: "16px 16px 4px", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".1em", color: "rgba(255,255,255,.5)", fontWeight: 600 }}>{hr >= 17 || hr < 4 ? "Tonight" : "Today"}</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: "white", fontWeight: 600, marginTop: 2 }}>{hr >= 17 || hr < 4 ? "Wind down with Harlo" : "A moment for you"}</div>
              {nights > 0 && <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)", marginTop: 3 }}>🌙 {nights} {nights === 1 ? "night" : "nights"} checked in</div>}
            </div>
            <span style={{ fontSize: 28 }}>🌙</span>
          </div>
          {[
            { n: 1, t: "Check in", s: todayMood ? "Mood logged for today" : "Log your mood or write a few words", done: !!todayMood, sc: "entry" },
            { n: 2, t: pick ? "Your story for tonight" : "Sleep story", s: window._harloAudioId ? "Now playing" : (pick ? `${pick.title}. ${pick.why}` : (hr >= 17 || hr < 4 ? "Drift off with a calming story" : "Save one for tonight")), done: false, sc: "sleep" },
          ].map(step => (
            <div key={step.n} onClick={() => go(step.sc)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: ".5px solid rgba(255,255,255,.12)", cursor: "pointer" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: step.done ? C.teal : "rgba(255,255,255,.12)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{step.done ? "✓" : step.n}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "white" }}>{step.t}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)", marginTop: 1 }}>{step.s}</div>
              </div>
              <div style={{ color: "rgba(255,255,255,.4)", fontSize: 18 }}>›</div>
            </div>
          ))}
        </div>
        {FEATURES.circle && pendingShareMood && (
          <div style={{ background: C.roseLt, border: ".5px solid #F4C0D1", borderRadius: 14, padding: "12px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.roseDk, marginBottom: 2 }}>Share with your circle?</div>
              <div style={{ fontSize: 11, color: C.rose }}>Let them know how you are doing</div>
            </div>
            <button onClick={() => shareMoodWithCircle(pendingShareMood)} disabled={sharingMood} style={{ background: C.roseDk, color: "white", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{sharingMood ? "..." : "Share"}</button>
            <button onClick={() => setPendingShareMood(null)} style={{ background: "none", color: C.mid, border: "none", fontSize: 16, cursor: "pointer", padding: "4px 6px" }}>x</button>
          </div>
        )}
        {shareToast && (
          <div style={{ background: C.tealLt, border: `.5px solid ${C.teal}`, borderRadius: 14, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.tealDk, textAlign: "center", fontWeight: 600 }}>
            {shareToast}
          </div>
        )}
        {quickToast && (
          <div style={{ background: C.amberLt, border: `.5px solid ${C.amber}`, borderRadius: 14, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.amberDk, textAlign: "center", fontWeight: 600 }}>
            {quickToast}
          </div>
        )}
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div className="slbl" style={{ marginBottom: 0 }}>Mood this week</div>
            <div onClick={() => go("insights")} style={{ fontSize: 11, color: C.teal, fontWeight: 600, cursor: "pointer" }}>Your week →</div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 70 }}>
            {weekMoods.map((m, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ width: "100%", height: m ? `${(m.mood_score / 5) * 56}px` : "6px", borderRadius: 6, background: i === todayIdx ? C.rose : m ? C.roseDk : "#e8e2da", opacity: m ? 1 : 0.4 }} />
                <div style={{ fontSize: 9, color: i === todayIdx ? C.roseDk : C.mid, fontWeight: i === todayIdx ? 700 : 400 }}>{DAY_LABELS[i]}</div>
              </div>
            ))}
          </div>
        </div>
        {FEATURES.hydration && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>💧 Hydration</div>
              <div style={{ fontSize: 11, color: C.mid, marginTop: 1 }}>{water} of 8 glasses today</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={() => logWater(-1)} style={{ width: 32, height: 32, borderRadius: "50%", border: `.5px solid ${C.teal}`, background: "white", fontSize: 18, color: C.teal, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.tealDk, minWidth: 20, textAlign: "center" }}>{water}</div>
              <button onClick={() => logWater(1)} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: C.teal, fontSize: 18, color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
            </div>
          </div>
          <div style={{ height: 6, background: "#e8e2da", borderRadius: 99, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, (water / 8) * 100)}%`, height: "100%", background: water >= 8 ? C.teal : C.tealDk, borderRadius: 99, transition: "width .3s ease" }} />
          </div>
          {water >= 8 && <div style={{ fontSize: 11, color: C.teal, fontWeight: 600, marginTop: 6, textAlign: "center" }}>🎉 Goal reached! Great work today.</div>}
          <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={{ flex: 1, height: 20, borderRadius: 4, background: i < water ? C.teal : "#e8e2da", transition: "background .2s" }} />
            ))}
          </div>
        </div>
        )}
        {FEATURES.todayPrompt && (
        <div onClick={() => go("entry")} style={{ background: C.amberLt, borderRadius: 14, padding: "12px 14px", border: `.5px solid ${C.amber}`, marginBottom: 12, cursor: "pointer" }}>
          <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".08em", color: C.amberDk, fontWeight: 600, marginBottom: 6 }}>✦ Today's prompt</div>
          <div style={{ fontSize: 13, color: C.amberDk, lineHeight: 1.6, fontStyle: "italic" }}>{PROMPTS[promptIdx]}</div>
          <div style={{ fontSize: 11, color: C.amber, marginTop: 6 }}>Tap to write →</div>
        </div>
        )}
        {FEATURES.cheers && unread > 0 && (
          <div onClick={() => go("cheers")} style={{ background: C.roseLt, borderRadius: 14, padding: "12px 14px", border: ".5px solid #F4C0D1", marginBottom: 12, cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.roseDk }}>✦ Cheers for you</div>
              <div style={{ background: C.rose, color: "white", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>{unread} new</div>
            </div>
            <div style={{ fontSize: 11, color: C.roseDk, fontStyle: "italic" }}>Tap to see messages from your supporters</div>
          </div>
        )}
        {[
          { ic:"✍️", t:"New entry", s:"Write how you feel today", col:C.roseLt, sc:"entry", on: FEATURES.homeShortcuts },
          { ic:"💬", t:"Journal", s:"Your entries and Harlo's replies", col:C.tealLt, sc:"reflect" },
          { ic:"📤", t:"Publish entry", s:"Share to your community", col:C.amberLt, sc:"share", on: FEATURES.community },
          { ic:"✦",  t:"Send a cheer", s:"Encourage a friend", col:C.roseLt, sc:"cheers", on: FEATURES.cheers },
          { ic:"👥", t:"Community", s:"Connect with others", col:C.purpleLt, sc:"community", on: FEATURES.community },

          { ic:"🌙", t:"Sleep stories", s:"Drift off with a calming story", col:"#EEF2FF", sc:"sleep", on: FEATURES.homeShortcuts },
          { ic:"✉️", t:"Letters to yourself", s:"Write to your future self", col:"#f0f7f0", sc:"letter" },
          { ic:"🫂", t:"I'm struggling", s:"Breathing, grounding & helplines", col:"#e8e8f0", sc:"crisis" },
        ].filter(card => card.on !== false).map(card => (
          <div key={card.sc} onClick={() => go(card.sc)} className="card" style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: card.col, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{card.ic}</div>
            <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{card.t}</div><div style={{ fontSize: 11, color: C.mid, marginTop: 1 }}>{card.s}</div></div>
            <div style={{ color: "#c0b8ae", fontSize: 18 }}>›</div>
          </div>
        ))}
        {FEATURES.dailyQuote && quote && (
          <div style={{ background: "#EEEDFE", borderRadius: 14, padding: "14px 16px", border: ".5px solid #7F77DD", marginTop: 4 }}>
            <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".1em", color: "#3C3489", marginBottom: 6, fontWeight: 600 }}>✦ Your daily quote</div>
            <div style={{ fontSize: 13, color: "#3C3489", lineHeight: 1.7, fontStyle: "italic" }}>{quote.replace(/\*\*/g,'').replace(/\*/g,'')}</div>
          </div>
        )}
        
      </div>
      <Nav active="home" go={go} />
    </div>
  );
}


function EntryScreen({ go, user, onSave }) {
  const [text, setText] = useState("");
  const [medTaken, setMedTaken] = useState(null);
  const [gratitude, setGratitude] = useState(["", "", ""]);
  const [showAllGrat, setShowAllGrat] = useState(false);
  const [exercised, setExercised] = useState(false);
  const [exerciseMins, setExerciseMins] = useState("");
  useEffect(() => {
    const today = new Date();
    const d = today.getFullYear()+"-"+String(today.getMonth()+1).padStart(2,"0")+"-"+String(today.getDate()).padStart(2,"0");
    if (FEATURES.medication) supabase.from("medication_logs").select("taken").eq("user_id", user.id).eq("log_date", d).single().then(({ data }) => { if (data) setMedTaken(data.taken); });
  }, [user.id]);
  const [tags, setTags] = useState([]);
  const [date, setDate] = useState(localDate());
  const [saving, setSaving] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [err, setErr] = useState("");
  const allTags = [
    { l:"Loved",      e:"🥰", s:"rz" },
    { l:"Proud",      e:"✨", s:"rz" },
    { l:"Hopeful",    e:"🌱", s:"rz" },
    { l:"Grateful",   e:"🙏", s:"rz" },
    { l:"Tired",      e:"😴", s:"bz" },
    { l:"Anxious",    e:"😰", s:"rz" },
    { l:"Frustrated", e:"😣", s:"rz" },
    { l:"Sad",        e:"😔", s:"bz" },
  ];
  const tog = l => setTags(t => t.includes(l) ? t.filter(x => x !== l) : [...t, l]);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true); setErr("");
    const ai = await getAI(text);
    const gratitudeList = gratitude.filter(g => g.trim());
    const { error } = await supabase.from("entries").insert({ user_id: user.id, entry_date: date, text: text.trim(), tags, ai_reply: ai || "", self_reply: "", gratitude: gratitudeList.length ? gratitudeList : null });
    if (error) { setErr("Could not save. Try again."); setSaving(false); return; }
    if (exercised) await supabase.from("exercise_logs").upsert({ user_id: user.id, log_date: date, exercised: true, minutes: exerciseMins ? parseInt(exerciseMins) : null }, { onConflict: "user_id,log_date" });
    onSave();
    if (looksLikeCrisis(text)) { setSaving(false); setShowSupport(true); return; }
    go("reflect");
    maybeAskReview(user.id);
  };

  return (
    <div className="screen">
      {showSupport && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(20,30,28,.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: C.paper, borderRadius: 20, padding: "26px 22px", maxWidth: 380, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 10 }}>💛</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 600, color: C.ink, marginBottom: 10 }}>Thank you for writing that down</div>
            <div style={{ fontSize: 13, color: C.mid, lineHeight: 1.7, marginBottom: 18 }}>Your entry is saved. If you're feeling unsafe or thinking about hurting yourself, you don't have to carry it alone. Support is available right now, any time of day.</div>
            <button className="pbtn" onClick={() => go("crisis")} style={{ marginBottom: 10 }}>See support options</button>
            <button onClick={() => go("reflect")} style={{ background: "none", color: C.mid, fontSize: 13, padding: "8px 0", fontWeight: 600 }}>Continue to my reply</button>
            <div style={{ fontSize: 11, color: C.mid, marginTop: 12, lineHeight: 1.6 }}>In an emergency, call your local emergency number (000 in Australia).</div>
          </div>
        </div>
      )}
      <div className="hdr" style={{ background: C.roseDk }}>
        <div className="back" onClick={() => go("home")}></div>
        <div className="htitle">New entry</div>
      </div>
      <div className="body">
        {err && <div className="err">{err}</div>}
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, background: C.roseLt, borderRadius: 10, padding: "9px 12px" }}>
            <div style={{ fontSize: 9, textTransform: "uppercase", color: C.roseDk, marginBottom: 3 }}>Today</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.roseDk }}>{new Date().toLocaleDateString("en-AU", { month: "short", day: "numeric", year: "numeric" })}</div>
          </div>
          <div style={{ flex: 1, background: C.tealLt, borderRadius: 10, padding: "9px 12px" }}>
            <div style={{ fontSize: 9, textTransform: "uppercase", color: C.tealDk, marginBottom: 3 }}>Select date</div>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: 11, fontWeight: 600, color: C.tealDk, background: "transparent", border: "none", width: "100%", padding: 0 }} />
          </div>
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Today I felt..." className="ta" style={{ minHeight: 140, borderRadius: 12, padding: "12px 14px", fontSize: 14, marginBottom: 14 }} />

        <div style={{ background: "#FFFBF0", borderRadius: 12, padding: "14px", marginBottom: 14, border: ".5px solid #F4C0D1" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: C.amberDk, fontWeight: 600, marginBottom: 10 }}>{showAllGrat ? "🌿 3 things I'm grateful for today" : "🌿 Something I'm grateful for today"}</div>
          {gratitude.map((g, i) => ({ g, i })).filter(({ g, i }) => i === 0 || showAllGrat || g.trim()).map(({ g, i }, idx, arr) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: idx < arr.length - 1 ? 8 : 0 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: g.trim() ? C.teal : "#e8e2da", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "white", flexShrink: 0 }}>{i + 1}</div>
              <input
                value={g}
                onChange={e => { const updated = [...gratitude]; updated[i] = e.target.value; setGratitude(updated); }}
                placeholder={["Something small that made me smile...", "Someone I appreciate...", "Something I often take for granted..."][i]}
                style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: `.5px solid ${g.trim() ? C.teal : "#e0dbd4"}`, background: "white", fontSize: 13, color: C.ink, outline: "none", fontFamily: "'Nunito', sans-serif" }}
              />
            </div>
          ))}
          {showAllGrat
            ? <div style={{ fontSize: 11, color: C.mid, marginTop: 10, fontStyle: "italic" }}>Optional — even one is enough.</div>
            : <div onClick={() => setShowAllGrat(true)} style={{ fontSize: 12, color: C.amberDk, marginTop: 10, fontWeight: 600, cursor: "pointer" }}>+ add more</div>}
        </div>

        {FEATURES.medication && <div onClick={async () => { const next = medTaken !== true; setMedTaken(next); await supabase.from("medication_logs").upsert({ user_id: user.id, log_date: date, taken: next }, { onConflict: "user_id,log_date" }); }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: medTaken ? "#E8F5F2" : "#F5F5F5", borderRadius: 12, padding: "12px 14px", marginBottom: 8, cursor: "pointer" }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 20 }}>💊</span><div><div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Medication today?</div><div style={{ fontSize: 12, color: C.mid, marginTop: 1 }}>{medTaken ? "Taken" : "Tap to log"}</div></div></div><div style={{ width: 26, height: 26, borderRadius: "50%", background: medTaken ? C.teal : "#e0dbd4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "white" }}>{medTaken ? "✓" : ""}</div></div>}

        {FEATURES.exerciseLog && (<>
        <div onClick={() => setExercised(e => !e)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: exercised ? "#E8F5F2" : "#F5F5F5", borderRadius: 12, padding: "12px 14px", marginBottom: exercised ? 8 : 12, cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🏃</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Exercise today?</div>
              <div style={{ fontSize: 12, color: C.mid, marginTop: 1 }}>{exercised ? "Logged" : "Tap to log"}</div>
            </div>
          </div>
          <div style={{ width: 26, height: 26, borderRadius: "50%", background: exercised ? C.teal : "#e0dbd4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "white" }}>{exercised ? "✓" : ""}</div>
        </div>
        {exercised && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, paddingLeft: 4 }}>
            <input
              type="number"
              value={exerciseMins}
              onChange={e => setExerciseMins(e.target.value)}
              placeholder="How many minutes?"
              style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: `.5px solid ${C.teal}`, background: "white", fontSize: 13, color: C.ink, outline: "none" }}
            />
            <div style={{ fontSize: 12, color: C.mid }}>mins</div>
          </div>
        )}
        </>)}

        <div className="slbl">How are you feeling?</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {allTags.map(t => (
            <button key={t.l} className={`tag${tags.includes(t.l) ? " " + t.s : ""}`} onClick={() => tog(t.l)}>
              <span style={{ fontSize: 14 }}>{t.e}</span>{t.l}
            </button>
          ))}
        </div>
        <button className="pbtn" onClick={save} disabled={saving || !text.trim()}>
          {saving ? <><Spinner />&nbsp;Saving & getting reflection...</> : "Save entry"}
        </button>
      </div>
      <Nav active="write" go={go} />
    </div>
  );
}

function ReflectScreen({ go, user, rk }) {
  const [entries, setEntries] = useState([]);
  const [sel, setSel] = useState(0);
  const [sr, setSr] = useState("");
  const [ctab, setCtab] = useState("self");
  const [afu, setAfu] = useState("");
  const [loadAfu, setLoadAfu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentAiReply, setCurrentAiReply] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [pick, setPick] = useState(null);
  useEffect(() => { getTonightPick(user.id).then(setPick); }, [user.id, rk]);
  const [view, setView] = useState("timeline");
  const [moodFilter, setMoodFilter] = useState(null);
  const [medLog, setMedLog] = useState(null);
  const [exerciseLog, setExerciseLog] = useState(null);

  useEffect(() => {
    if (FEATURES.exerciseLog && entries[sel]?.entry_date) {
      supabase.from("exercise_logs").select("*").eq("user_id", user.id).eq("log_date", entries[sel].entry_date).single()
        .then(({ data }) => setExerciseLog(data || null));
    }
  }, [sel, entries, user.id]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("entries").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(20);
    const sorted = (data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setEntries(sorted);
    setSel(0);
    if (sorted[0]) setCurrentAiReply(sorted[0].ai_reply || "");
    setLoading(false);
  }, [user.id, rk]);

  useEffect(() => { setSel(0); }, [rk]);
  useEffect(() => { loadEntries(); }, [user.id, rk]);

  useEffect(() => {
    if (entries[sel]) {
      setCurrentAiReply(entries[sel].ai_reply || "");
      const d = entries[sel].entry_date;
      if (FEATURES.medication) supabase.from("medication_logs").select("taken").eq("user_id", user.id).eq("log_date", d).single().then(({ data }) => setMedLog(data));
      if (FEATURES.exerciseLog) supabase.from("exercise_logs").select("exercised, minutes").eq("user_id", user.id).eq("log_date", d).single().then(({ data }) => setExerciseLog(data));
    }
  }, [sel, entries]);

  const fmt = d => new Date(d + "T00:00:00").toLocaleDateString("en-AU", { month: "short", day: "numeric" });
  const e = entries[sel];

  const saveSr = async () => {
    await supabase.from("entries").update({ self_reply: sr }).eq("id", e.id);
    setEntries(prev => prev.map((x, i) => i === sel ? { ...x, self_reply: sr } : x));
    setSr("");
  };

  const sendAfu = async () => {
    if (!afu.trim()) return;
    setLoadAfu(true);
    const question = afu;
    const ok = await ensureAiConsent({ reask: true });
    if (!ok) { setLoadAfu(false); return; }
    setAfu("");
    const r = await getAI(`Original journal entry: "${e.text}". The user has a follow-up question: "${question}"`);
    await supabase.from("entries").update({ ai_reply: r }).eq("id", e.id);
    setCurrentAiReply(r);
    setEntries(prev => prev.map((x, i) => i === sel ? { ...x, ai_reply: r } : x));
    setLoadAfu(false);
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}><Spinner color={C.teal} /></div>;

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.tealDk }}>
        <div className="back" onClick={() => go("home")}>← home</div>
        <div className="htitle">Journal</div>
        <div className="hsub">Your entries and Harlo's replies</div>
      </div>
      <div className="body">
        {entries.length === 0 ? (
          <div className="card" style={{ textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✍️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 6 }}>No entries yet</div>
            <div style={{ fontSize: 12, color: C.mid, marginBottom: 16 }}>Write your first entry to get a reflection.</div>
            <button className="pbtn" onClick={() => go("entry")}>Write first entry</button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 0, background: "#eee9e2", borderRadius: 10, padding: 3, marginBottom: 14 }}>
              {[["timeline","Timeline"],["moods","By mood"]].map(([v,l]) => (
                <button key={v} onClick={() => { setView(v); setMoodFilter(null); }} style={{ flex: 1, padding: "7px 0", fontSize: 11, fontWeight: 600, borderRadius: 8, background: view===v?"white":"transparent", color: view===v?C.tealDk:C.mid, border: "none" }}>{l}</button>
              ))}
            </div>

            {view === "moods" && (
              <>
                {!moodFilter ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                    {[
                      { tag: "grateful", emoji: "🙂", color: C.roseLt, txt: C.roseDk, border: C.rose },
                      { tag: "anxious",  emoji: "😰", color: C.amberLt, txt: C.amberDk, border: C.amber },
                      { tag: "calm",     emoji: "😌", color: C.tealLt, txt: C.tealDk, border: C.teal },
                      { tag: "sad",      emoji: "😔", color: C.blueLt, txt: C.blueDk, border: C.blue },
                      { tag: "hopeful",  emoji: "🌱", color: C.tealLt, txt: C.tealDk, border: C.teal },
                      { tag: "proud",    emoji: "✨", color: C.purpleLt, txt: C.purpleDk, border: C.purple },
                    ].map(m => {
                      const count = entries.filter(e => e.tags?.includes(m.tag)).length;
                      return (
                        <div key={m.tag} onClick={() => { if(count > 0) setMoodFilter(m.tag); }} style={{ display: "flex", alignItems: "center", gap: 12, background: m.color, borderRadius: 12, padding: "12px 14px", border: `.5px solid ${m.border}`, cursor: count > 0 ? "pointer" : "default", opacity: count > 0 ? 1 : 0.4 }}>
                          <span style={{ fontSize: 22 }}>{m.emoji}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: m.txt, textTransform: "capitalize" }}>{m.tag}</div>
                            <div style={{ fontSize: 10, color: m.txt, opacity: 0.7, marginTop: 2 }}>{count} {count === 1 ? "entry" : "entries"}</div>
                          </div>
                          <div style={{ fontSize: 16, color: m.txt, opacity: 0.5 }}>›</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <button onClick={() => setMoodFilter(null)} style={{ fontSize: 11, color: C.teal, background: "none", border: "none", padding: 0, cursor: "pointer" }}>← all moods</button>
                      <span style={{ fontSize: 11, color: C.mid }}>· {moodFilter} entries</span>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 14, background: "#eee9e2", borderRadius: 10, padding: 3, overflowX: "auto" }}>
                      {entries.filter(e => e.tags?.includes(moodFilter)).map((en, i) => (
                        <button key={en.id} onClick={() => { setSel(entries.indexOf(en)); }} style={{ flex: "0 0 auto", padding: "7px 12px", fontSize: 11, fontWeight: 600, borderRadius: 8, background: entries[sel]?.id===en.id?"white":"transparent", color: entries[sel]?.id===en.id?C.tealDk:C.mid, border: "none", whiteSpace: "nowrap" }}>{fmt(en.entry_date)}</button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {(view === "timeline" || moodFilter) && (
            <div style={{ display: "flex", gap: 6, marginBottom: 14, background: "#eee9e2", borderRadius: 10, padding: 3, overflowX: "auto" }}>
              {(view === "timeline" ? entries : entries.filter(e => e.tags?.includes(moodFilter))).slice(0, 7).map((en, i) => {
                const actualIdx = entries.indexOf(en);
                const label = fmt(en.entry_date) + (entries.filter(x => x.entry_date === en.entry_date).length > 1 ? ` #${entries.filter(x => x.entry_date === en.entry_date).indexOf(en) + 1}` : "");
                return <button key={en.id} onClick={() => setSel(actualIdx)} style={{ flex: "0 0 auto", padding: "7px 12px", fontSize: 11, fontWeight: 600, borderRadius: 8, background: sel===actualIdx?"white":"transparent", color: sel===actualIdx?C.tealDk:C.mid, border: "none", whiteSpace: "nowrap" }}>{label}</button>;
              })}
            </div>)}
            <div className="card" style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", color: C.mid, marginBottom: 6 }}>{fmt(e.entry_date)} · your entry</div>
              <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.7 }}>"{e.text}"</div>
              {e.gratitude?.length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: ".5px solid #f0ebe4" }}>
                  <div style={{ fontSize: 9, textTransform: "uppercase", color: C.amberDk, fontWeight: 600, marginBottom: 6 }}>🌿 Grateful for</div>
                  {e.gratitude.map((g, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.teal, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "white", flexShrink: 0 }}>{i+1}</div>
                      <div style={{ fontSize: 12, color: C.ink }}>{g}</div>
                    </div>
                  ))}
                </div>
              )}
              {e.tags?.length > 0 && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
                  {e.tags.map(t => <span key={t} className={`tag ${t==="sad"?"bz":"rz"}`} style={{ fontSize: 10, padding: "3px 9px" }}>{t}</span>)}
                </div>
              )}
              {((FEATURES.medication && medLog?.taken) || (FEATURES.exerciseLog && exerciseLog?.exercised)) && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: ".5px solid #f0ebe4", display: "flex", gap: 12 }}>
                  {FEATURES.medication && medLog?.taken && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 14 }}>💊</span>
                      <div style={{ fontSize: 12, color: C.tealDk, fontWeight: 600 }}>Medication taken</div>
                    </div>
                  )}
                  {FEATURES.exerciseLog && exerciseLog?.exercised && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 14 }}>🏃</span>
                      <div style={{ fontSize: 12, color: C.tealDk, fontWeight: 600 }}>Exercised{exerciseLog.minutes ? ` · ${exerciseLog.minutes} mins` : ""}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ background: C.tealLt, borderRadius: 12, padding: "12px 14px", borderLeft: `3px solid ${C.teal}`, borderTopLeftRadius: 0, marginBottom: 8 }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", color: C.tealDk, fontWeight: 600, marginBottom: 5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.teal }}></div>Harlo replied
                </div>
                <button onClick={async () => {
                  const ok = await ensureAiConsent({ reask: true });
                  if (!ok) return;
                  setAiBusy(true);
                  setCurrentAiReply("");
                  const r = await getAI(e.text);
                  setAiBusy(false);
                  if (r == null) return;
                  await supabase.from("entries").update({ ai_reply: r }).eq("id", e.id);
                  setCurrentAiReply(r);
                  setEntries(prev => prev.map((x, i) => i === sel ? { ...x, ai_reply: r } : x));
                }} style={{ fontSize: 10, color: C.teal, background: "none", border: "none", cursor: "pointer", padding: 0 }}>↻ refresh</button>
              </div>
              {currentAiReply
                ? <div style={{ fontSize: 13, color: C.tealDk, lineHeight: 1.7, fontStyle: "italic" }}>"{currentAiReply}"</div>
                : aiBusy
                  ? <div><span className="td" /><span className="td" /><span className="td" /></div>
                  : <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.6 }}>No reply for this entry. Tap "refresh" if you would like Harlo to write one. This sends the entry to an AI service.</div>}
            </div>
            <div onClick={() => go("sleep")} style={{ background: "linear-gradient(135deg,#1a2a3a 0%,#2a1a3a 100%)", borderRadius: 14, padding: "14px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
              <span style={{ fontSize: 26 }}>🌙</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "white" }}>{pick ? "Your story for tonight" : "Ready to wind down?"}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)", marginTop: 2 }}>{pick ? `${pick.title}. ${pick.why}` : "Play a sleep story"}</div>
              </div>
              <div style={{ color: "rgba(255,255,255,.5)", fontSize: 18 }}>›</div>
            </div>
            {e.self_reply && (
              <div style={{ background: C.roseLt, borderRadius: 12, padding: "12px 14px", borderLeft: `3px solid ${C.rose}`, borderTopLeftRadius: 0, marginBottom: 8 }}>
                <div style={{ fontSize: 9, textTransform: "uppercase", color: C.roseDk, fontWeight: 600, marginBottom: 5, display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.rose }}></div>You replied
                </div>
                <div style={{ fontSize: 13, color: C.roseDk, lineHeight: 1.7 }}>{e.self_reply}</div>
              </div>
            )}
            <div className="card">
              <div style={{ display: "flex", marginBottom: 10, background: "#eee9e2", borderRadius: 8, padding: 2 }}>
                {[["self","Write self-response"],["ai","Ask AI follow-up"]].map(([t, l]) => (
                  <button key={t} onClick={() => setCtab(t)} style={{ flex: 1, padding: "6px 0", fontSize: 10, fontWeight: 600, borderRadius: 6, background: ctab===t?"white":"transparent", color: ctab===t?C.roseDk:C.mid, border: "none" }}>{l}</button>
                ))}
              </div>
              {ctab === "self" ? (
                <>
                  <div style={{ fontSize: 10, color: C.mid, fontStyle: "italic", marginBottom: 8 }}>Write back to yourself — from where you are now.</div>
                  <textarea value={sr} onChange={ev => setSr(ev.target.value)} placeholder="Looking back, I think..." className="ta" style={{ minHeight: 70, marginBottom: 8 }} />
                  <button className="pbtn" onClick={saveSr} disabled={!sr.trim()}>Save my reply</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 10, color: C.mid, fontStyle: "italic", marginBottom: 8 }}>Ask Harlo a follow-up question.</div>
                  <textarea value={afu} onChange={ev => setAfu(ev.target.value)} placeholder="I am curious about..." className="ta" style={{ minHeight: 70, marginBottom: 8 }} />
                  <button className="pbtn" onClick={sendAfu} style={{ background: C.tealDk }} disabled={loadAfu || !afu.trim()}>
                    {loadAfu ? <><Spinner />&nbsp;Thinking...</> : "Send to AI"}
                  </button>
                </>
              )}
            </div>
            {FEATURES.community && <ShareToComm entry={e} currentAiReply={currentAiReply} user={user} go={go} />}
          </>
        )}
      </div>
      <Nav active="reflect" go={go} />
    </div>
  );
}

function ShareScreen({ go, user }) {
  const [entries, setEntries] = useState([]);
  const [selId, setSelId] = useState("");
  const [inc, setInc] = useState({ entry: true, ai: true, self: true });
  const [anon, setAnon] = useState(false);
  const [dests, setDests] = useState({ ig: true, tw: false, com: true, lnk: false });
  const [done, setDone] = useState(false);

  useEffect(() => {
    supabase.from("entries").select("*").eq("user_id", user.id).order("entry_date", { ascending: false }).limit(5)
      .then(({ data }) => { setEntries(data || []); if (data?.length) setSelId(data[0].id); });
  }, [user.id]);

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.amberDk }}>
        <div className="back" onClick={() => go("home")}>← home</div>
        <div className="htitle">Share your story</div>
        <div className="hsub">Choose exactly what the world sees</div>
      </div>
      <div className="body">
        {entries.length > 0 && (
          <><div className="slbl">Which entry</div>
          <select value={selId} onChange={e => setSelId(e.target.value)} style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: ".5px solid #e0dbd4", background: "white", fontSize: 13, color: C.ink, marginBottom: 14, fontFamily: "Nunito,sans-serif" }}>
            {entries.map(e => <option key={e.id} value={e.id}>{new Date(e.entry_date + "T00:00:00").toLocaleDateString("en-AU", { month: "short", day: "numeric" })} — {e.text.slice(0, 40)}...</option>)}
          </select></>
        )}
        <div className="slbl">Select what to include</div>
        <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 12 }}>
          {[["entry","Your original entry",C.paper],["ai","AI reflection",C.tealLt],["self","Your self-response",C.roseLt]].map(([k,l,f]) => (
            <div key={k} onClick={() => setInc(i => ({ ...i, [k]: !i[k] }))} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: ".5px solid #f0ebe4", background: inc[k]?f:"white", cursor: "pointer" }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${inc[k]?C.amber:"#d0cac2"}`, background: inc[k]?C.amber:"white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white", fontWeight: 700, flexShrink: 0 }}>{inc[k]?"✓":""}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.amberLt, borderRadius: 10, padding: "11px 13px", marginBottom: 12 }}>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: C.amberDk }}>Post anonymously</div><div style={{ fontSize: 10, color: C.amber, marginTop: 2 }}>Your name won't appear</div></div>
          <div onClick={() => setAnon(a => !a)} style={{ width: 38, height: 22, borderRadius: 11, background: anon?C.amber:"#d0cac2", position: "relative", cursor: "pointer", transition: "background .2s" }}>
            <div style={{ width: 18, height: 18, borderRadius: "50%", background: "white", position: "absolute", top: 2, left: anon?18:2, transition: "left .2s" }}></div>
          </div>
        </div>
        <div className="slbl">Share to</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          {[["ig","📸 Instagram"],["tw","𝕏 Twitter"],["com","👥 Community"],["lnk","🔗 Copy link"]].map(([k,l]) => (
            <div key={k} onClick={() => setDests(d => ({ ...d, [k]: !d[k] }))} style={{ border: `1px solid ${dests[k]?C.amber:"#e0dbd4"}`, borderRadius: 10, padding: "11px 8px", textAlign: "center", cursor: "pointer", background: dests[k]?C.amberLt:"white", fontSize: 12, fontWeight: dests[k]?600:400, color: dests[k]?C.amberDk:C.mid }}>{l}</div>
          ))}
        </div>
        <button className="pbtn" style={{ background: C.amberDk }} onClick={() => { setDone(true); setTimeout(() => setDone(false), 2500); }}>
          {done ? "✓ Published!" : "Publish now"}
        </button>
      </div>
      <Nav active="home" go={go} />
    </div>
  );
}

function CheersScreen({ go, user, cheers, setCheers }) {
  const [tab, setTab] = useState("received");
  const [sent, setSent] = useState([]);
  const [loadingSent, setLoadingSent] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [newType, setNewType] = useState("prompt");
  const [newMsg, setNewMsg] = useState("");
  const [newTo, setNewTo] = useState("");
  const [sendingNew, setSendingNew] = useState(false);
  const [sendErr, setSendErr] = useState("");
  const [sendDone, setSendDone] = useState(false);
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newPhotoUrl, setNewPhotoUrl] = useState("");
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useState(null);
  const chunksRef = useState([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef[1](mr);
      chunksRef[1]([]);
      mr.ondataavailable = e => chunksRef[1](prev => [...prev, e.data]);
      mr.onstop = () => {
        const blob = new Blob(chunksRef[0], { type: "audio/webm" });
        setVoiceBlob(blob);
        setVoiceUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setIsRecording(true);
    } catch (e) { alert("Microphone access needed to record voice notes."); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef[0] && isRecording) {
      mediaRecorderRef[0].stop();
      setIsRecording(false);
    }
  };

  const markRead = async id => {
    await supabase.from("cheers").update({ is_read: true }).eq("id", id);
    setCheers(c => c.map(x => x.id === id ? { ...x, is_read: true } : x));
  };

  useEffect(() => {
    if (tab === "sent") {
      setLoadingSent(true);
      supabase.from("cheers").select("*, profiles!cheers_to_user_id_fkey(username, display_name)").eq("from_user_id", user.id).order("created_at", { ascending: false })
        .then(({ data }) => { setSent(data || []); setLoadingSent(false); });
    }
  }, [tab, user.id]);

  const toggleLike = async (c) => {
    const newVal = !c.is_liked;
    await supabase.from("cheers").update({ is_liked: newVal }).eq("id", c.id);
    setCheers(prev => prev.map(x => x.id === c.id ? { ...x, is_liked: newVal } : x));
  };

  const sendReply = async (c) => {
    if (!replyText.trim()) return;
    setSending(true);
    await supabase.from("cheers").insert({ from_user_id: user.id, to_user_id: c.from_user_id, type: "prompt", content: replyText.trim(), reply_to: c.id, is_read: false });
    setReplyText(""); setReplyingTo(null); setSending(false);
  };

  const sendNew = async () => {
    if (!newTo.trim()) { setSendErr("Please enter a username"); return; }
    if (newType === "prompt" && !newMsg.trim()) { setSendErr("Please write a message"); return; }
    if (newType === "photo" && !newPhotoFile) { setSendErr("Please choose a photo"); return; }
    if (newType === "voice" && !voiceBlob) { setSendErr("Please record a voice note"); return; }
    setSendingNew(true); setSendErr("");
    const { data: profile } = await supabase.from("profiles").select("id").eq("username", newTo.trim()).single();
    if (!profile) { setSendErr("Username not found."); setSendingNew(false); return; }
    let mediaUrl = null;
    if (newType === "photo" && newPhotoFile) {
      const ext = newPhotoFile.name.split(".").pop();
      const path = `photos/${user.id}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("cheers-media").upload(path, newPhotoFile);
      if (upErr) { setSendErr("Could not upload photo."); setSendingNew(false); return; }
      const { data: urlData } = supabase.storage.from("cheers-media").getPublicUrl(path);
      mediaUrl = urlData.publicUrl;
    }
    if (newType === "voice" && voiceBlob) {
      const path = `voice/${user.id}-${Date.now()}.webm`;
      const { error: upErr } = await supabase.storage.from("cheers-media").upload(path, voiceBlob);
      if (upErr) { setSendErr("Could not upload voice note."); setSendingNew(false); return; }
      const { data: urlData } = supabase.storage.from("cheers-media").getPublicUrl(path);
      mediaUrl = urlData.publicUrl;
    }
    const { error } = await supabase.from("cheers").insert({ from_user_id: user.id, to_user_id: profile.id, type: newType, content: newType === "prompt" ? newMsg : (newMsg || ""), caption: mediaUrl });
    if (error) { setSendErr("Could not send. Try again."); setSendingNew(false); return; }
    setSendDone(true); setNewMsg(""); setNewTo(""); setNewPhotoFile(null); setNewPhotoUrl(""); setVoiceBlob(null); setVoiceUrl("");
    setTimeout(() => { setSendDone(false); setTab("sent"); }, 1500);
    setSendingNew(false);
  };

  const cheerCard = (c, isSent) => (
    <div key={c.id} className="card" style={{ borderColor: (!isSent && !c.is_read) ? "#F4C0D1" : "#e8e2da" }} onClick={() => !isSent && markRead(c.id)}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.roseLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.roseDk, flexShrink: 0 }}>
          {isSent ? "→" : "✦"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.ink }}>
            {isSent ? `To: ${c.profiles?.username || c.profiles?.display_name || "Someone"}` : (c.reply_to ? "↩ Reply to your cheer" : `From: ${c.profiles?.username || c.profiles?.display_name || "A friend"}`)}
          </div>
          <div style={{ fontSize: 10, color: C.mid, marginTop: 1 }}>{c.type} · {new Date(c.created_at).toLocaleDateString("en-AU", { month: "short", day: "numeric" })}</div>
        </div>
        {!isSent && !c.is_read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.rose }}></div>}
        {isSent && c.is_liked && <div style={{ fontSize: 14 }}>♥</div>}
      </div>
      {c.type === "prompt" && <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.6, fontStyle: "italic" }}>"{c.content}"</div>}
      {c.type === "photo" && (c.caption ? <img src={c.caption} alt="cheer" style={{ width: "100%", borderRadius: 8, maxHeight: 180, objectFit: "cover", marginBottom: 6 }} /> : <div style={{ width: "100%", height: 80, background: C.tealLt, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🖼</div>)}
      {c.type === "voice" && <div style={{ background: C.purpleLt, borderRadius: 9, padding: "9px 12px" }}>{c.caption ? <audio controls src={c.caption} style={{ width: "100%" }} /> : <div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ width: 28, height: 28, borderRadius: "50%", background: C.roseDk, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white" }}>▶</div><div style={{ fontSize: 11, color: C.purpleDk, fontWeight: 600 }}>Voice note</div></div>}</div>}
      {!isSent && (
        <div style={{ display: "flex", gap: 6, marginTop: 10 }} onClick={ev => ev.stopPropagation()}>
          <button className={`tag${c.is_liked?" rz":""}`} style={{ fontSize: 11 }} onClick={() => toggleLike(c)}>{c.is_liked ? "♥ Loved" : "♡ Love this"}</button>
          <button className={`tag${replyingTo===c.id?" rz":""}`} style={{ fontSize: 11 }} onClick={() => { setReplyingTo(replyingTo===c.id?null:c.id); setReplyText(""); }}>↩ Reply</button>
        </div>
      )}
      {replyingTo === c.id && (
        <div style={{ marginTop: 10 }} onClick={ev => ev.stopPropagation()}>
          <textarea value={replyText} onChange={ev => setReplyText(ev.target.value)} placeholder="Write a reply..." className="ta" style={{ minHeight: 60, marginBottom: 8, fontSize: 12 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button className="pbtn" onClick={() => sendReply(c)} disabled={sending || !replyText.trim()} style={{ fontSize: 12, padding: "9px" }}>{sending ? "Sending..." : "Send reply"}</button>
            <button onClick={() => setReplyingTo(null)} style={{ flex: 1, padding: "9px", borderRadius: 10, border: ".5px solid #e0dbd4", background: "white", fontSize: 12, color: C.mid }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk }}>
        <div className="back" onClick={() => go("home")}>← home</div>
        <div className="htitle">Cheers</div>
        <div className="hsub">Send & receive encouragement</div>
      </div>
      <div className="body">
        <div style={{ display: "flex", gap: 0, background: "#eee9e2", borderRadius: 10, padding: 3, marginBottom: 14 }}>
          {[["received","Received"],["sent","Sent"],["send","Send new"]].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "7px 0", fontSize: 11, fontWeight: 600, borderRadius: 8, background: tab===t?"white":"transparent", color: tab===t?C.roseDk:C.mid, border: "none" }}>{l}</button>
          ))}
        </div>

        {tab === "received" && (
          <>
            {cheers.length === 0 && <div className="card" style={{ textAlign: "center", padding: "32px 20px" }}><div style={{ fontSize: 32, marginBottom: 12 }}>✦</div><div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 6 }}>No cheers yet</div><div style={{ fontSize: 12, color: C.mid }}>When friends send you encouragement it'll appear here.</div></div>}
            {cheers.map(c => cheerCard(c, false))}
          </>
        )}

        {tab === "sent" && (
          <>
            {loadingSent && <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.rose} /></div>}
            {!loadingSent && sent.length === 0 && <div className="card" style={{ textAlign: "center", padding: "32px 20px" }}><div style={{ fontSize: 32, marginBottom: 12 }}>✦</div><div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 6 }}>No cheers sent yet</div><div style={{ fontSize: 12, color: C.mid }}>Tap "Send new" to encourage a friend!</div></div>}
            {sent.map(c => cheerCard(c, true))}
          </>
        )}

        {tab === "send" && (
          <div className="card">
            {sendErr && <div className="err">{sendErr}</div>}
            {sendDone && <div className="ok">✦ Cheer sent!</div>}
            <div className="slbl">Their username</div>
            <input className="inp" value={newTo} onChange={e => setNewTo(e.target.value)} placeholder="their_username" />
            <div style={{ display: "flex", gap: 0, background: "#eee9e2", borderRadius: 10, padding: 3, marginBottom: 12 }}>
              {[["prompt","✍ Message"],["photo","🖼 Photo"],["voice","🎙 Voice"]].map(([t,l]) => (
                <button key={t} onClick={() => setNewType(t)} style={{ flex: 1, padding: "7px 0", fontSize: 10, fontWeight: 600, borderRadius: 8, background: newType===t?"white":"transparent", color: newType===t?C.roseDk:C.mid, border: "none" }}>{l}</button>
              ))}
            </div>
            {newType === "prompt" && <textarea value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Write an encouraging message..." className="ta" style={{ minHeight: 90, marginBottom: 12 }} />}
            {newType === "photo" && (
              <div style={{ marginBottom: 12 }}>
                {newPhotoUrl ? (
                  <div style={{ position: "relative", marginBottom: 8 }}>
                    <img src={newPhotoUrl} alt="preview" style={{ width: "100%", borderRadius: 10, maxHeight: 200, objectFit: "cover" }} />
                    <button onClick={() => { setNewPhotoUrl(""); setNewPhotoFile(null); }} style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.5)", color: "white", border: "none", borderRadius: "50%", width: 24, height: 24, fontSize: 12, cursor: "pointer" }}>✕</button>
                  </div>
                ) : (
                  <label style={{ display: "block", background: C.paper, border: "1.5px dashed #d0cac2", borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer", marginBottom: 8 }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>🖼</div>
                    <div style={{ fontSize: 13, color: C.mid }}>Tap to choose a photo</div>
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                      const file = e.target.files[0];
                      if (file) { setNewPhotoFile(file); setNewPhotoUrl(URL.createObjectURL(file)); }
                    }} />
                  </label>
                )}
                <textarea value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Add a caption..." className="ta" style={{ minHeight: 50 }} />
              </div>
            )}
            {newType === "voice" && (
              <div style={{ background: C.purpleLt, borderRadius: 12, padding: 16, textAlign: "center", marginBottom: 12 }}>
                {voiceBlob ? (
                  <div>
                    <audio controls src={voiceUrl} style={{ width: "100%", marginBottom: 10 }} />
                    <button onClick={() => { setVoiceBlob(null); setVoiceUrl(""); }} style={{ fontSize: 11, color: C.purpleDk, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Record again</button>
                  </div>
                ) : (
                  <div>
                    <div
                      onMouseDown={startRecording} onMouseUp={stopRecording}
                      onTouchStart={startRecording} onTouchEnd={stopRecording}
                      style={{ width: 60, height: 60, borderRadius: "50%", background: isRecording ? C.rose : C.purpleDk, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: 24, color: "white", cursor: "pointer", transition: "background .2s" }}>
                      🎙
                    </div>
                    <div style={{ fontSize: 13, color: C.purpleDk, fontWeight: 600 }}>{isRecording ? "Recording... release to stop" : "Hold to record"}</div>
                    <div style={{ fontSize: 11, color: C.purple, marginTop: 4 }}>Up to 60 seconds</div>
                  </div>
                )}
              </div>
            )}
            <button className="pbtn" onClick={sendNew} disabled={sendingNew || sendDone}>{sendingNew ? <><Spinner />&nbsp;Sending...</> : "Send cheer"}</button>
          </div>
        )}
      </div>
      <Nav active="home" go={go} />
    </div>
  );
}


function SendCheerScreen({ go, user }) {
  const [type, setType] = useState("prompt");
  const [msg, setMsg] = useState("");
  const [toUser, setToUser] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!toUser.trim() || !msg.trim()) { setErr("Please fill in all fields"); return; }
    setSending(true); setErr("");
    const { data: profile } = await supabase.from("profiles").select("id").eq("username", toUser.trim()).single();
    if (!profile) { setErr("Username not found."); setSending(false); return; }
    const { error } = await supabase.from("cheers").insert({ from_user_id: user.id, to_user_id: profile.id, type, content: msg });
    if (error) { setErr("Could not send. Try again."); setSending(false); return; }
    setSent(true); setTimeout(() => go("home"), 2000);
  };

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.amberLt, paddingTop: 52 }}>
        <div className="back" style={{ color: C.mid }} onClick={() => go("home")}>← home</div>
        <div className="htitle" style={{ color: C.roseDk }}>Send a cheer</div>
        <div className="hsub" style={{ color: C.mid }}>Lift someone up today</div>
      </div>
      <div className="body">
        {err && <div className="err">{err}</div>}
        {sent && <div className="ok">✦ Cheer sent!</div>}
        <div className="card">
          <div className="slbl">Their username</div>
          <input className="inp" value={toUser} onChange={e => setToUser(e.target.value)} placeholder="their_username" style={{ marginBottom: 0 }} />
        </div>
        <div style={{ display: "flex", gap: 0, background: "#eee9e2", borderRadius: 10, padding: 3, marginBottom: 12 }}>
          {[["prompt","✍ Prompt"],["photo","🖼 Photo"],["voice","🎙 Voice"]].map(([t, l]) => (
            <button key={t} onClick={() => setType(t)} style={{ flex: 1, padding: "8px 0", fontSize: 11, fontWeight: 600, borderRadius: 8, background: type===t?"white":"transparent", color: type===t?C.roseDk:C.mid, border: "none" }}>{l}</button>
          ))}
        </div>
        {type === "prompt" && (
          <div className="card">
            <textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="You've been showing up every day — that takes real courage..." className="ta" style={{ minHeight: 90 }} />
          </div>
        )}
        {type !== "prompt" && <div style={{ background: C.purpleLt, borderRadius: 12, padding: 24, textAlign: "center" }}><div style={{ fontSize: 13, color: C.purpleDk, fontWeight: 600 }}>Coming soon</div></div>}
        <button className="pbtn" style={{ marginTop: 14 }} onClick={send} disabled={sending || sent}>
          {sending ? <><Spinner />&nbsp;Sending...</> : sent ? "✦ Sent!" : "Send cheer"}
        </button>
      </div>
      <Nav active="home" go={go} />
    </div>
  );
}

function RemindersScreen({ go, user, scheduleDailyPrompt, scheduleMorningMotivation, scheduleMedicationReminder, scheduleWinBack }) {
  const [rem, setRem] = useState({
    journal_on: false, journal_time: "20:00",
    quote_on: false, quote_time: "07:30",
    medication_on: false, medication_time: "08:00",
    weekly_on: false
  });
  const [saved, setSaved] = useState(false);
  const [username, setUsername] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data: reminderData } = await supabase.from("reminders").select("*").eq("user_id", user.id).single();
        if (reminderData) setRem(r => ({ ...r, ...reminderData }));
        const { data: profileData } = await supabase.from("profiles").select("username").eq("id", user.id).single();
        if (profileData?.username) setUsername(profileData.username);
        setLoaded(true);
      } catch (error) {
        setLoaded(true);
      }
    };
    loadData();
  }, [user.id]);

  const save = async () => {
    if (!rem) return;
    try {
      if (username.trim()) await supabase.from("profiles").update({ username: username.trim() }).eq("id", user.id);
      const remData = {
        user_id: user.id,
        journal_on: rem.journal_on,
        journal_time: rem.journal_time,
        quote_on: rem.quote_on,
        quote_time: rem.quote_time,
        medication_on: rem.medication_on,
        medication_time: rem.medication_time,
        weekly_on: rem.weekly_on,
      };
      await supabase.from("reminders").upsert({ ...remData, updated_at: new Date().toISOString() }, { onConflict: "user_id" });
      
      const LN = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.LocalNotifications;
      
      if (rem.journal_on) {
        try { await scheduleDailyPrompt(rem.journal_time); } catch(e) { }
      } else if (LN) {
        try { await LN.cancel({ notifications: [{ id: 1001 }] }); } catch(e) {}
      }
      // Win-back nudges follow the evening reminder: off here means no win-back either
      try { await scheduleWinBack(!!rem.journal_on); } catch(e) { }
      if (!rem.journal_on && LN) {
        try { await LN.cancel({ notifications: [{ id: 1020 }] }); localStorage.removeItem(`harlo_letter_nudge_${user.id}`); } catch(e) { }
      }
      if (rem.quote_on && FEATURES.morningReminder) {
        try { await scheduleMorningMotivation(rem.quote_time); } catch(e) { }
      } else if (LN) {
        try { await LN.cancel({ notifications: [{ id: 1002 }] }); } catch(e) {}
      }
      if (rem.medication_on && FEATURES.medication) {
        try { await scheduleMedicationReminder(rem.medication_time); } catch(e) { }
      } else if (LN) {
        try { await LN.cancel({ notifications: [{ id: 1003 }] }); } catch(e) {}
      }
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch(e) { }
  };

  const items = [
    { k:"quote",      ic:"☀️", col:C.amberLt, txt:C.amberDk, ac:C.amber,  n:"Morning motivation", s:"A daily uplifting quote to start your day", tk:"quote_time", on: FEATURES.morningReminder },
    { k:"journal",    ic:"✍️", col:C.roseLt,  txt:C.roseDk,  ac:C.rose,   n:"Evening wind-down", s:"A gentle nudge to check in and drift off", tk:"journal_time" },
    { k:"medication", ic:"💊", col:C.tealLt,  txt:C.tealDk,  ac:C.teal,   n:"Medication reminder", s:"A daily nudge to take your medication", tk:"medication_time", on: FEATURES.medication },
    { k:"weekly",     ic:"📊", col:C.purpleLt, txt:C.purpleDk, ac:C.purple, n:"Weekly reflection", s:"Sunday mood & entry summary", tk:null, on: FEATURES.weeklyReminder },
  ].filter(it => it.on !== false);

  if (!loaded || !rem) {
    return (
      <div className="screen">
        <div className="hdr" style={{ background: C.roseDk }}>
          <div className="back" onClick={() => go("profile")}>← back</div>
          <div className="htitle">Reminders</div>
          <div className="hsub">Your evening wind-down</div>
        </div>
        <div className="body" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <Spinner />
        </div>
        <Nav active="settings" go={go} />
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk }}>
        <div className="back" onClick={() => go("profile")}>← back</div>
        <div className="htitle">Reminders</div>
        <div className="hsub">Your evening wind-down</div>
      </div>
      <div className="body">
        {saved && <div className="ok">✓ Saved!</div>}
        {SOCIAL_ON && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="slbl">Your username</div>
          <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>Friends use this to send you cheers.</div>
          <input className="inp" value={username} onChange={e => setUsername(e.target.value)} placeholder="choose_a_username" style={{ marginBottom: 0 }} />
        </div>
        )}
        <div className="slbl">Notifications</div>
        <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>Use the button below to save your changes.</div>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {items.map((item, i) => (
            <div key={item.k} style={{ padding: "13px 14px", borderBottom: i < items.length-1?".5px solid #f0ebe4":"none" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: item.col, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{item.ic}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{item.n}</div>
                </div>
                <div onClick={() => setRem(r => ({ ...r, [`${item.k}_on`]: !r[`${item.k}_on`] }))} style={{ width: 38, height: 22, borderRadius: 11, background: rem[`${item.k}_on`]?item.ac:"#d0cac2", position: "relative", cursor: "pointer", transition: "background .2s" }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "white", position: "absolute", top: 2, left: rem[`${item.k}_on`]?18:2, transition: "left .2s" }}></div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.mid, marginBottom: item.tk?6:0 }}>{item.s}</div>
              {item.tk && <input type="time" value={rem[item.tk]} onChange={e => setRem(r => ({ ...r, [item.tk]: e.target.value }))} style={{ fontSize: 11, fontWeight: 600, color: item.txt, background: item.col, border: "none", borderRadius: 7, padding: "4px 9px" }} />}
            </div>
          ))}
        </div>
        <button style={{ marginTop: 14, background: C.roseDk, color: "white", borderRadius: 12, padding: 13, width: "100%", fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer", WebkitAppearance: "none" }} onClick={() => save()}>Save & enable notifications</button>
        <div onClick={() => supabase.auth.signOut()} style={{ textAlign: "center", fontSize: 11, color: C.mid, marginTop: 16, cursor: "pointer", padding: "8px 0" }}>Sign out</div>
      </div>
      <Nav active="settings" go={go} />
    </div>
  );
}


function ShareToComm({ entry, currentAiReply, user, go }) {
  const [open, setOpen] = useState(false);
  const [inc, setInc] = useState({ entry: true, ai: true, self: false });
  const [anon, setAnon] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  const buildContent = () => {
    const parts = [];
    if (inc.entry && entry?.text) parts.push(`"${entry.text}"`);
    if (inc.ai && currentAiReply) parts.push(`Harlo reflected: "${currentAiReply}"`);
    if (inc.self && entry?.self_reply) parts.push(`My response: "${entry.self_reply}"`);
    return parts.join("\n\n");
  };

  const share = async () => {
    const content = buildContent();
    if (!content) return;
    setSharing(true);
    await supabase.from("posts").insert({ user_id: user.id, content, entry_id: entry.id, is_anonymous: anon });
    setSharing(false); setShared(true); setOpen(false);
    setTimeout(() => setShared(false), 3000);
  };

  return (
    <>
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <div onClick={() => setOpen(o => !o)} style={{ flex: 1, border: `.5px solid ${open?C.purple:"#e0dbd4"}`, borderRadius: 9, padding: "9px 8px", textAlign: "center", cursor: "pointer", background: open?C.purpleLt:"white", fontSize: 12, fontWeight: 600, color: open?C.purpleDk:C.mid }}>
          👥 Share to community
        </div>
        {shared && <div style={{ flex: 1, border: `.5px solid ${C.teal}`, borderRadius: 9, padding: "9px 8px", textAlign: "center", background: C.tealLt, fontSize: 12, fontWeight: 600, color: C.tealDk }}>✓ Shared!</div>}
      </div>
      {open && (
        <div style={{ background: C.purpleLt, borderRadius: 12, padding: "12px 14px", marginTop: 8, border: `.5px solid ${C.purple}` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.purpleDk, marginBottom: 10 }}>Choose what to share</div>
          {[
            ["entry", "Your original entry", entry?.text],
            ["ai", "AI reflection", currentAiReply],
            ["self", "Your self-response", entry?.self_reply],
          ].map(([k, l, val]) => val ? (
            <div key={k} onClick={() => setInc(i => ({ ...i, [k]: !i[k] }))} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8, cursor: "pointer" }}>
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${inc[k]?C.purple:"#d0cac2"}`, background: inc[k]?C.purple:"white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "white", flexShrink: 0, marginTop: 1 }}>{inc[k]?"✓":""}</div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.purpleDk }}>{l}</div>
                <div style={{ fontSize: 10, color: C.purpleDk, opacity: 0.7, lineHeight: 1.4 }}>{val.slice(0, 50)}{val.length > 50 ? "..." : ""}</div>
              </div>
            </div>
          ) : null)}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: `.5px solid ${C.purple}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} onClick={() => setAnon(a => !a)}>
              <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${anon?C.purple:"#d0cac2"}`, background: anon?C.purple:"white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "white" }}>{anon?"✓":""}</div>
              <span style={{ fontSize: 11, color: C.purpleDk }}>Post anonymously</span>
            </div>
            <button onClick={share} disabled={sharing || !buildContent()} className="pbtn" style={{ width: "auto", padding: "8px 16px", background: C.roseDk, fontSize: 12 }}>
              {sharing ? <Spinner /> : "Share now"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}


function CommunityScreen({ go, user }) {
  const [tab, setTab] = useState("feed");
  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [anon, setAnon] = useState(false);
  const [posting, setPosting] = useState(false);
  const [commentingOn, setCommentingOn] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState({});
  const [newGroup, setNewGroup] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [myGroups, setMyGroups] = useState([]);
  const [topicPrompt, setTopicPrompt] = useState("");
  const [topicPosts, setTopicPosts] = useState([]);
  const [topicResponse, setTopicResponse] = useState("");
  const [postingTopic, setPostingTopic] = useState(false);
  const [topicAnon, setTopicAnon] = useState(false);
  const [loadingTopic, setLoadingTopic] = useState(false);

  const loadFeed = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { data: blocked } = await supabase.from("blocked_users").select("blocked_id").eq("blocker_id", user.id);
    const blockedIds = (blocked || []).map(b => b.blocked_id);
    let query = supabase.from("posts").select("*, profiles(username, display_name), post_likes(user_id)").order("created_at", { ascending: false }).limit(30);
    if (blockedIds.length > 0) query = query.not("user_id", "in", `(${blockedIds.join(",")})`);
    const { data } = await query;
    if (data) data.forEach(p => { p.user_liked = (p.post_likes || []).some(l => l.user_id === user?.id); });
    setPosts(data || []);
    setLoading(false);
  };

  const loadGroups = async () => {
    const { data } = await supabase.from("groups").select("*, group_members(count)").order("created_at", { ascending: false });
    setGroups(data || []);
    const { data: mine } = await supabase.from("group_members").select("group_id").eq("user_id", user.id);
    setMyGroups((mine || []).map(m => m.group_id));
  };

  useEffect(() => { loadFeed(); loadGroups(); loadTopic(); }, []);

  const getWeekKey = () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    return localDate(start);
  };

  const loadTopic = async () => {
    setLoadingTopic(true);
    const TOPICS = [
      "What's something small that's been bringing you comfort lately?",
      "How do you know when you need to slow down?",
      "What does rest actually look like for you — not what it should look like?",
      "What's something you wish people understood about how you're feeling?",
      "What's one thing you've been gentle with yourself about recently?",
      "What helps you feel like yourself again after a hard day?",
      "What's something you've stopped apologising for?",
      "When did you last feel genuinely proud of yourself — even for something small?",
      "What does your anxiety feel like in your body?",
      "What's a boundary you've set that's made your life better?",
      "What's something you're slowly learning to let go of?",
      "What does a good morning look like for you right now?",
      "What's something that used to feel impossible that you can do now?",
      "Who or what has been your unexpected source of support lately?",
      "What's a thought pattern you've noticed yourself in recently?",
      "What does loneliness feel like for you — and what helps?",
      "What's one thing you do that's just for you, with no purpose other than enjoyment?",
      "What do you wish your past self knew about getting through hard times?",
      "What's something you've been carrying quietly that deserves to be said out loud?",
      "What does healing feel like for you on a good day?",
      "What's a small act of self-care that actually works for you — not the Instagram version?",
      "What's something you're still figuring out, and that's okay?",
      "How do you talk to yourself when things go wrong?",
      "What's something that used to drain you that no longer has that power?",
      "What does support look like to you when you're struggling?",
      "What's a feeling you find hard to name but experience often?",
      "What's something your body has been trying to tell you lately?",
      "What does joy feel like for you — when was the last time you felt it?",
      "What's something you've forgiven yourself for?",
      "What does connection mean to you right now?",
      "What's a habit or routine that genuinely anchors you?",
      "What's something you'd say to someone going through exactly what you're going through?",
      "What does safety feel like for you — where or with whom do you feel it?",
      "What's something you've been brave about recently, even if no one noticed?",
      "What's one thing this season of your life has taught you?",
      "What do you need more of right now — and what's making it hard to get?",
      "What's a feeling you've been avoiding — and what do you think it's trying to tell you?",
      "What's something about yourself that you're slowly learning to appreciate?",
      "What does a hard day look like for you, and how do you get through it?",
      "What's something you've started doing differently since you began paying attention to your mental health?",
      "What's a small moment from this week that deserves more credit than it got?",
      "What does overwhelm feel like for you — and what's one thing that helps?",
      "What's something you've been too hard on yourself about?",
      "What's a relationship in your life that's been good for your mental health?",
      "What's one thing you've let yourself need recently?",
      "What does grief look like for you — not just loss, but any kind of ending?",
      "What's something you're doing now that your past self would be proud of?",
      "What's a version of yourself you've had to leave behind — and how do you feel about that?",
      "What does courage look like in your everyday life?",
      "What's something that helps you feel grounded when everything feels like too much?",
      "What's something you're still becoming?",
      "What would you want the Harlo community to know about you?",
    ];
    const weekKey = getWeekKey();
    const weekNumber = Math.floor(new Date(weekKey).getTime() / (7 * 24 * 60 * 60 * 1000));
    const topic = TOPICS[weekNumber % TOPICS.length];
    setTopicPrompt(topic);
    const { data } = await supabase.from("topic_posts").select("*, topic_post_likes(user_id)").eq("week_key", weekKey).order("created_at", { ascending: false }).limit(30);
    if (data && data.length > 0) {
      const userIds = [...new Set(data.map(p => p.user_id))];
      const { data: profiles } = await supabase.from("profiles").select("id, username, display_name").in("id", userIds);
      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.id] = p; });
      data.forEach(p => { 
        p.profiles = profileMap[p.user_id] || null;
        p.user_liked = (p.topic_post_likes || []).some(l => l.user_id === user.id);
      });
    }
    setTopicPosts(data || []);
    setLoadingTopic(false);
  };

  const submitTopicPost = async () => {
    if (!topicResponse.trim()) return;
    setPostingTopic(true);
    const weekKey = getWeekKey();
    await supabase.from("topic_posts").insert({ user_id: user.id, week_key: weekKey, content: topicResponse.trim(), is_anonymous: topicAnon, topic: topicPrompt });
    setTopicResponse("");
    await loadTopic();
    setPostingTopic(false);
  };

  const submitPost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    await supabase.from("posts").insert({ user_id: user.id, content: newPost.trim(), is_anonymous: anon });
    setNewPost(""); setAnon(false);
    await loadFeed();
    setPosting(false);
  };

  const toggleLike = async (post) => {
    const liked = post.user_liked;
    setPosts(prev => prev.map(p => p.id === post.id ? {
      ...p, user_liked: !liked,
      likes_count: liked ? Math.max(0, p.likes_count - 1) : p.likes_count + 1
    } : p));
    if (liked) {
      await supabase.from("post_likes").delete().eq("post_id", post.id).eq("user_id", user.id);
      await supabase.from("posts").update({ likes_count: Math.max(0, post.likes_count - 1) }).eq("id", post.id);
    } else {
      await supabase.from("post_likes").insert({ post_id: post.id, user_id: user.id });
      await supabase.from("posts").update({ likes_count: post.likes_count + 1 }).eq("id", post.id);
    }
    await loadFeed();
  };

  const loadComments = async (postId) => {
    if (commentingOn === postId) { setCommentingOn(null); return; }
    const { data } = await supabase.from("post_comments").select("*, profiles(username, display_name)").eq("post_id", postId).order("created_at", { ascending: true });
    setComments(c => ({ ...c, [postId]: data || [] }));
    setCommentingOn(postId);
  };

  const submitComment = async (postId) => {
    if (!commentText.trim()) return;
    await supabase.from("post_comments").insert({ post_id: postId, user_id: user.id, content: commentText.trim(), is_anonymous: anon });
    await supabase.from("posts").update({ comments_count: (posts.find(p => p.id === postId)?.comments_count || 0) + 1 }).eq("id", postId);
    setCommentText("");
    await loadComments(postId);
    await loadFeed();
  };

  const joinGroup = async (groupId) => {
    if (myGroups.includes(groupId)) {
      await supabase.from("group_members").delete().eq("group_id", groupId).eq("user_id", user.id);
      setMyGroups(g => g.filter(x => x !== groupId));
    } else {
      await supabase.from("group_members").insert({ group_id: groupId, user_id: user.id });
      setMyGroups(g => [...g, groupId]);
    }
  };

  const createGroup = async () => {
    if (!newGroup.trim()) return;
    setCreatingGroup(true);
    await supabase.from("groups").insert({ name: newGroup.trim(), created_by: user.id });
    setNewGroup("");
    await loadGroups();
    setCreatingGroup(false);
  };

  const displayName = (post) => post.is_anonymous ? "Anonymous" : (post.profiles?.username || post.profiles?.display_name || "Someone");

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk }}>
        <div className="back" onClick={() => go("home")}>← home</div>
        <div className="htitle">Community</div>
        <div className="hsub">Connect & support each other</div>
      </div>
      <div className="body">
        <div style={{ display: "flex", gap: 0, background: "#eee9e2", borderRadius: 10, padding: 3, marginBottom: 14 }}>
          {[["feed","Feed"],["topic","Topic"],["support","Support"]].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "8px 0", fontSize: 12, fontWeight: 600, borderRadius: 8, background: tab===t?"white":"transparent", color: tab===t?C.purpleDk:C.mid, border: "none" }}>{l}</button>
          ))}
        </div>

        {tab === "topic" && (
          <div>
            <div style={{ background: C.purpleLt, borderRadius: 14, padding: "16px", marginBottom: 14, border: `.5px solid ${C.purple}` }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".08em", color: C.purpleDk, fontWeight: 600, marginBottom: 8 }}>✦ This week's community topic</div>
              {loadingTopic ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="td" /><span className="td" /><span className="td" /></div>
              ) : (
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 17, color: C.purpleDk, lineHeight: 1.7 }}>"{topicPrompt}"</div>
              )}
            </div>

            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>Share your thoughts with the community</div>
              <textarea value={topicResponse} onChange={e => setTopicResponse(e.target.value)} placeholder="What comes up for you when you read this?" className="ta" style={{ minHeight: 90, marginBottom: 10 }} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => setTopicAnon(a => !a)}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${topicAnon?C.purple:"#d0cac2"}`, background: topicAnon?C.purple:"white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "white" }}>{topicAnon?"✓":""}</div>
                  <span style={{ fontSize: 11, color: C.mid }}>Post anonymously</span>
                </div>
                <button className="pbtn" onClick={submitTopicPost} disabled={postingTopic || !topicResponse.trim()} style={{ width: "auto", padding: "9px 18px", background: C.purpleDk, fontSize: 12 }}>
                  {postingTopic ? <Spinner /> : "Share"}
                </button>
              </div>
            </div>

            {loadingTopic && <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.purple} /></div>}
            {!loadingTopic && topicPosts.length === 0 && (
              <div className="card" style={{ textAlign: "center", padding: "28px 20px" }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>💬</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 6 }}>Be the first to respond</div>
                <div style={{ fontSize: 12, color: C.mid }}>Share your thoughts on this week's topic.</div>
              </div>
            )}
            {topicPosts.map(post => (
              <div key={post.id} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.purpleLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.purpleDk, flexShrink: 0 }}>
                    {post.is_anonymous ? "?" : (post.profiles?.username || post.profiles?.display_name || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.ink }}>{post.is_anonymous ? "Anonymous" : (post.profiles?.username || post.profiles?.display_name || "Someone")}</div>
                    <div style={{ fontSize: 10, color: C.mid }}>{new Date(post.created_at).toLocaleDateString("en-AU", { month: "short", day: "numeric" })}</div>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.7, marginBottom: 10 }}>{post.content}</div>
                <div style={{ borderTop: ".5px solid #f0ebe4", paddingTop: 8, display: "flex", gap: 8 }}>
                  <button onClick={async () => {
                    const liked = post.user_liked;
                    setTopicPosts(prev => prev.map(p => p.id === post.id ? { ...p, user_liked: !liked, likes_count: liked ? Math.max(0, (p.likes_count||0) - 1) : (p.likes_count||0) + 1 } : p));
                    if (liked) {
                      await supabase.from("topic_post_likes").delete().eq("post_id", post.id).eq("user_id", user.id);
                      await supabase.from("topic_posts").update({ likes_count: Math.max(0, (post.likes_count||0) - 1) }).eq("id", post.id);
                    } else {
                      await supabase.from("topic_post_likes").insert({ post_id: post.id, user_id: user.id });
                      await supabase.from("topic_posts").update({ likes_count: (post.likes_count||0) + 1 }).eq("id", post.id);
                    }
                  }} className="tag" style={{ fontSize: 11, color: post.user_liked?C.purple:C.mid, borderColor: post.user_liked?C.purple:"#e0dbd4", background: post.user_liked?C.purpleLt:"white" }}>
                    {post.user_liked ? "♥" : "♡"} {post.likes_count > 0 ? post.likes_count : ""} Like
                  </button>
                  {post.user_id !== user.id && (
                    <>
                      <button onClick={async () => {
                        if (window.confirm("Report this post?")) {
                          await supabase.from("post_reports").insert({ post_id: post.id, reporter_id: user.id, reason: "User reported" });
                          alert("Thank you — we will review this post within 24 hours.");
                        }
                      }} className="tag" style={{ fontSize: 11, color: "#999" }}>🚩 Report</button>
                      <button onClick={async () => {
                        if (window.confirm("Block this user?")) {
                          setTopicPosts(prev => prev.filter(p => p.user_id !== post.user_id));
                          await supabase.from("blocked_users").insert({ blocker_id: user.id, blocked_id: post.user_id });
                        }
                      }} className="tag" style={{ fontSize: 11, color: "#999", marginLeft: "auto" }}>🚫 Block</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "feed" && (
          <>
            <div className="card" style={{ marginBottom: 14 }}>
              <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder="Share something with the community..." className="ta" style={{ minHeight: 80, marginBottom: 10 }} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => setAnon(a => !a)}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${anon?C.purple:"#d0cac2"}`, background: anon?C.purple:"white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "white" }}>{anon?"✓":""}</div>
                  <span style={{ fontSize: 11, color: C.mid }}>Post anonymously</span>
                </div>
                <button className="pbtn" onClick={submitPost} disabled={posting || !newPost.trim()} style={{ width: "auto", padding: "9px 18px", background: C.roseDk, fontSize: 12 }}>
                  {posting ? <Spinner /> : "Post"}
                </button>
              </div>
            </div>

            {loading && <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.purple} /></div>}

            {posts.map(post => (
              <div key={post.id} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.purpleLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.purpleDk, flexShrink: 0 }}>
                    {post.is_anonymous ? "?" : displayName(post)[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.ink }}>{displayName(post)}</div>
                    <div style={{ fontSize: 10, color: C.mid }}>{new Date(post.created_at).toLocaleDateString("en-AU", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.7, marginBottom: 10 }}>
                  {post.content.length > 180 && !post.expanded
                    ? <>{post.content.slice(0, 180)}... <span onClick={() => setPosts(prev => prev.map(p => p.id === post.id ? {...p, expanded: true} : p))} style={{ color: C.purple, cursor: "pointer", fontWeight: 600 }}>read more</span></>
                    : <>{post.content}{post.content.length > 180 && <> <span onClick={() => setPosts(prev => prev.map(p => p.id === post.id ? {...p, expanded: false} : p))} style={{ color: C.purple, cursor: "pointer", fontWeight: 600 }}>show less</span></>}</>
                  }
                </div>
                <div style={{ display: "flex", gap: 8, borderTop: ".5px solid #f0ebe4", paddingTop: 8 }}>
                  <button onClick={() => toggleLike(post)} className="tag" style={{ fontSize: 11, color: post.user_liked?C.rose:C.mid, borderColor: post.user_liked?"#F4C0D1":"#e0dbd4", background: post.user_liked?C.roseLt:"white" }}>
                    {post.user_liked ? "♥" : "♡"} {post.likes_count > 0 ? post.likes_count : ""} Like
                  </button>
                  <button onClick={() => loadComments(post.id)} className="tag" style={{ fontSize: 11 }}>
                    💬 {post.comments_count > 0 ? post.comments_count : ""} Comment
                  </button>
                  {post.user_id !== user.id && (
                    <>
                      <button onClick={async () => {
                        if (window.confirm("Report this post? We will review it within 24 hours.")) {
                          await supabase.from("post_reports").insert({ post_id: post.id, reporter_id: user.id, reason: "User reported" });
                          alert("Thank you — we will review this post within 24 hours.");
                        }
                      }} className="tag" style={{ fontSize: 11, color: "#999" }}>
                        🚩 Report
                      </button>
                      <button onClick={async () => {
                        if (window.confirm("Block this user? Their posts will be removed from your feed immediately.")) {
                          setPosts(prev => prev.filter(p => p.user_id !== post.user_id));
                          await supabase.from("blocked_users").insert({ blocker_id: user.id, blocked_id: post.user_id });
                        }
                      }} className="tag" style={{ fontSize: 11, color: "#999", marginLeft: "auto" }}>
                        🚫 Block
                      </button>
                    </>
                  )}
                </div>
                {commentingOn === post.id && (
                  <div style={{ marginTop: 10 }}>
                    {(comments[post.id] || []).map(c => (
                      <div key={c.id} style={{ display: "flex", gap: 8, marginBottom: 8, padding: "8px 10px", background: C.purpleLt, borderRadius: 10 }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.purple, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "white", flexShrink: 0 }}>
                          {c.is_anonymous ? "?" : (c.profiles?.username || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: C.purpleDk }}>{c.is_anonymous ? "Anonymous" : (c.profiles?.username || c.profiles?.display_name || "Someone")}</div>
                          <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.5 }}>{c.content}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Write a comment..." className="ta" style={{ flex: 1, minHeight: 40 }} />
                      <button onClick={() => submitComment(post.id)} className="pbtn" style={{ width: "auto", padding: "0 14px", background: C.roseDk, fontSize: 12 }} disabled={!commentText.trim()}>Send</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === "support" && (
          <SupportTab user={user} />
        )}
      </div>
      <Nav active="community" go={go} />
    </div>
  );
}

function SupportTab({ user }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replies, setReplies] = useState({});

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    setLoading(true);
    const { data } = await supabase.from("support_posts").select("*").order("created_at", { ascending: false }).limit(30);
    if (data && data.length > 0) {
      const userIds = [...new Set(data.map(p => p.user_id))];
      const { data: profiles } = await supabase.from("profiles").select("id, username, display_name").in("id", userIds);
      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.id] = p; });
      data.forEach(p => { p.profiles = profileMap[p.user_id] || null; });
    }
    setPosts(data || []);
    setLoading(false);
  };

  const submitPost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    await supabase.from("support_posts").insert({ user_id: user.id, content: newPost.trim() });
    setNewPost("");
    await loadPosts();
    setPosting(false);
  };

  const loadReplies = async (postId) => {
    if (replyingTo === postId) { setReplyingTo(null); return; }
    const { data } = await supabase.from("support_replies").select("*").eq("post_id", postId).order("created_at", { ascending: true });
    setReplies(r => ({ ...r, [postId]: data || [] }));
    setReplyingTo(postId);
  };

  const submitReply = async (postId) => {
    if (!replyText.trim()) return;
    await supabase.from("support_replies").insert({ post_id: postId, user_id: user.id, content: replyText.trim() });
    await supabase.from("support_posts").update({ replies_count: (posts.find(p => p.id === postId)?.replies_count || 0) + 1 }).eq("id", postId);
    setReplyText("");
    await loadReplies(postId);
    await loadPosts();
  };

  return (
    <div>
      <div style={{ background: "#FFF8F0", borderRadius: 14, padding: "14px 16px", marginBottom: 14, border: ".5px solid #F4C0D1" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.roseDk, marginBottom: 4 }}>🫂 A safe space to be heard</div>
        <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.6 }}>Share something you're going through. All posts are completely anonymous — no names, no judgement. The community is here to listen and encourage.</div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>What's on your mind?</div>
        <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder="I've been struggling with..." className="ta" style={{ minHeight: 80, marginBottom: 10 }} />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="pbtn" onClick={submitPost} disabled={posting || !newPost.trim()} style={{ width: "auto", padding: "9px 18px", background: C.roseDk, fontSize: 12 }}>
            {posting ? <Spinner /> : "Share anonymously"}
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.rose} /></div>}

      {!loading && posts.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "28px 20px" }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🫂</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 6 }}>No posts yet</div>
          <div style={{ fontSize: 12, color: C.mid }}>Be the first to share. You're not alone here.</div>
        </div>
      )}

      {posts.map(post => (
        <div key={post.id} className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.roseLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🫂</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink }}>Anonymous</div>
              <div style={{ fontSize: 10, color: C.mid }}>{new Date(post.created_at).toLocaleDateString("en-AU", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.7, marginBottom: 10 }}>{post.content}</div>
          <div style={{ display: "flex", gap: 8, borderTop: ".5px solid #f0ebe4", paddingTop: 8 }}>
            <button onClick={() => loadReplies(post.id)} style={{ fontSize: 12, color: C.roseDk, background: C.roseLt, border: "none", borderRadius: 20, padding: "6px 14px", cursor: "pointer", fontWeight: 600 }}>
              🤍 {post.replies_count > 0 ? `${post.replies_count} encouragement${post.replies_count > 1 ? "s" : ""}` : "Send encouragement"}
            </button>
            {post.user_id !== user.id && (
              <>
                <button onClick={async () => {
                  if (window.confirm("Report this post?")) {
                    await supabase.from("post_reports").insert({ post_id: post.id, reporter_id: user.id, reason: "User reported" });
                    alert("Thank you — we will review this post within 24 hours.");
                  }
                }} className="tag" style={{ fontSize: 11, color: "#999" }}>🚩 Report</button>
                <button onClick={async () => {
                  if (window.confirm("Block this user?")) {
                    setPosts(prev => prev.filter(p => p.user_id !== post.user_id));
                    await supabase.from("blocked_users").insert({ blocker_id: user.id, blocked_id: post.user_id });
                  }
                }} className="tag" style={{ fontSize: 11, color: "#999", marginLeft: "auto" }}>🚫 Block</button>
              </>
            )}
          </div>

          {replyingTo === post.id && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: ".5px solid #f0ebe4" }}>
              {(replies[post.id] || []).map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: C.tealLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🤍</div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.ink }}>Anonymous</div>
                    <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.5 }}>{r.content}</div>
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Send some encouragement..." className="ta" style={{ flex: 1, minHeight: 40 }} />
                <button onClick={() => submitReply(post.id)} className="pbtn" style={{ width: "auto", padding: "0 14px", background: C.tealDk, fontSize: 12 }} disabled={!replyText.trim()}>Send</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CrisisScreen({ go, user }) {
  const [tab, setTab] = useState("breathe");
  const [breathPhase, setBreathPhase] = useState("inhale");
  const [breathCount, setBreathCount] = useState(4);
  const [groundStep, setGroundStep] = useState(0);
  const [country, setCountry] = useState("australia");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const [technique, setTechnique] = useState("box");

  const techniques = {
    box:   { name: "Box breathing", desc: "Calm your nervous system", phases: [{ phase:"inhale", count:4 },{ phase:"hold", count:4 },{ phase:"exhale", count:4 },{ phase:"hold", count:4 }] },
    "478": { name: "4-7-8 breathing", desc: "For sleep & deep calm", phases: [{ phase:"inhale", count:4 },{ phase:"hold", count:7 },{ phase:"exhale", count:8 }] },
    calm:  { name: "Calm breathing", desc: "Simple & soothing", phases: [{ phase:"inhale", count:5 },{ phase:"exhale", count:5 }] },
  };

  useEffect(() => {
    supabase.from("profiles").select("emergency_contact, country").eq("id", user.id).single()
      .then(({ data }) => {
        if (data?.emergency_contact) setEmergencyContact(data.emergency_contact);
        if (data?.country) setCountry(data.country);
      });
  }, [user.id]);

  useEffect(() => {
    if (tab !== "breathe") return;
    const phases = techniques[technique].phases;
    let phaseIdx = 0;
    let count = phases[0].count;
    setBreathPhase(phases[0].phase);
    setBreathCount(phases[0].count);
    const interval = setInterval(() => {
      count--;
      setBreathCount(count);
      if (count <= 0) {
        phaseIdx = (phaseIdx + 1) % phases.length;
        count = phases[phaseIdx].count;
        setBreathPhase(phases[phaseIdx].phase);
        setBreathCount(count);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [tab, technique]);

  const groundSteps = [
    { num: 5, sense: "see", prompt: "Name 5 things you can SEE right now" },
    { num: 4, sense: "touch", prompt: "Name 4 things you can TOUCH" },
    { num: 3, sense: "hear", prompt: "Name 3 things you can HEAR" },
    { num: 2, sense: "smell", prompt: "Name 2 things you can SMELL" },
    { num: 1, sense: "taste", prompt: "Name 1 thing you can TASTE" },
  ];

  const helplines = {
    australia: [
      { name: "Lifeline", number: "13 11 14", desc: "24/7 crisis support" },
      { name: "Beyond Blue", number: "1300 22 4636", desc: "Anxiety & depression support" },
      { name: "Suicide Call Back Service", number: "1300 659 467", desc: "24/7 counselling" },
      { name: "Kids Helpline", number: "1800 55 1800", desc: "Ages 5-25, 24/7" },
    ],
    canada: [
      { name: "Crisis Services Canada", number: "1-833-456-4566", desc: "24/7 crisis support" },
      { name: "Kids Help Phone", number: "1-800-668-6868", desc: "24/7 youth support" },
      { name: "Hope for Wellness", number: "1-855-242-3310", desc: "Indigenous peoples support" },
      { name: "Crisis Text Line", number: "Text HOME to 686868", desc: "24/7 text support" },
    ],
    uk: [
      { name: "Samaritans", number: "116 123", desc: "24/7 emotional support" },
      { name: "Crisis Text Line", number: "Text HELLO to 85258", desc: "24/7 text support" },
      { name: "Mind", number: "0300 123 3393", desc: "Mental health support" },
    ],
    usa: [
      { name: "988 Suicide & Crisis Lifeline", number: "988", desc: "Call or text 988, 24/7" },
      { name: "Crisis Text Line", number: "Text HOME to 741741", desc: "24/7 text support" },
      { name: "NAMI Helpline", number: "1-800-950-6264", desc: "Mon-Fri 10am-10pm ET" },
    ],
    newzealand: [
      { name: "Lifeline", number: "0800 543 354", desc: "24/7 crisis support" },
      { name: "Youthline", number: "0800 376 633", desc: "Youth support 24/7" },
      { name: "1737", number: "1737", desc: "Call or text, 24/7" },
    ],
  };

  const phaseColors = { inhale: C.teal, hold: C.purple, exhale: C.rose, rest: C.mid };
  const phaseSize = { inhale: 160, hold: 160, exhale: 110, rest: 100 };
  const phaseLabel = { inhale: "Breathe in", hold: "Hold", exhale: "Breathe out", rest: "Rest" };

  const saveContact = async () => {
    setSavingContact(true);
    await supabase.from("profiles").update({ emergency_contact: emergencyContact, country }).eq("id", user.id);
    setSavingContact(false);
    alert("Saved!");
  };

  return (
    <div className="screen">
      <div className="hdr" style={{ background: "#1a1a2e", paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,0.5)" }} onClick={() => go("home")}>← home</div>
        <div className="htitle" style={{ color: "white" }}>I'm struggling</div>
        <div className="hsub" style={{ color: "rgba(255,255,255,0.5)" }}>You are not alone. Take a breath.</div>
      </div>
      <div className="body" style={{ paddingTop: 10 }}>
        <div style={{ display: "flex", gap: 0, background: "#eee9e2", borderRadius: 10, padding: 3, marginBottom: 16 }}>
          {[["breathe","Breathe"],["ground","Ground"],["helplines","Helplines"],["contact","My contact"]].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "7px 0", fontSize: 10, fontWeight: 600, borderRadius: 8, background: tab===t?"white":"transparent", color: tab===t?"#1a1a2e":C.mid, border: "none" }}>{l}</button>
          ))}
        </div>

        {tab === "breathe" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px 0" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 24, width: "100%" }}>
              {Object.entries(techniques).map(([key, t]) => (
                <button key={key} onClick={() => setTechnique(key)} style={{ flex: 1, padding: "10px 6px", borderRadius: 12, border: technique===key?"2px solid #1a1a2e":"1px solid #e0dbd4", background: technique===key?"#1a1a2e":"white", cursor: "pointer" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: technique===key?"white":"#333" }}>{t.name}</div>
                  <div style={{ fontSize: 10, color: technique===key?"rgba(255,255,255,.6)":"#888", marginTop: 2 }}>{t.desc}</div>
                </button>
              ))}
            </div>

            <div style={{ fontSize: 13, color: C.mid, marginBottom: 32, textAlign: "center", lineHeight: 1.6 }}>
              Follow the circle. Let it guide your breath.
            </div>

            <div style={{
              width: phaseSize[breathPhase] || 130,
              height: phaseSize[breathPhase] || 130,
              borderRadius: "50%",
              background: phaseColors[breathPhase] || C.teal,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              transition: "width 1s ease, height 1s ease, background 1.5s ease",
              marginBottom: 28,
              boxShadow: `0 0 0 20px ${(phaseColors[breathPhase] || C.teal)}22, 0 0 0 40px ${(phaseColors[breathPhase] || C.teal)}11`
            }}>
              <div style={{ fontSize: 12, color: "white", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 4 }}>{phaseLabel[breathPhase] || breathPhase}</div>
              <div style={{ fontSize: 44, fontFamily: "'Playfair Display',serif", color: "white", fontWeight: 600, lineHeight: 1 }}>{breathCount}</div>
            </div>

            <div style={{ fontSize: 12, color: C.mid, textAlign: "center", lineHeight: 1.8 }}>
              {techniques[technique].phases.map((p, i) => (
                <span key={i} style={{ marginRight: 8, opacity: breathPhase===p.phase ? 1 : 0.4, fontWeight: breathPhase===p.phase ? 600 : 400 }}>
                  {phaseLabel[p.phase]} {p.count}{i < techniques[technique].phases.length - 1 ? " ·" : ""}
                </span>
              ))}
            </div>

            <div style={{ marginTop: 32, background: "#f0f0f8", borderRadius: 12, padding: "14px 16px", width: "100%", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#555", lineHeight: 1.7 }}>Breathing exercises activate your parasympathetic nervous system — your body's natural calm response. Even 2 minutes helps.</div>
            </div>
          </div>
        )}

        {tab === "ground" && (
          <div>
            <div style={{ fontSize: 12, color: C.mid, marginBottom: 16, lineHeight: 1.6, textAlign: "center" }}>The 5-4-3-2-1 technique brings you back to the present moment.</div>
            {groundSteps.map((step, i) => (
              <div key={i} onClick={() => setGroundStep(i)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px", background: groundStep===i?"#1a1a2e":C.paper, borderRadius: 12, marginBottom: 8, cursor: "pointer", border: `.5px solid ${groundStep===i?"#1a1a2e":"#e0dbd4"}`, transition: "all .2s" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: groundStep===i?C.teal:C.tealLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: groundStep===i?"white":C.tealDk, flexShrink: 0 }}>{step.num}</div>
                <div style={{ fontSize: 13, color: groundStep===i?"white":C.ink, lineHeight: 1.5 }}>{step.prompt}</div>
              </div>
            ))}
            <div style={{ marginTop: 8, padding: "12px 14px", background: C.tealLt, borderRadius: 12, fontSize: 12, color: C.tealDk, lineHeight: 1.6, textAlign: "center" }}>
              Take your time with each step. There is no rush.
            </div>
          </div>
        )}

        {tab === "helplines" && (
          <div>
            <div className="slbl">Select your country</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 16 }}>
              {[["australia","🇦🇺 Australia"],["canada","🇨🇦 Canada"],["uk","🇬🇧 UK"],["usa","🇺🇸 USA"],["newzealand","🇳🇿 New Zealand"]].map(([c,l]) => (
                <button key={c} onClick={() => setCountry(c)} style={{ padding: "9px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, border: `1px solid ${country===c?"#1a1a2e":"#e0dbd4"}`, background: country===c?"#1a1a2e":"white", color: country===c?"white":C.mid, cursor: "pointer" }}>{l}</button>
              ))}
            </div>
            {(helplines[country] || []).map((h, i) => (
              <div key={i} className="card" style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 2 }}>{h.name}</div>
                <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>{h.desc}</div>
                <a href={`tel:${h.number.replace(/\s/g,"")}`} style={{ display: "block", background: "#1a1a2e", color: "white", borderRadius: 10, padding: "10px", textAlign: "center", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>📞 {h.number}</a>
              </div>
            ))}
          </div>
        )}

        {tab === "contact" && (
          <div>
            <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.7, marginBottom: 16 }}>Save a trusted person's number here. When you're struggling, you can call them directly from this screen.</div>
            <div className="slbl">Emergency contact phone number</div>
            <input className="inp" value={emergencyContact} onChange={e => setEmergencyContact(e.target.value)} placeholder="+61 400 000 000" type="tel" />
            <button className="pbtn" style={{ background: "#1a1a2e", marginBottom: 16 }} onClick={saveContact} disabled={savingContact}>
              {savingContact ? "Saving..." : "Save contact"}
            </button>
            {emergencyContact && (
              <a href={`tel:${emergencyContact.replace(/\s/g,"")}`} style={{ display: "block", background: C.teal, color: "white", borderRadius: 12, padding: "14px", textAlign: "center", fontSize: 15, fontWeight: 600, textDecoration: "none" }}>
                📞 Call {emergencyContact}
              </a>
            )}
            <div style={{ marginTop: 16, padding: "12px 14px", background: C.roseLt, borderRadius: 12, fontSize: 12, color: C.roseDk, lineHeight: 1.6 }}>
              If you are in immediate danger, please call emergency services — 000 in Australia, 999 in UK, 911 in USA.
            </div>
          </div>
        )}
      </div>
      <Nav active="home" go={go} />
    </div>
  );
}


function getAI_safe(text) { return getAI(text); }

function InsightsScreen({ go, user }) {
  const [entries, setEntries] = useState([]);
  const [moods, setMoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState("");
  const [loadingInsight, setLoadingInsight] = useState(false);

  useEffect(() => {
    const weekDates = getWeekDates();
    Promise.all([
      supabase.from("entries").select("*").eq("user_id", user.id).gte("entry_date", weekDates[0]).lte("entry_date", weekDates[6]),
      supabase.from("mood_logs").select("*").eq("user_id", user.id).gte("log_date", weekDates[0]).lte("log_date", weekDates[6]),
    ]).then(([e, m]) => {
      setEntries(e.data || []);
      setMoods(m.data || []);
      setLoading(false);
      generateInsight(e.data || [], m.data || [], weekDates);
    });
  }, [user.id]);

  const generateInsight = async (entriesData, moodsData, weekDates) => {
    const cacheKey = `harlo_insight_${user.id}_${weekDates[0]}_${entriesData.length}_${moodsData.length}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) { setAiInsight(cached); return; }
    if (!entriesData.length && !moodsData.length) {
      setAiInsight("Start logging your mood and writing entries to see your personalised weekly insights here.");
      return;
    }
    setLoadingInsight(true);
    const avgMood = moodsData.length ? (moodsData.reduce((a, m) => a + m.mood_score, 0) / moodsData.length).toFixed(1) : null;
    const tags = entriesData.flatMap(e => e.tags || []);
    const topTags = [...new Set(tags)].slice(0, 3).join(", ") || "none";
    const entryDays = entriesData.map(e => e.entry_date);
    const moodDays = moodsData.map(m => ({ date: m.log_date, score: m.mood_score, label: m.mood_label }));
    const prompt = `You are a warm, empathetic wellness companion. Write a short, personal weekly insight (3-4 sentences) for someone based on their week:
- Mood logs: ${JSON.stringify(moodDays)}
- Journal entries written on: ${entryDays.join(", ") || "none"}
- Average mood score: ${avgMood || "no data"} out of 5
- Most common mood tags: ${topTags}
- Total words written: ${entriesData.reduce((a, e) => a + (e.text || "").split(/\s+/).filter(Boolean).length, 0)}
Be specific, warm and observational. Notice patterns. Don't give generic advice. Speak directly to them. Keep it under 80 words.`;
    try {
      const insight = await getAI(prompt);
      if (insight == null) {
        setAiInsight("AI insights are off. You can turn them on in Profile if you would like a short note about your week.");
        setLoadingInsight(false);
        return;
      }
      const cleaned = insight.replace(/\*\*/g, "").replace(/\*/g, "");
      setAiInsight(cleaned);
      localStorage.setItem(cacheKey, cleaned);
    } catch (e) {
      setAiInsight("You showed up this week. That matters more than you know.");
    }
    setLoadingInsight(false);
  };

  const weekDates = getWeekDates();
  const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const today = localDate();
  const todayIdx = weekDates.indexOf(today);
  const avgMood = moods.length ? (moods.reduce((a, m) => a + m.mood_score, 0) / moods.length).toFixed(1) : null;
  const totalWords = entries.reduce((a, e) => a + (e.text || "").split(/\s+/).filter(Boolean).length, 0);
  const topTag = (() => { const counts = {}; entries.flatMap(e => e.tags || []).forEach(t => { counts[t] = (counts[t] || 0) + 1; }); return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || null; })();
  const moodCounts = { Great: 0, Good: 0, Okay: 0, Low: 0, Hard: 0 };
  const moodMap = { 5: "Great", 4: "Good", 3: "Okay", 2: "Low", 1: "Hard" };
  moods.forEach(m => { if (moodMap[m.mood_score]) moodCounts[moodMap[m.mood_score]]++; });
  const moodColors = { Great: C.teal, Good: C.tealDk, Okay: C.amber, Low: C.rose, Hard: C.roseDk };

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.tealDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("home")}>← home</div>
        <div className="htitle">Weekly insights</div>
        <div className="hsub">Your mood & journal summary</div>
      </div>
      <div className="body">
        {loading ? <div style={{ textAlign: "center", padding: 40 }}><Spinner color={C.teal} /></div> : (
          <>
            <div style={{ background: C.tealLt, borderRadius: 14, padding: "16px", border: `.5px solid ${C.teal}`, marginBottom: 14 }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".08em", color: C.tealDk, fontWeight: 600, marginBottom: 8 }}>✦ Your week, reflected back</div>
              {loadingInsight ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="td" /><span className="td" /><span className="td" />
                  <span style={{ fontSize: 12, color: C.teal }}>Harlo is reflecting on your week...</span>
                </div>
              ) : (
                <div style={{ fontSize: 14, color: C.tealDk, lineHeight: 1.8, fontStyle: "italic" }}>{aiInsight}</div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              {[{ v: entries.length, l: "Entries this week" }, { v: avgMood || "—", l: "Avg mood score" }, { v: totalWords, l: "Words written" }, { v: topTag || "—", l: "Top mood tag" }].map((m, i) => (
                <div key={i} style={{ background: C.tealLt, borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: C.tealDk }}>{m.v}</div>
                  <div style={{ fontSize: 11, color: C.teal, marginTop: 2 }}>{m.l}</div>
                </div>
              ))}
            </div>
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="slbl" style={{ marginBottom: 10 }}>Mood this week</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80 }}>
                {weekDates.map((d, i) => {
                  const mood = moods.find(m => m.log_date === d);
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ width: "100%", height: mood ? `${(mood.mood_score / 5) * 64}px` : "6px", borderRadius: 6, background: i === todayIdx ? C.teal : mood ? C.tealDk : "#e8e2da", opacity: mood ? 1 : 0.35 }} />
                      <div style={{ fontSize: 9, color: i === todayIdx ? C.tealDk : C.mid, fontWeight: i === todayIdx ? 700 : 400 }}>{DAY_LABELS[i].charAt(0)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="slbl" style={{ marginBottom: 10 }}>Mood breakdown</div>
              {Object.entries(moodCounts).map(([label, count]) => {
                const pct = moods.length ? Math.round((count / moods.length) * 100) : 0;
                return (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: C.mid, width: 44 }}>{label}</div>
                    <div style={{ flex: 1, height: 6, background: "#e8e2da", borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: moodColors[label], borderRadius: 99 }} />
                    </div>
                    <div style={{ fontSize: 11, color: C.mid, width: 28, textAlign: "right" }}>{pct}%</div>
                  </div>
                );
              })}
            </div>
            {entries.length === 0 && (
              <div className="card" style={{ textAlign: "center", padding: "28px 20px" }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>✍️</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 6 }}>No entries this week yet</div>
                <div style={{ fontSize: 12, color: C.mid, marginBottom: 14 }}>Write your first entry to start building insights.</div>
                <button className="pbtn" onClick={() => go("entry")}>Write now</button>
              </div>
            )}
          </>
        )}
      </div>
      <Nav active="home" go={go} />
    </div>
  );
}

function MoodSharesInboxScreen({ go, user, setActiveShareId }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      // Find all owners whose circle I am an accepted member of
      const { data: circles } = await supabase
        .from("mood_circles")
        .select("owner_id")
        .eq("member_id", user.id)
        .eq("status", "accepted");
      const ownerIds = (circles || []).map(c => c.owner_id);
      if (ownerIds.length === 0) {
        setShares([]); setLoading(false); return;
      }
      // Fetch all shares from those owners
      const { data: sharesData } = await supabase
        .from("mood_shares")
        .select("id, user_id, mood, created_at")
        .in("user_id", ownerIds)
        .order("created_at", { ascending: false })
        .limit(50);
      // Fetch sender profiles
      const { data: profiles } = await supabase
        .from("profiles").select("id, username, display_name").in("id", ownerIds);
      const profilesMap = {};
      (profiles || []).forEach(p => { profilesMap[p.id] = p; });
      // Fetch responses I have already given (so we know which shares I have already reacted to)
      const shareIds = (sharesData || []).map(s => s.id);
      let myResponses = {};
      if (shareIds.length > 0) {
        const { data: resp } = await supabase
          .from("mood_share_responses")
          .select("share_id, reaction")
          .in("share_id", shareIds)
          .eq("responder_id", user.id);
        (resp || []).forEach(r => { myResponses[r.share_id] = r.reaction; });
      }
      const enriched = (sharesData || []).map(s => ({
        ...s,
        profile: profilesMap[s.user_id] || {},
        myReaction: myResponses[s.id] || null,
      }));
      setShares(enriched);
      setLoading(false);
    };
    load();
  }, [user.id]);

  const moodEmoji = (label) => {
    const map = { "great": "😄", "good": "🙂", "okay": "😐", "low": "😔", "sad": "😢" };
    return map[(label || "").toLowerCase()] || "💭";
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return d.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
  };

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("home")}>← home</div>
        <div className="htitle">Your Circle</div>
        <div className="hsub">Recent moods from people you support</div>
      </div>
      <div className="body">
        {loading ? <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.rose} /></div> : (
          shares.length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: C.mid, fontSize: 13 }}>
              No mood shares yet.<br/>When friends in your circle share their mood, they will appear here.
            </div>
          ) : (
            <div className="card" style={{ marginBottom: 12 }}>
              {shares.map((s, i) => (
                <div key={s.id} onClick={() => { setActiveShareId(s.id); go("moodShare"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < shares.length - 1 ? ".5px solid #e8e2da" : "none", cursor: "pointer" }}>
                  <div style={{ fontSize: 28 }}>{moodEmoji(s.mood)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
                      {s.profile?.username || "someone"} is feeling {s.mood?.toLowerCase()}
                    </div>
                    <div style={{ fontSize: 11, color: C.mid, marginTop: 2 }}>
                      {formatTime(s.created_at)}
                      {s.myReaction && <span style={{ marginLeft: 8, color: C.tealDk }}>✓ you replied</span>}
                    </div>
                  </div>
                  <div style={{ color: "#c0b8ae", fontSize: 16 }}>›</div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function MoodShareScreen({ go, user, activeShareId }) {
  const [share, setShare] = useState(null);
  const [profile, setProfile] = useState(null);
  const [reaction, setReaction] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const REACTIONS = [
    { id: "heart", e: "❤️", l: "sending love" },
    { id: "hug", e: "🫂", l: "sending hugs" },
    { id: "prayer", e: "🙏", l: "thinking of you" },
    { id: "star", e: "✨", l: "you got this" },
    { id: "flower", e: "🌿", l: "here for you" },
  ];

  useEffect(() => {
    const load = async () => {
      if (!activeShareId) { setLoading(false); return; }
      const { data: s } = await supabase
        .from("mood_shares")
        .select("id, user_id, mood, created_at")
        .eq("id", activeShareId)
        .single();
      if (s) {
        setShare(s);
        const { data: p } = await supabase
          .from("profiles").select("id, username, display_name").eq("id", s.user_id).single();
        setProfile(p);
        // Check for existing response
        const { data: r } = await supabase
          .from("mood_share_responses")
          .select("reaction, message")
          .eq("share_id", activeShareId)
          .eq("responder_id", user.id)
          .maybeSingle();
        if (r) {
          setReaction(r.reaction);
          setMessage(r.message || "");
        }
      }
      setLoading(false);
    };
    load();
  }, [activeShareId, user.id]);

  const sendResponse = async () => {
    if (!reaction || saving) return;
    setSaving(true);
    const { error } = await supabase
      .from("mood_share_responses")
      .upsert(
        { share_id: activeShareId, responder_id: user.id, reaction, message: message.trim() || null },
        { onConflict: "share_id,responder_id" }
      );
    setSaving(false);
    if (!error) {
      setDone(true);
      setTimeout(() => go("moodInbox"), 1500);
    }
  };

  if (loading) {
    return <div className="screen"><div className="body" style={{ textAlign: "center", padding: 40 }}><Spinner color={C.rose} /></div></div>;
  }
  if (!share) {
    return <div className="screen"><div className="body" style={{ textAlign: "center", padding: 40, color: C.mid }}>Share not found</div></div>;
  }

  const moodEmoji = (label) => {
    const map = { "great": "😄", "good": "🙂", "okay": "😐", "low": "😔", "sad": "😢" };
    return map[(label || "").toLowerCase()] || "💭";
  };

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("moodInbox")}>← back</div>
        <div className="htitle">{profile?.username || "Someone"}</div>
        <div className="hsub">shared their mood</div>
      </div>
      <div className="body">
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 64, marginBottom: 10 }}>{moodEmoji(share.mood)}</div>
          <div style={{ fontSize: 16, color: C.ink, fontWeight: 600 }}>
            {profile?.username || "Someone"} is feeling {share.mood?.toLowerCase()}
          </div>
        </div>

        {done ? (
          <div style={{ textAlign: "center", padding: 30, color: C.tealDk, fontSize: 14, fontWeight: 600 }}>
            Sent 🌿
          </div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 12, textAlign: "center" }}>Send a reaction</div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                {REACTIONS.map(r => (
                  <button key={r.id} onClick={() => setReaction(r.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 4px", borderRadius: 12, border: reaction === r.id ? `2px solid ${C.roseDk}` : ".5px solid #e8e2da", background: reaction === r.id ? C.roseLt : "white", cursor: "pointer" }}>
                    <span style={{ fontSize: 24 }}>{r.e}</span>
                    <span style={{ fontSize: 9, color: C.mid, fontWeight: 600 }}>{r.l}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 8 }}>Add a message (optional)</div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value.slice(0, 200))}
                placeholder="Just a few words..."
                style={{ width: "100%", border: ".5px solid #e8e2da", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: C.ink, fontFamily: "inherit", resize: "none", minHeight: 60, outline: "none" }}
              />
              <div style={{ fontSize: 10, color: C.mid, textAlign: "right", marginTop: 4 }}>{message.length}/200</div>
            </div>

            <button
              onClick={sendResponse}
              disabled={!reaction || saving}
              className="pbtn"
              style={{ background: reaction ? C.roseDk : "#c0b8ae", marginBottom: 10 }}
            >
              {saving ? "Sending..." : "Send"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function MyMoodSharesScreen({ go, user, setActiveMyShareId }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: sharesData } = await supabase
        .from("mood_shares")
        .select("id, mood, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      const shareIds = (sharesData || []).map(s => s.id);
      let counts = {};
      if (shareIds.length > 0) {
        const { data: resp } = await supabase
          .from("mood_share_responses")
          .select("share_id, reaction")
          .in("share_id", shareIds);
        (resp || []).forEach(r => {
          counts[r.share_id] = (counts[r.share_id] || 0) + 1;
        });
      }
      const enriched = (sharesData || []).map(s => ({
        ...s,
        responseCount: counts[s.id] || 0,
      }));
      setShares(enriched);
      setLoading(false);
    };
    load();
  }, [user.id]);

  const moodEmoji = (label) => {
    const map = { "great": "😄", "good": "🙂", "okay": "😐", "low": "😔", "sad": "😢" };
    return map[(label || "").toLowerCase()] || "💭";
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now.getTime() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return Math.floor(diff/60) + "m ago";
    if (diff < 86400) return Math.floor(diff/3600) + "h ago";
    if (diff < 604800) return Math.floor(diff/86400) + "d ago";
    return d.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
  };

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("profile")}>← back</div>
        <div className="htitle">My mood shares</div>
        <div className="hsub">Responses from your circle</div>
      </div>
      <div className="body">
        {loading ? <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.rose} /></div> : (
          shares.length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: C.mid, fontSize: 13 }}>
              You have not shared a mood yet.<br/>When you do, responses from your circle will appear here.
            </div>
          ) : (
            <div className="card" style={{ marginBottom: 12 }}>
              {shares.map((s, i) => (
                <div key={s.id} onClick={() => { setActiveMyShareId(s.id); go("myShareDetail"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < shares.length - 1 ? ".5px solid #e8e2da" : "none", cursor: "pointer" }}>
                  <div style={{ fontSize: 28 }}>{moodEmoji(s.mood)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
                      Feeling {s.mood?.toLowerCase()}
                    </div>
                    <div style={{ fontSize: 11, color: C.mid, marginTop: 2 }}>
                      {formatDate(s.created_at)}
                      {s.responseCount > 0 ? (
                        <span style={{ marginLeft: 8, color: C.roseDk, fontWeight: 600 }}>
                          {s.responseCount} {s.responseCount === 1 ? "response" : "responses"} 💝
                        </span>
                      ) : (
                        <span style={{ marginLeft: 8, color: C.mid }}>no responses yet</span>
                      )}
                    </div>
                  </div>
                  <div style={{ color: "#c0b8ae", fontSize: 16 }}>›</div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function MyShareDetailScreen({ go, user, activeMyShareId }) {
  const [share, setShare] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!activeMyShareId) { setLoading(false); return; }
      const { data: s } = await supabase
        .from("mood_shares")
        .select("id, mood, created_at, user_id")
        .eq("id", activeMyShareId)
        .single();
      if (!s || s.user_id !== user.id) {
        setLoading(false); return;
      }
      setShare(s);
      const { data: resp } = await supabase
        .from("mood_share_responses")
        .select("id, responder_id, reaction, message, created_at")
        .eq("share_id", activeMyShareId)
        .order("created_at", { ascending: false });
      const responderIds = (resp || []).map(r => r.responder_id);
      let profilesMap = {};
      if (responderIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles").select("id, username, display_name").in("id", responderIds);
        (profiles || []).forEach(p => { profilesMap[p.id] = p; });
      }
      const enriched = (resp || []).map(r => ({
        ...r,
        profile: profilesMap[r.responder_id] || {},
      }));
      setResponses(enriched);
      setLoading(false);
    };
    load();
  }, [activeMyShareId, user.id]);

  const moodEmoji = (label) => {
    const map = { "great": "😄", "good": "🙂", "okay": "😐", "low": "😔", "sad": "😢" };
    return map[(label || "").toLowerCase()] || "💭";
  };

  const reactionEmoji = (id) => {
    const map = { heart: "❤️", hug: "🫂", prayer: "🙏", star: "✨", flower: "🌿" };
    return map[id] || "💭";
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return Math.floor(diff/60) + "m ago";
    if (diff < 86400) return Math.floor(diff/3600) + "h ago";
    return d.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
  };

  if (loading) {
    return <div className="screen"><div className="body" style={{ textAlign: "center", padding: 40 }}><Spinner color={C.rose} /></div></div>;
  }
  if (!share) {
    return <div className="screen"><div className="body" style={{ textAlign: "center", padding: 40, color: C.mid }}>Share not found</div></div>;
  }

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("myShares")}>← back</div>
        <div className="htitle">Your share</div>
        <div className="hsub">{new Date(share.created_at).toLocaleDateString("en-AU", { weekday: "long", month: "long", day: "numeric" })}</div>
      </div>
      <div className="body">
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>{moodEmoji(share.mood)}</div>
          <div style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>You shared feeling {share.mood?.toLowerCase()}</div>
        </div>

        {responses.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: C.mid, fontSize: 13 }}>
            No responses yet.<br/>Your circle has been notified.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 8, paddingLeft: 4 }}>
              {responses.length} {responses.length === 1 ? "response" : "responses"}
            </div>
            <div className="card" style={{ marginBottom: 12 }}>
              {responses.map((r, i) => (
                <div key={r.id} style={{ padding: "12px 0", borderBottom: i < responses.length - 1 ? ".5px solid #e8e2da" : "none" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.roseLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.roseDk, flexShrink: 0 }}>
                      {(r.profile?.username || "?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{r.profile?.username || "someone"}</span>
                        <span style={{ fontSize: 18 }}>{reactionEmoji(r.reaction)}</span>
                        <span style={{ fontSize: 10, color: C.mid }}>{formatTime(r.created_at)}</span>
                      </div>
                      {r.message && (
                        <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.4, background: C.roseLt, padding: "8px 12px", borderRadius: 10, marginTop: 4 }}>
                          {r.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CircleScreen({ go, user }) {
  const [members, setMembers] = useState([]);          // accepted in MY circle (people I added who accepted)
  const [pendingSent, setPendingSent] = useState([]);  // I added them, they haven't responded
  const [pendingReceived, setPendingReceived] = useState([]); // someone added me, I need to accept/decline
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState("");
  const [addErr, setAddErr] = useState("");
  const [adding, setAdding] = useState(false);

  const loadCircle = useCallback(async () => {
    setLoading(true);
    // Members I added (where I am the owner)
    const { data: myCircle } = await supabase
      .from("mood_circles")
      .select("id, member_id, status, created_at")
      .eq("owner_id", user.id);
    // Members who added me (where I am the member)
    const { data: theirCircles } = await supabase
      .from("mood_circles")
      .select("id, owner_id, status, created_at")
      .eq("member_id", user.id);

    const allUserIds = [
      ...((myCircle || []).map(c => c.member_id)),
      ...((theirCircles || []).map(c => c.owner_id)),
    ];
    let profilesMap = {};
    if (allUserIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles").select("id, username, display_name").in("id", allUserIds);
      (profiles || []).forEach(p => { profilesMap[p.id] = p; });
    }

    const accepted = (myCircle || []).filter(c => c.status === "accepted").map(c => ({
      ...c, profile: profilesMap[c.member_id] || {},
    }));
    const sent = (myCircle || []).filter(c => c.status === "pending").map(c => ({
      ...c, profile: profilesMap[c.member_id] || {},
    }));
    const received = (theirCircles || []).filter(c => c.status === "pending").map(c => ({
      ...c, profile: profilesMap[c.owner_id] || {},
    }));

    setMembers(accepted);
    setPendingSent(sent);
    setPendingReceived(received);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { loadCircle(); }, [loadCircle]);

  const addMember = async () => {
    setAddErr("");
    if (!newUsername.trim()) { setAddErr("Enter a username"); return; }
    if (members.length + pendingSent.length >= 5) {
      setAddErr("Circle is full (max 5)"); return;
    }
    setAdding(true);
    const cleanUsername = newUsername.trim();
    const { data: profile } = await supabase
      .from("profiles").select("id, username").eq("username", cleanUsername).single();
    if (!profile) {
      setAddErr("No Harlo user found with that username");
      setAdding(false); return;
    }
    if (profile.id === user.id) {
      setAddErr("You can't add yourself");
      setAdding(false); return;
    }
    const { error } = await supabase
      .from("mood_circles")
      .insert({ owner_id: user.id, member_id: profile.id, status: "pending" });
    if (error) {
      if (error.code === "23505") {
        setAddErr("Already in your circle (or invite pending)");
      } else {
        setAddErr("Could not add — please try again");
      }
      setAdding(false); return;
    }
    setNewUsername("");
    setAdding(false);
    await loadCircle();
  };

  const respondToInvite = async (circleId, accept) => {
    await supabase
      .from("mood_circles")
      .update({ status: accept ? "accepted" : "declined", updated_at: new Date().toISOString() })
      .eq("id", circleId);
    await loadCircle();
  };

  const removeMember = async (circleId) => {
    await supabase.from("mood_circles").delete().eq("id", circleId);
    await loadCircle();
  };

  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("profile")}>← back</div>
        <div className="htitle">Your Circle</div>
        <div className="hsub">Up to 5 trusted people</div>
      </div>
      <div className="body">
        {loading ? <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.rose} /></div> : (
          <>
            {pendingReceived.length > 0 && (
              <div className="card" style={{ marginBottom: 12, background: C.amberLt }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.amberDk, marginBottom: 8 }}>Invitations to you</div>
                {pendingReceived.map(inv => (
                  <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: ".5px solid #f0e6d4" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{inv.profile?.username || "someone"}</div>
                      <div style={{ fontSize: 11, color: C.mid }}>wants you in their circle</div>
                    </div>
                    <button onClick={() => respondToInvite(inv.id, true)} style={{ background: C.teal, color: "white", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>Accept</button>
                    <button onClick={() => respondToInvite(inv.id, false)} style={{ background: "none", color: C.mid, border: ".5px solid #d8d2c8", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>Decline</button>
                  </div>
                ))}
              </div>
            )}

            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 8 }}>Add to your circle</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="inp"
                  value={newUsername}
                  onChange={e => { setNewUsername(e.target.value.replace(/\s/g, "_").replace(/[^a-zA-Z0-9_-]/g, "")); setAddErr(""); }}
                  placeholder="their_username"
                  style={{ flex: 1, marginBottom: 0 }}
                />
                <button onClick={addMember} disabled={adding} className="pbtn" style={{ background: C.rose, padding: "10px 16px", fontSize: 13, width: "auto" }}>{adding ? "..." : "Add"}</button>
              </div>
              {addErr && <div style={{ fontSize: 11, color: "#c54040", marginTop: 6 }}>{addErr}</div>}
              <div style={{ fontSize: 10, color: C.mid, marginTop: 6 }}>{members.length + pendingSent.length} of 5 used</div>
            </div>

            {members.length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 8 }}>In your circle</div>
                {members.map(m => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: ".5px solid #e8e2da" }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.roseLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.roseDk }}>
                      {(m.profile?.username || "?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, fontSize: 13, color: C.ink }}>{m.profile?.username || "unknown"}</div>
                    <button onClick={() => removeMember(m.id)} style={{ background: "none", color: C.mid, border: "none", fontSize: 11, cursor: "pointer" }}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            {pendingSent.length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.mid, marginBottom: 8 }}>Pending invites</div>
                {pendingSent.map(p => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: ".5px solid #e8e2da" }}>
                    <div style={{ flex: 1, fontSize: 13, color: C.mid }}>{p.profile?.username || "unknown"} <span style={{ fontSize: 10 }}>(waiting)</span></div>
                    <button onClick={() => removeMember(p.id)} style={{ background: "none", color: C.mid, border: "none", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                  </div>
                ))}
              </div>
            )}

            {members.length === 0 && pendingSent.length === 0 && pendingReceived.length === 0 && (
              <div style={{ textAlign: "center", padding: 30, color: C.mid, fontSize: 13 }}>
                Your circle is empty.<br/>Add a friend by their Harlo username above.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProfileScreen({ go, user }) {
  const [entryCount, setEntryCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [totalMoods, setTotalMoods] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const name = user?.user_metadata?.display_name || user?.email?.split("@")[0] || "there";
  const initials = name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
  const joinDate = new Date(user?.created_at).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  useEffect(() => {
    const weekDates = getWeekDates();
    Promise.all([
      supabase.from("entries").select("id", { count: "exact" }).eq("user_id", user.id),
      supabase.from("mood_logs").select("log_date").eq("user_id", user.id).order("log_date", { ascending: false }).limit(1000),
    ]).then(([e, m]) => {
      setEntryCount(e.count || 0);
      const dates = (m.data || []).map(d => d.log_date);
      setTotalMoods(dates.length);
      let count = 0;
      const check = new Date();
      if (!dates.includes(localDate(check))) check.setDate(check.getDate() - 1);
      while (true) {
        const s = localDate(check);
        if (dates.includes(s)) { count++; check.setDate(check.getDate() - 1); } else break;
      }
      setStreak(count);
      setLoading(false);
    });
  }, [user.id]);
  const [aiOn, setAiOn] = useState(() => readAiConsent(user.id) === "yes");
  const toggleAi = () => { const next = !aiOn; writeAiConsent(user.id, next ? "yes" : "no"); setAiOn(next); };
  const [deleteErr, setDeleteErr] = useState("");
  const deleteAccount = async () => {
    setDeleting(true); setDeleteErr("");
    try {
      // The server function deletes all of this user's data and the login itself
      const { data, error } = await supabase.functions.invoke("smart-worker");
      if (error || data?.error) throw new Error("delete failed");
      try { Object.keys(localStorage).filter(k => k.includes(user.id)).forEach(k => localStorage.removeItem(k)); } catch (e) {}
      try { await window.Capacitor?.Plugins?.LocalNotifications?.cancel({ notifications: [{ id: 1001 }, { id: 1002 }, { id: 1003 }, { id: 1010 }, { id: 1011 }, { id: 1020 }] }); } catch (e) {}
      await supabase.auth.signOut();
    } catch (e) {
      setDeleteErr("We couldn't delete your account just now. Please check your connection and try again, or contact support.");
      setDeleting(false);
    }
  };
  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("home")}>← home</div>
        <div className="htitle">Profile</div>
        <div className="hsub">Your journey so far</div>
      </div>
      <div className="body">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 0 20px" }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.purpleLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 700, color: C.purpleDk, marginBottom: 12 }}>{initials}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.ink }}>{name}</div>
          <div style={{ fontSize: 12, color: C.mid, marginTop: 2 }}>{user?.email}</div>
          <div style={{ fontSize: 11, color: C.mid, marginTop: 4 }}>Member since {joinDate}</div>
        </div>
        {loading ? <div style={{ textAlign: "center", padding: 20 }}><Spinner color={C.purple} /></div> : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            {[{ v: entryCount, l: "Entries" }, { v: `${streak}🔥`, l: "Day streak" }, { v: totalMoods, l: "Nights checked in" }].map((s, i) => (
              <div key={i} style={{ background: C.purpleLt, borderRadius: 12, padding: "14px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.purpleDk }}>{s.v}</div>
                <div style={{ fontSize: 10, color: C.purple, marginTop: 2 }}>{s.l}</div>
              </div>
            ))}
          </div>
        )}
        <div className="card" style={{ marginBottom: 10 }}>
          {[
            { label: "My journal", action: () => go("reflect"), icon: "📖" },
            { label: "Weekly insights", action: () => go("insights"), icon: "📊" },
            { label: "Your circle", action: () => go("circle"), icon: "🌿", on: FEATURES.circle },
            { label: "Mood shares", action: () => go("moodInbox"), icon: "💭", on: FEATURES.circle },
            { label: "My shares", action: () => go("myShares"), icon: "💝", on: FEATURES.circle },
            { label: "Reminders", action: () => go("reminders"), icon: "🔔" },
            { label: "Contact support", action: () => { window.location.href = "mailto:support@harlo-app.com?subject=Harlo%20support"; }, icon: "💌" },
            { label: "Privacy policy", action: () => go("privacy"), icon: "🔒" },
            { label: "Terms of service", action: () => go("terms"), icon: "📄" },
          ].filter(item => item.on !== false).map((item, i, arr) => (
            <div key={i} onClick={item.action} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < arr.length - 1 ? `.5px solid #e8e2da` : "none", cursor: "pointer" }}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              <div style={{ flex: 1, fontSize: 14, color: C.ink }}>{item.label}</div>
              <div style={{ color: "#c0b8ae", fontSize: 16 }}>›</div>
            </div>
          ))}
        </div>
        <div className="card" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: C.ink }}>AI replies</div>
            <div style={{ fontSize: 11, color: C.mid, marginTop: 3, lineHeight: 1.5 }}>{aiOn ? "On. Your entries are sent to an AI service (Anthropic) to write replies and insights." : "Off. Nothing is sent to an AI service and no replies are written."}</div>
          </div>
          <button onClick={toggleAi} style={{ padding: "8px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: aiOn ? "#2D5A4F" : "#e0dbd4", color: aiOn ? "white" : C.mid }}>{aiOn ? "On" : "Off"}</button>
        </div>
        <button onClick={() => supabase.auth.signOut()} className="pbtn" style={{ background: C.roseDk, marginBottom: 10 }}>Sign out</button>
        {!showDelete ? (
          <div style={{ textAlign: "center" }}>
            <button onClick={() => setShowDelete(true)} style={{ background: "none", border: "none", fontSize: 12, color: C.mid, cursor: "pointer", textDecoration: "underline" }}>Delete my account</button>
          </div>
        ) : (
          <div style={{ background: "#FCEBEB", borderRadius: 12, padding: 16, border: ".5px solid #F09595" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#A32D2D", marginBottom: 6 }}>Delete account?</div>
            <div style={{ fontSize: 12, color: "#A32D2D", marginBottom: 14, lineHeight: 1.6 }}>This will permanently delete your account and everything in it: entries, moods, letters, reminders and any shared content. This cannot be undone.</div>
            {deleteErr && <div style={{ fontSize: 12, color: "#A32D2D", fontWeight: 600, marginBottom: 12, lineHeight: 1.5 }}>{deleteErr}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowDelete(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: ".5px solid #e0dbd4", background: "white", fontSize: 13, color: C.mid, cursor: "pointer" }}>Cancel</button>
              <button onClick={deleteAccount} disabled={deleting} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: "#A32D2D", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{deleting ? "Deleting..." : "Yes, delete"}</button>
            </div>
          </div>
        )}
      </div>
      <Nav active="settings" go={go} />
    </div>
  );
}

function PrivacyScreen({ go }) {
  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("profile")}>← back</div>
        <div className="htitle">Privacy policy</div>
        <div className="hsub">Last updated October 2026</div>
      </div>
      <div className="body">
        {[
          { t: "Who we are", b: "Harlo is a wellness and journaling app made by Callum Benson in Perth, Western Australia. Questions about your privacy can be sent to support@harlo-app.com." },
          { t: "Information we collect", b: "Your email address, the name you choose, an automatically created username, and what you add to the app: journal entries, gratitude notes, mood logs and tags, letters to your future self, and your reminder settings. If you allow notifications, we also store a device token so reminders can reach you. We do not collect your location, contacts or advertising identifiers." },
          { t: "How we use your information", b: "Only to run Harlo for you: to save your entries and moods, show your insights and streaks, deliver reminders and letters, and generate AI reflections. We do not sell your data and we do not use it for advertising." },
          { t: "AI reflections and insights", b: "When you ask for a reflection or open your weekly insights, the relevant journal text and mood information is sent from our server to Anthropic, the provider of the Claude AI, so it can write a reply. How Anthropic handles that information is described in its own privacy policy at anthropic.com/privacy. AI replies can be wrong, so please treat them as gentle suggestions only. Harlo asks your permission before it sends anything to the AI service, and you can turn AI replies off at any time in Profile." },
          { t: "Notifications", b: "Harlo can send reminders to wind down, a note if you have been away for a while, and a message when a letter you wrote to yourself arrives. These are optional. You can turn the evening reminder off in Profile, then Reminders, or turn all notifications off in your device settings." },
          { t: "Where your data is stored", b: "Your data is stored with Supabase, a cloud database provider. It is encrypted in transit, and our hosting providers encrypt stored data. Only you can see your journal entries and moods in the app." },
          { t: "Earlier features", b: "Earlier versions of Harlo included community, cheers and mood-sharing features. These are currently switched off. Anything you posted or shared earlier is kept until you delete your account, and it is not visible to others while these features are off. If we turn them back on, we will update this policy first." },
          { t: "Keeping and deleting your data", b: "We keep your data while your account is active. You can delete your account at any time from Profile, then Delete my account. This permanently removes your login and your data, including entries, moods, letters, reminders and any shared content. Deleted data may remain in routine backups for a short time before it is removed." },
          { t: "Your rights", b: "You can ask to see, correct or delete your personal information at any time by contacting support@harlo-app.com. If you are in Australia, you may also contact the Office of the Australian Information Commissioner (oaic.gov.au)." },
          { t: "Changes to this policy", b: "If we make important changes we will update this page and the date above." },
        ].map((s, i) => (
          <div key={i} className="card" style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 6 }}>{s.t}</div>
            <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.7 }}>{s.b}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TermsScreen({ go }) {
  return (
    <div className="screen">
      <div className="hdr" style={{ background: C.roseDk, paddingTop: 52 }}>
        <div className="back" style={{ color: "rgba(255,255,255,.5)" }} onClick={() => go("profile")}>← back</div>
        <div className="htitle">Terms of service</div>
        <div className="hsub">Last updated October 2026</div>
      </div>
      <div className="body">
        {[
          { t: "Acceptance of terms", b: "By using Harlo, you agree to these terms. If you do not agree, please do not use the app." },
          { t: "Not a medical service", b: "Harlo is a wellness and journaling app. It is not a medical or mental health service, and AI reflections are not a substitute for professional advice. If you are in crisis, please use the helplines in the app. If you are in immediate danger, call your local emergency number (000 in Australia)." },
          { t: "AI reflections", b: "Reflections and insights are generated by AI and may sometimes be inaccurate or unsuitable. Please use your own judgement and speak to a professional if you need support." },
          { t: "Your account and content", b: "You must be 16 or older to use Harlo. You are responsible for keeping your login secure. You own what you write, and you give us permission to store it and process it (including sending it to our AI provider) so we can provide the app to you." },
          { t: "Acceptable use", b: "Please do not misuse the app, attempt to access other people's data, or use it for anything harmful, abusive or illegal." },
          { t: "Account termination", b: "We may suspend or end accounts that break these terms. You can delete your account at any time from Profile, then Delete my account." },
          { t: "Changes and availability", b: "We may update the app and these terms from time to time. Harlo is provided as is, and we cannot promise it will always be available or error free." },
          { t: "Contact", b: "For questions about these terms, contact us at support@harlo-app.com." },
        ].map((s, i) => (
          <div key={i} className="card" style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 6 }}>{s.t}</div>
            <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.7 }}>{s.b}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OnboardingScreen({ user, onDone, scheduleDailyPrompt }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState(user?.user_metadata?.display_name || "");
  const [windTime, setWindTime] = useState("21:30");
  const [useReminder, setUseReminder] = useState(true);
  const [saveErr, setSaveErr] = useState("");
  const [saving, setSaving] = useState(false);
  const fmtTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "pm" : "am"}`;
  };
  return (
    <div className="screen" style={{ background: C.roseDk }}>
      {step > 0 && (
        <div style={{ padding: "52px 20px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={() => setStep(s => s - 1)} style={{ background: "none", border: "none", color: "rgba(255,255,255,.5)", fontSize: 13, cursor: "pointer" }}>← back</button>
          <div style={{ display: "flex", gap: 6 }}>
            {[1,2,3].map(i => (<div key={i} style={{ width: i <= step ? 20 : 6, height: 6, borderRadius: 99, background: i <= step ? "white" : "rgba(255,255,255,.25)", transition: "width .3s" }} />))}
          </div>
        </div>
      )}
      {step === 0 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 52, color: "white", fontWeight: 600, lineHeight: 1.1, marginBottom: 16 }}>Harlo</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,.65)", lineHeight: 1.7, marginBottom: 48 }}>Your evening wind-down.</div>
          <button className="pbtn" onClick={() => setStep(1)} style={{ background: "white", color: C.roseDk, fontSize: 15, padding: "14px 0" }}>Get started</button>
        </div>
      )}
      {step === 1 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 28px 40px" }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginBottom: 8 }}>Step 1 of 3</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, color: "white", fontWeight: 600, marginBottom: 8 }}>What should we call you?</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.6)", marginBottom: 32, lineHeight: 1.6 }}>This is how we'll greet you each day.</div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: "none", background: "rgba(255,255,255,.15)", color: "white", fontSize: 16, marginBottom: 16, outline: "none" }} />
          <button className="pbtn" onClick={() => name.trim() && setStep(2)} disabled={!name.trim()} style={{ background: "white", color: C.roseDk, fontSize: 15, padding: "14px 0", marginTop: "auto" }}>Continue</button>
        </div>
      )}
      {step === 2 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 28px 40px" }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginBottom: 8 }}>Step 2 of 3</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, color: "white", fontWeight: 600, marginBottom: 8 }}>What time do you wind down?</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.6)", marginBottom: 28, lineHeight: 1.6 }}>We'll send one gentle reminder each evening to check in and drift off with a sleep story.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
            {["21:00", "21:30", "22:00", "22:30"].map(t => (
              <button key={t} onClick={() => setWindTime(t)} style={{ padding: "9px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, background: windTime === t ? "white" : "rgba(255,255,255,.15)", color: windTime === t ? C.roseDk : "white" }}>{fmtTime(t)}</button>
            ))}
          </div>
          <input type="time" value={windTime} onChange={e => e.target.value && setWindTime(e.target.value)} style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: "none", background: "rgba(255,255,255,.15)", color: "white", fontSize: 22, textAlign: "center", colorScheme: "dark" }} />
          <button className="pbtn" onClick={() => { setUseReminder(true); setStep(3); }} style={{ background: "white", color: C.roseDk, fontSize: 15, padding: "14px 0", marginTop: "auto" }}>Remind me at {fmtTime(windTime)}</button>
          <button onClick={() => { setUseReminder(false); setStep(3); }} style={{ background: "none", color: "rgba(255,255,255,.55)", fontSize: 13, padding: "14px 0 0" }}>Skip for now</button>
        </div>
      )}
      {step === 3 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "24px 28px 40px" }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginBottom: 8 }}>Step 3 of 3</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, color: "white", fontWeight: 600, marginBottom: 24 }}>Your evening, sorted</div>
          {saveErr && <div style={{ background: "rgba(255,255,255,.15)", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "white", marginBottom: 12 }}>{saveErr}</div>}
          {[
            { ic: "🌙", t: "Sleep stories", s: "Calm, narrated stories to help you drift off" },
            { ic: "✍️", t: "Daily check-in", s: "A quick moment to notice how you feel" },
            { ic: "🔔", t: "Gentle reminder", s: useReminder ? `We'll nudge you at ${fmtTime(windTime)} each evening` : "You can turn on a reminder any time in Profile" },
            { ic: "🆘", t: "Crisis support", s: "Breathing, grounding & helplines always available" },
          ].map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{f.ic}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "white" }}>{f.t}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)", marginTop: 1 }}>{f.s}</div>
              </div>
            </div>
          ))}
          <button className="pbtn" onClick={async () => {
            setSaving(true); setSaveErr("");
            try {
              // Auto-generate a unique username (name + 4 digits); it can be changed later in Reminders.
              const base = name.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_-]/g, "").slice(0, 14) || "harlo";
              let finalName = "";
              for (let i = 0; i < 6; i++) {
                const candidate = base + Math.floor(1000 + Math.random() * 9000);
                const { data: taken } = await supabase.from("profiles").select("id").eq("username", candidate).single();
                if (!taken) { finalName = candidate; break; }
              }
              if (!finalName) finalName = base + String(Date.now()).slice(-6);
              const { error: profErr } = await supabase.from("profiles").update({ username: finalName, display_name: name.trim(), has_onboarded: true }).eq("id", user.id);
              if (profErr) { setSaveErr("Something went wrong saving your profile. Please try again."); setSaving(false); return; }
              await supabase.auth.updateUser({ data: { display_name: name.trim() } });
              if (useReminder) {
                try {
                  await supabase.from("reminders").upsert({
                    user_id: user.id,
                    journal_on: true, journal_time: windTime,
                    quote_on: false, quote_time: "07:30",
                    medication_on: false, medication_time: "08:00",
                    weekly_on: false,
                    updated_at: new Date().toISOString(),
                  }, { onConflict: "user_id" });
                } catch (e) {}
                try { await scheduleDailyPrompt(windTime); } catch (e) {}
              }
            } catch (e) {
              setSaveErr("Something went wrong. Please check your connection and try again.");
              setSaving(false);
              return;
            }
            setSaving(false); onDone();
          }} disabled={saving} style={{ background: "white", color: C.roseDk, fontSize: 15, padding: "14px 0", marginTop: "auto" }}>{saving ? "Setting up..." : "Let's go ✦"}</button>
        </div>
      )}
    </div>
  );
}


function ResetPasswordScreen({ onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (pw.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    if (error) { setErr(error.message); setLoading(false); return; }
    setOk(true);
    setTimeout(() => onDone(), 2000);
  };
  return (
    <div className="screen" style={{ background: C.roseDk }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px" }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, color: "white", fontWeight: 600, marginBottom: 8 }}>New password</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,.65)", marginBottom: 32 }}>Choose a new password for your account.</div>
        {err && <div className="err" style={{ width: "100%" }}>{err}</div>}
        {ok && <div className="ok" style={{ width: "100%" }}>Password updated! Signing you in...</div>}
        <input type="password" className="inp" value={pw} onChange={e => setPw(e.target.value)} placeholder="New password" style={{ marginBottom: 12 }} />
        <input type="password" className="inp" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Confirm new password" />
        <button className="pbtn" onClick={save} disabled={loading || ok} style={{ marginTop: 8 }}>{loading ? "Saving..." : "Set new password"}</button>
      </div>
    </div>
  );
}



// Gentle safety net: if an entry mentions self-harm or suicide, show support options after saving.
// This is a simple phrase check, not a diagnosis, and it never blocks saving.
const CRISIS_PHRASES = [
  "suicid", "kill myself", "killing myself", "end my life", "end it all", "take my own life", "take my life",
  "want to die", "wanna die", "wish i was dead", "wish i were dead", "better off dead", "better off without me",
  "don't want to be here anymore", "don't want to live", "don't want to exist", "no reason to live",
  "self harm", "self-harm", "selfharm", "hurt myself", "hurting myself", "harm myself", "cut myself", "overdose",
];
function looksLikeCrisis(text) {
  const t = String(text || "").toLowerCase().replace(/[\u2018\u2019]/g, "'");
  return CRISIS_PHRASES.some(p => t.includes(p));
}

// Records when a story starts or finishes (best effort; never blocks or breaks playback)
// Which story to suggest tonight, based on how the person said they felt today (nothing is sent anywhere)
const TONIGHT_PICKS = {
  tired:      { id: 7,  title: "The Cabin in the Rain",  why: "You felt tired today. A warm place to rest." },
  anxious:    { id: 8,  title: "The Night Train",        why: "Your mind felt busy. A steady rhythm to follow." },
  frustrated: { id: 10, title: "The Lake at Dusk",       why: "Today felt frustrating. Still water, nowhere to be." },
  sad:        { id: 12, title: "The Snowed-In Village",  why: "Today felt heavy. Warmth and quiet." },
  good:       { id: 9,  title: "The Lighthouse Keeper",  why: "A good day. A gentle way to end it." },
  okay:       { id: 11, title: "The Quiet Library",      why: "An ordinary day. Slow down with a quiet one." },
};
async function getTonightPick(userId) {
  try {
    const today = localDate();
    const [ent, mood] = await Promise.all([
      supabase.from("entries").select("tags").eq("user_id", userId).eq("entry_date", today).order("created_at", { ascending: false }).limit(3),
      supabase.from("mood_logs").select("mood_score").eq("user_id", userId).eq("log_date", today).limit(1),
    ]);
    const tags = (ent.data || []).flatMap(e => e.tags || []).map(t => String(t).toLowerCase());
    if (tags.includes("anxious")) return TONIGHT_PICKS.anxious;
    if (tags.includes("sad")) return TONIGHT_PICKS.sad;
    if (tags.includes("frustrated")) return TONIGHT_PICKS.frustrated;
    if (tags.includes("tired")) return TONIGHT_PICKS.tired;
    const score = mood.data && mood.data[0] ? mood.data[0].mood_score : null;
    if (score !== null && score <= 2) return TONIGHT_PICKS.sad;
    if (score === 3) return TONIGHT_PICKS.okay;
    if (score !== null && score >= 4) return TONIGHT_PICKS.good;
    if (tags.length) return TONIGHT_PICKS.good;
    return null;
  } catch (e) { return null; }
}

function logStoryEvent(userId, storyId, event) {
  if (!userId) return;
  try { Promise.resolve(supabase.from("story_plays").insert({ user_id: userId, story_id: storyId, event })).catch(() => {}); } catch (e) {}
}

// Asks for an App Store rating once, after a good moment (3+ check-ins or 2+ finished stories).
// iOS app only, once per person per device. Apple decides whether the pop-up actually appears.
async function maybeAskReview(userId) {
  try {
    if (!userId || !Capacitor.isNativePlatform()) return;
    const key = `harlo_review_asked_${userId}`;
    if (localStorage.getItem(key)) return;
    const [{ count: entryCount }, { count: storyCount }] = await Promise.all([
      supabase.from("entries").select("*", { count: "exact", head: true }).eq("user_id", userId),
      supabase.from("story_plays").select("*", { count: "exact", head: true }).eq("user_id", userId).eq("event", "complete"),
    ]);
    if ((entryCount || 0) < 3 && (storyCount || 0) < 2) return;
    localStorage.setItem(key, String(Date.now()));
    setTimeout(() => { try { InAppReview.requestReview(); } catch (e) {} }, 3000);
  } catch (e) {}
}

// Sleep audio helpers. State lives on window so playback and the timer continue across screens.
function harloHalt() {
  try { window._harloAudio?.pause(); } catch (e) {}
  window._harloAudio = null;
  window._harloAudioId = null;
}
function harloStopCountdown() {
  if (window._harloSleepPoll) clearInterval(window._harloSleepPoll);
  window._harloSleepPoll = null;
  window._harloSleepEnd = null; // the chosen length (_harloSleepMins) is kept for the session
}
// Fades the volume over the last 10 seconds, then stops. Called by audio "timeupdate" and a 1s poll.
function harloCheckSleep() {
  const a = window._harloAudio;
  const end = window._harloSleepEnd;
  if (!a || !end) return;
  const left = end - Date.now();
  if (left <= 0) { harloHalt(); harloStopCountdown(); return; }
  if (left <= 10000) a.volume = Math.max(0, 0.6 * left / 10000);
}
// Starts (or restarts) the countdown from now. If nothing is playing, it starts when a story does.
function harloArmTimer(mins) {
  harloStopCountdown();
  window._harloSleepMins = mins || 0;
  if (window._harloAudio) window._harloAudio.volume = 0.6;
  if (!mins || !window._harloAudio) return;
  window._harloSleepEnd = Date.now() + mins * 60000;
  window._harloSleepPoll = setInterval(harloCheckSleep, 1000);
}

// Guided breathing for winding down (4-7-8 or box breathing), stops by itself after six rounds
function BreathePanel() {
  const TECH = {
    "478": { name: "4-7-8", note: "Slow and deep, for sleep", phases: [["Breathe in", 4], ["Hold", 7], ["Breathe out", 8]] },
    box: { name: "Box", note: "Steady and even", phases: [["Breathe in", 4], ["Hold", 4], ["Breathe out", 4], ["Hold", 4]] },
  };
  const [tech, setTech] = useState("478");
  const [running, setRunning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [left, setLeft] = useState(4);
  const [rounds, setRounds] = useState(0);
  const [finished, setFinished] = useState(false);
  const phases = TECH[tech].phases;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setLeft(l => l - 1), 1000);
    return () => clearInterval(t);
  }, [running]);
  useEffect(() => {
    if (!running || left > 0) return;
    const next = (idx + 1) % phases.length;
    if (next === 0) {
      if (rounds + 1 >= 6) { setRunning(false); setFinished(true); setIdx(0); setLeft(phases[0][1]); setRounds(0); return; }
      setRounds(r => r + 1);
    }
    setIdx(next); setLeft(phases[next][1]);
  }, [left, running]);
  const start = () => { setIdx(0); setLeft(phases[0][1]); setRounds(0); setFinished(false); setRunning(true); };
  const stop = () => { setRunning(false); setIdx(0); setLeft(phases[0][1]); setRounds(0); };
  const pick = (k) => { setTech(k); setRunning(false); setFinished(false); setIdx(0); setLeft(TECH[k].phases[0][1]); setRounds(0); };
  const name = phases[idx][0];
  const prevName = phases[(idx + phases.length - 1) % phases.length][0];
  const big = running && (name === "Breathe in" || (name === "Hold" && prevName === "Breathe in"));
  const moving = running && name !== "Hold";
  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:20 }}>
        {Object.entries(TECH).map(([k, t]) => (
          <button key={k} onClick={() => pick(k)} style={{ flex:1, padding:"10px 8px", borderRadius:12, border:"none", fontSize:12, fontWeight:600, cursor:"pointer", background: tech === k ? "rgba(255,255,255,.15)" : "rgba(255,255,255,.05)", color: "white" }}>
            {t.name}<div style={{ fontSize:10, fontWeight:400, opacity:.6, marginTop:2 }}>{t.note}</div>
          </button>
        ))}
      </div>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"10px 0 24px" }}>
        <div style={{ width:200, height:200, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ width:200, height:200, borderRadius:"50%", background:"radial-gradient(circle, rgba(122,170,255,.55) 0%, rgba(80,110,200,.25) 70%)", transform:`scale(${big ? 1 : 0.6})`, transition: moving ? `transform ${phases[idx][1]}s linear` : "transform .4s ease", display:"flex", alignItems:"center", justifyContent:"center" }} />
        </div>
        <div style={{ marginTop:22, fontSize:20, fontFamily:"'Playfair Display',serif", color:"white", minHeight:28 }}>{running ? name : finished ? "Nicely done" : "Ready when you are"}</div>
        <div style={{ fontSize:34, color:"rgba(255,255,255,.85)", minHeight:44, marginTop:4 }}>{running ? Math.max(left, 1) : ""}</div>
        {finished && <div style={{ fontSize:12, color:"rgba(255,255,255,.5)", marginTop:4 }}>Rest for a moment. A sleep story is waiting.</div>}
      </div>
      <button onClick={running ? stop : start} style={{ width:"100%", padding:"14px 0", borderRadius:14, border:"none", fontSize:15, fontWeight:600, cursor:"pointer", background: running ? "rgba(255,255,255,.1)" : "white", color: running ? "white" : "#0d1117" }}>{running ? "Stop" : "Start breathing"}</button>
    </div>
  );
}

function SleepScreen({ go, user }) {
  const [playing, setPlaying] = useState(window._harloAudioId || null);
  const [timerMins, setTimerMins] = useState(window._harloSleepMins || 0);
  const [view, setView] = useState(typeof window._harloAudioId === 'string' ? 'sounds' : 'stories');
  const [, setTick] = useState(0);
  const [pick, setPick] = useState(null);
  useEffect(() => { getTonightPick(user?.id).then(setPick); }, [user?.id]);
  // Keep the screen in sync when a story or the timer ends while it is open
  useEffect(() => {
    const t = setInterval(() => { setPlaying(window._harloAudioId || null); setTick(x => x + 1); }, 1000);
    return () => clearInterval(t);
  }, []);
  const fmtLeft = (m) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m} min`;
  const leftMin = window._harloSleepEnd ? Math.max(1, Math.ceil((window._harloSleepEnd - Date.now()) / 60000)) : 0;

  const stories = [
    { id:7, title:'The Cabin in the Rain', desc:'A warm cabin, a crackling fire and rain on the roof', emoji:'🏡', color:'#1a2a3a', voice:'Male', audio:'/audio/the-cabin-in-the-rain.mp3' },
    { id:8, title:'The Night Train', desc:'A slow overnight journey, rocked gently toward sleep', emoji:'🚂', color:'#141a2a', voice:'Male', audio:'/audio/the-night-train.mp3' },
    { id:9, title:'The Lighthouse Keeper', desc:'Keep the light on a quiet headland, then rest by the sea', emoji:'🗼', color:'#1a2a4a', voice:'Male', audio:'/audio/the-lighthouse-keeper.mp3' },
    { id:10, title:'The Lake at Dusk', desc:'Drift across still water under the first stars', emoji:'🌌', color:'#1a1a3a', voice:'Male', audio:'/audio/the-lake-at-dusk.mp3' },
    { id:11, title:'The Quiet Library', desc:'An old library after hours, with rain on the window', emoji:'📚', color:'#2a2418', voice:'Male', audio:'/audio/the-quiet-library.mp3' },
    { id:12, title:'The Snowed-In Village', desc:'Snow falls softly while a whole village settles down to sleep', emoji:'❄️', color:'#1a2a3a', voice:'Male', audio:'/audio/the-snowed-in-village.mp3' },
    { id:1, title:'What Safety Feels Like', desc:'For anxious evenings — explore the ways you create safety', emoji:'🌙', color:'#1a2a3a', voice:'Female', audio:'/audio/final_product_what_safety_feels_like.mp3' },
    { id:2, title:'Your Inner Seasons', desc:'For times of transition — what season is your heart in?', emoji:'🍂', color:'#2a1a1a', voice:'Male', audio:'/audio/the_complete_inner_season.mp3' },
    { id:3, title:"Your Body's Daily Journey", desc:'Check in with your body and thank it for all it carried today', emoji:'✨', color:'#1a1a3a', voice:'Male', audio:'/audio/your_bodys_daily_journal.mp3' },
    { id:4, title:'Letters to Your Future Self', desc:'Write gentle letters full of wisdom and love to yourself', emoji:'✉️', color:'#2a1a3a', voice:'Female', audio:'/audio/letters_to_yourself_final_product.mp3' },
    { id:5, title:'Your Compassion Practice', desc:'For difficult days — meet yourself with curiosity, not judgment', emoji:'💚', color:'#1a3a1a', voice:'Female', audio:'/audio/compassion_practise_complete_story_2.mp3' },
    { id:6, title:'What Today Taught You', desc:'A gentle journey through your day with curiosity and kindness', emoji:'🌿', color:'#1a3a2a', voice:'Male', audio:'/audio/complete_what_todat_taught_you_male.mp3' },
  ];

  const pickStory = pick ? stories.find(s => s.id === pick.id) : null;

  const togglePlay = (story) => {
    if (playing === story.id) {
      harloHalt();
      harloStopCountdown();
      setPlaying(null);
    } else {
      harloHalt();
      const audio = new Audio(story.audio);
      audio.loop = false;
      audio.volume = 0.6;
      audio.addEventListener("timeupdate", harloCheckSleep);
      audio.addEventListener("ended", () => {
        if (window._harloAudio === audio) { harloHalt(); harloStopCountdown(); }
        logStoryEvent(user?.id, story.id, "complete");
        maybeAskReview(user?.id);
      });
      audio.play().catch(() => {});
      window._harloAudio = audio;
      window._harloAudioId = story.id;
      harloArmTimer(window._harloSleepMins || 0);
      logStoryEvent(user?.id, story.id, "start");
      setPlaying(story.id);
    }
  };

  // Looping background sounds. They share the story player, so only one thing plays at a time.
  const sounds = [
    { id:'rain', title:'Rain', desc:'Steady rain on concrete', emoji:'🌧️', color:'#1a2a3a', audio:'/audio/sounds/rain.mp3' },
    { id:'forest-rain', title:'Forest rain', desc:'Rain through the trees, with distant birds', emoji:'🌲', color:'#1a3a2a', audio:'/audio/sounds/forest-rain.mp3' },
    { id:'fireplace', title:'Fireplace', desc:'A warm, crackling fire', emoji:'🔥', color:'#3a2a1a', audio:'/audio/sounds/fireplace.mp3' },
    { id:'ocean', title:'Ocean waves', desc:'Gentle waves and soft ambient music', emoji:'🌊', color:'#1a2a4a', audio:'/audio/sounds/ocean.mp3' },
    { id:'forest-wind', title:'Forest wind', desc:'Wind moving through the pines', emoji:'🍃', color:'#2a3a2a', audio:'/audio/sounds/forest-wind.mp3' },
    { id:'night-rain', title:'Night rain', desc:'Deep, dark rain in the distance', emoji:'🌑', color:'#141a2a', audio:'/audio/sounds/night-rain.mp3' },
    { id:'campfire', title:'Campfire', desc:'Close, crackling firewood', emoji:'🪵', color:'#3a2418', audio:'/audio/sounds/campfire.mp3' },
    { id:'wind-chimes', title:'Wind chimes', desc:'Soft chimes on a quiet breeze', emoji:'🎐', color:'#2a2a3a', audio:'/audio/sounds/wind-chimes.mp3' },
    { id:'sleep-music', title:'Sleep music', desc:'Slow, soft ambient music', emoji:'🎶', color:'#2a1a3a', audio:'/audio/sounds/sleep-music.mp3' },
  ];
  const togglePlaySound = (snd) => {
    if (playing === snd.id) {
      harloHalt(); harloStopCountdown(); setPlaying(null);
      return;
    }
    harloHalt();
    const audio = new Audio(snd.audio);
    audio.loop = true;
    audio.volume = 0.6;
    audio.addEventListener("timeupdate", harloCheckSleep);
    audio.play().catch(() => {});
    window._harloAudio = audio;
    window._harloAudioId = snd.id;
    // A looping sound never ends by itself, so it always gets a timer (60 min unless another is chosen)
    const mins = window._harloSleepMins || 60;
    harloArmTimer(mins);
    setTimerMins(mins);
    setPlaying(snd.id);
  };

  return (
    <div className="screen" style={{ background:'#0d1117' }}>
      <div style={{ padding:'52px 20px 20px', background:'#0d1117' }}>
        <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', marginBottom:6, cursor:'pointer' }} onClick={() => go('home')}>back</div>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:'white', fontWeight:600 }}>Sleep Stories</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,.45)', marginTop:4 }}>
          {playing ? '🎵 Playing — audio continues as you navigate' : 'Tap a story to begin'}
        </div>
      </div>
      <div style={{ padding:'0 16px 100px', flex:1, overflowY:'auto' }}>
        {view !== 'breathe' && (
        <div style={{ background:'#1a1f2e', borderRadius:16, padding:'14px 16px', marginBottom:12 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'white' }}>⏱ Sleep timer</div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.5)' }}>
              {leftMin ? `Fades out in ${fmtLeft(leftMin)}` : timerMins ? 'Starts when a story plays' : 'Off'}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[0, 15, 30, 60, 90, 120, 180].map(m => (
              <button key={m} onClick={() => { harloArmTimer(m); setTimerMins(m); }} style={{ flex:'1 1 20%', padding:'8px 0', borderRadius:10, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: timerMins === m ? 'white' : 'rgba(255,255,255,.1)', color: timerMins === m ? '#0d1117' : 'white' }}>{m === 0 ? 'Off' : m >= 120 ? `${m / 60}h` : `${m} min`}</button>
            ))}
          </div>
        </div>
        )}
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          {[['stories','🌙 Stories'],['sounds','🌧️ Sounds'],['breathe','🫁 Breathe']].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} style={{ flex:1, padding:'10px 0', borderRadius:12, border:'none', fontSize:13, fontWeight:600, cursor:'pointer', background: view === k ? 'white' : 'rgba(255,255,255,.08)', color: view === k ? '#0d1117' : 'rgba(255,255,255,.7)' }}>{label}</button>
          ))}
        </div>
        {view === 'stories' && pickStory && (
          <div onClick={() => togglePlay(pickStory)} style={{ background:'linear-gradient(135deg,#1e2c3f 0%,#2a1f3d 100%)', borderRadius:16, padding:'14px 16px', marginBottom:14, border:'1px solid rgba(255,255,255,.15)', cursor:'pointer' }}>
            <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.1em', color:'rgba(255,255,255,.5)', fontWeight:600, marginBottom:8 }}>Picked for tonight</div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ fontSize:26 }}>{pickStory.emoji}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:15, fontWeight:600, color:'white' }}>{pickStory.title}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,.6)', marginTop:2, lineHeight:1.5 }}>{pick.why}</div>
              </div>
              <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, color:'white', flexShrink:0 }}>{playing === pickStory.id ? '⏸' : '▶'}</div>
            </div>
          </div>
        )}
        {view === 'stories' && stories.map(s => (
          <div key={s.id} onClick={() => togglePlay(s)} style={{ background: playing===s.id ? s.color : '#1a1f2e', borderRadius:16, padding:'18px 16px', marginBottom:12, border: playing===s.id ? '1px solid rgba(255,255,255,.2)' : '1px solid rgba(255,255,255,.06)', cursor:'pointer', transition:'all .3s' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:52, height:52, borderRadius:14, background: playing===s.id ? 'rgba(255,255,255,.15)' : s.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>{s.emoji}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:15, fontWeight:600, color:'white', marginBottom:3 }}>{s.title}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,.5)', lineHeight:1.5 }}>{s.desc}</div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,.3)', marginTop:4 }}>🎙 {s.voice} voice</div>
              </div>
              <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, color:'white', flexShrink:0 }}>
                {playing===s.id ? '⏸' : '▶'}
              </div>
            </div>
            {playing===s.id && (
              <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:4 }}>
                {[1,2,3,4,5,6,7,8,9,10].map(i => (
                  <div key={i} style={{ flex:1, height:`${6 + Math.abs(Math.sin(i * 0.8)) * 12}px`, borderRadius:3, background:'rgba(255,255,255,.35)', animation:`bo ${0.7 + i*0.08}s infinite` }} />
                ))}
              </div>
            )}
          </div>
        ))}
        {view === 'breathe' && <BreathePanel />}
        {view === 'sounds' && sounds.map(s => (
          <div key={s.id} onClick={() => togglePlaySound(s)} style={{ background: playing===s.id ? s.color : '#1a1f2e', borderRadius:16, padding:'16px', marginBottom:12, border: playing===s.id ? '1px solid rgba(255,255,255,.2)' : '1px solid rgba(255,255,255,.06)', cursor:'pointer' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:14, background: playing===s.id ? 'rgba(255,255,255,.15)' : s.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{s.emoji}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:15, fontWeight:600, color:'white', marginBottom:3 }}>{s.title}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,.5)', lineHeight:1.5 }}>{s.desc}</div>
              </div>
              <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, color:'white', flexShrink:0 }}>{playing===s.id ? '⏸' : '▶'}</div>
            </div>
          </div>
        ))}
        {playing && (
          <div onClick={() => { harloHalt(); harloStopCountdown(); setPlaying(null); }} style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:'14px 16px', textAlign:'center', cursor:'pointer', border:'1px solid rgba(255,255,255,.08)' }}>
            <div style={{ fontSize:13, color:'rgba(255,255,255,.6)', fontWeight:600 }}>⏹ Stop all audio</div>
          </div>
        )}
      </div>
      <Nav active="sleep" go={go} />
    </div>
  );
}


function LetterScreen({ go, user }) {
  const [tab, setTab] = useState('write');
  const [content, setContent] = useState('');
  const [deliverDays, setDeliverDays] = useState(30);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openLetter, setOpenLetter] = useState(null);
  const today = new Date().toLocaleDateString('en-AU', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  const deliverDate = new Date(Date.now() + deliverDays * 86400000).toLocaleDateString('en-AU', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

  useEffect(() => {
    if (tab === 'inbox') {
      setLoading(true);
      supabase.from('letters_to_self').select('*').eq('user_id', user.id).order('created_at', { ascending: false })
        .then(({ data }) => { setLetters(data || []); setLoading(false); });
    }
  }, [tab, user.id]);

  const send = async () => {
    if (!content.trim()) return;
    setSending(true);
    const deliver_at = new Date(Date.now() + deliverDays * 86400000).toISOString();
    await supabase.from('letters_to_self').insert({ user_id: user.id, content: content.trim(), deliver_at, delivered: false });
    // Notify when the letter arrives (iOS app only). Asks for permission here if it hasn't been asked yet.
    try {
      const LN = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.LocalNotifications;
      if (LN) {
        try { await LN.cancel({ notifications: [{ id: 1020 }] }); localStorage.setItem(`harlo_letter_nudge_${user.id}`, "skipped"); } catch (e) { }
        let perm = await LN.checkPermissions();
        if (perm.display === 'prompt' || perm.display === 'prompt-with-rationale') perm = await LN.requestPermissions();
        if (perm.display === 'granted') {
          await LN.schedule({ notifications: [{
            id: Math.floor(new Date(deliver_at).getTime() / 1000),
            title: 'A letter from past you has arrived ✉️',
            body: "Open it whenever you're ready.",
            schedule: { at: new Date(deliver_at) },
            sound: 'default',
          }]});
        }
      }
    } catch (e) { }
    setSending(false);
    setDone(true);
    setContent('');
    setTimeout(() => { setDone(false); setTab('inbox'); }, 2500);
  };

  const isReady = (l) => new Date(l.deliver_at) <= new Date();

  return (
    <div className="screen" style={{ background:'#f7f3ee' }}>
      <div style={{ background:'#1a2a1a', padding:'52px 20px 24px' }}>
        <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', marginBottom:8, cursor:'pointer' }} onClick={() => go('home')}>← home</div>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:'white', fontWeight:600 }}>Letters to Yourself</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,.45)', marginTop:4 }}>Words from you, to you</div>
      </div>

      <div style={{ display:'flex', gap:0, background:'#e8e2da', margin:'0', padding:3 }}>
        {[['write','✍️ Write a letter'],['inbox','✉️ My letters']].map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ flex:1, padding:'9px 0', fontSize:12, fontWeight:600, borderRadius:8, background:tab===t?'white':'transparent', color:tab===t?'#1a2a1a':'#888', border:'none' }}>{l}</button>
        ))}
      </div>

      <div style={{ padding:'16px 16px 100px', flex:1, overflowY:'auto' }}>
        {tab === 'write' && (
          <div>
            <div style={{ background:'white', borderRadius:16, padding:'24px 20px', marginBottom:16, border:'1px solid #e0dbd4', boxShadow:'0 2px 12px rgba(0,0,0,0.04)' }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:13, color:'#888', marginBottom:20, textAlign:'right' }}>{today}</div>

              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, color:'#2D5A4F', fontWeight:600, marginBottom:16 }}>Dear future me,</div>

              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={"Right now I am feeling...\n\nWhat I hope for you is...\n\nSomething I want you to remember..."}
                style={{ width:'100%', minHeight:280, border:'none', outline:'none', fontFamily:"'Playfair Display',serif", fontSize:15, fontStyle:'italic', lineHeight:2, color:'#2C2A28', background:'transparent', resize:'none', borderBottom:'1px dashed #d0cac2', paddingBottom:16, marginBottom:16 }}
              />

              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, fontStyle:'italic', color:'#888', textAlign:'right' }}>With love,</div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, color:'#2D5A4F', fontWeight:600, textAlign:'right', marginTop:4 }}>{user?.user_metadata?.display_name || 'Me'}</div>
            </div>

            <div style={{ background:'white', borderRadius:14, padding:'16px', marginBottom:16, border:'1px solid #e0dbd4' }}>
              <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'.08em', color:'#888', marginBottom:12 }}>Seal and deliver in</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:14 }}>
                {[
                  { d:7, l:'1 week' },
                  { d:30, l:'1 month' },
                  { d:90, l:'3 months' },
                  { d:180, l:'6 months' },
                  { d:365, l:'1 year' },
                  { d:730, l:'2 years' },
                  { d:1825, l:'5 years' },
                  { d:3650, l:'10 years' },
                ].map(({ d, l }) => (
                  <button key={d} onClick={() => setDeliverDays(d)} style={{ padding:'10px 4px', borderRadius:10, border:deliverDays===d?'2px solid #2D5A4F':'1px solid #e0dbd4', background:deliverDays===d?'#2D5A4F':'white', color:deliverDays===d?'white':'#666', fontSize:11, fontWeight:600, cursor:'pointer' }}>{l}</button>
                ))}
              </div>
              <div style={{ background:'#f0f7f0', borderRadius:10, padding:'12px 14px', textAlign:'center' }}>
                <div style={{ fontSize:11, color:'#4A7C6F', marginBottom:2 }}>This letter will be sealed until</div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, fontWeight:600, color:'#2D5A4F' }}>{deliverDate}</div>
              </div>
            </div>

            {done && (
              <div style={{ background:'#f0f7f0', border:'1px solid #2D5A4F', borderRadius:12, padding:'16px', textAlign:'center', marginBottom:16 }}>
                <div style={{ fontSize:20, marginBottom:6 }}>🌿</div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, color:'#2D5A4F', fontStyle:'italic' }}>Letter sealed. We'll let you know when it arrives.</div>
              </div>
            )}

            <button onClick={send} disabled={sending || done || !content.trim()} style={{ width:'100%', padding:'15px', borderRadius:12, border:'none', background: content.trim() ? '#1a2a1a' : '#ccc', color:'white', fontSize:15, fontWeight:600, fontFamily:"'Playfair Display',serif", cursor: content.trim() ? 'pointer' : 'not-allowed', letterSpacing:'.5px' }}>
              {sending ? 'Sealing your letter...' : done ? '✓ Letter sealed' : 'Seal & send to future me'}
            </button>
          </div>
        )}

        {tab === 'inbox' && (
          <div>
            {loading && <div style={{ textAlign:'center', padding:40 }}><Spinner /></div>}
            {!loading && letters.length === 0 && (
              <div style={{ textAlign:'center', padding:'48px 20px' }}>
                <div style={{ fontSize:48, marginBottom:16 }}>✉️</div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, color:'#333', marginBottom:8 }}>No letters yet</div>
                <div style={{ fontSize:13, color:'#888', lineHeight:1.6 }}>Write yourself a letter — your future self will thank you.</div>
              </div>
            )}
            {letters.map(l => (
              <div key={l.id} style={{ background:'white', borderRadius:16, padding:'18px', marginBottom:12, border: isReady(l) ? '1px solid #2D5A4F' : '1px solid #e0dbd4', opacity: isReady(l) ? 1 : 0.75, cursor: isReady(l) ? 'pointer' : 'default' }} onClick={() => isReady(l) && setOpenLetter(openLetter===l.id ? null : l.id)}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:12, background: isReady(l) ? '#f0f7f0' : '#f5f5f5', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                    {isReady(l) ? '✉️' : '🔒'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, fontWeight:600, color: isReady(l) ? '#2D5A4F' : '#333' }}>
                      {isReady(l) ? 'A letter from your past self' : 'Sealed letter'}
                    </div>
                    <div style={{ fontSize:11, color:'#888', marginTop:3 }}>
                      {isReady(l)
                        ? `Written ${new Date(l.created_at).toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })}`
                        : `Opens ${new Date(l.deliver_at).toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })}`}
                    </div>
                  </div>
                  {isReady(l) && <div style={{ fontSize:12, color:'#2D5A4F' }}>{openLetter===l.id ? '▲' : '▼'}</div>}
                </div>

                {isReady(l) && openLetter===l.id && (
                  <div style={{ marginTop:20, paddingTop:20, borderTop:'1px dashed #d0cac2' }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:13, color:'#888', marginBottom:16, textAlign:'right' }}>
                      {new Date(l.created_at).toLocaleDateString('en-AU', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
                    </div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, color:'#2D5A4F', fontWeight:600, marginBottom:16 }}>Dear future me,</div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, fontStyle:'italic', lineHeight:2, color:'#2C2A28', whiteSpace:'pre-wrap' }}>{l.content}</div>
                    <div style={{ marginTop:24, textAlign:'right' }}>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:14, fontStyle:'italic', color:'#888' }}>With love,</div>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, color:'#2D5A4F', fontWeight:600 }}>Past you</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Nav active="home" go={go} />
    </div>
  );
}


function EulaScreen({ onAccept }) {
  return (
    <div className="screen" style={{ background:'#2D5A4F' }}>
      <div style={{ padding:'52px 20px 32px', flex:1, overflowY:'auto' }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:28, color:'white', fontWeight:600, marginBottom:8 }}>Welcome to Harlo</div>
        <div style={{ fontSize:13, color:'rgba(255,255,255,.65)', marginBottom:28, lineHeight:1.6 }}>Before you join our community, please read and agree to our terms.</div>
        <div style={{ background:'rgba(255,255,255,.1)', borderRadius:16, padding:20, marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'white', marginBottom:10 }}>Community Guidelines</div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,.7)', lineHeight:1.8 }}>
            By using Harlo you agree to:<br/><br/>
            ✦ Treat all members with kindness and respect<br/>
            ✦ Not post harmful, abusive or offensive content<br/>
            ✦ Not harass, bully or threaten other users<br/>
            ✦ Report content that violates these guidelines<br/>
            ✦ We have zero tolerance for objectionable content<br/><br/>
            We review all reports within 24 hours and will remove content and users who violate these terms.
          </div>
        </div>
        <div style={{ background:'rgba(255,255,255,.1)', borderRadius:16, padding:20, marginBottom:28 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'white', marginBottom:10 }}>Privacy & Data</div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,.7)', lineHeight:1.8 }}>
            Your journal entries are private and only visible to you. Community posts are visible to other Harlo members. We never sell your personal data. For support visit harlo-app.vercel.app/support.html
          </div>
        </div>
        <button onClick={onAccept} className="pbtn" style={{ background:'white', color:'#2D5A4F', fontSize:16, padding:'15px 0', fontWeight:700, marginBottom:12 }}>
          I agree — take me to Harlo
        </button>
        <div style={{ textAlign:'center', fontSize:11, color:'rgba(255,255,255,.4)' }}>By tapping above you agree to our Terms of Use and Community Guidelines</div>
      </div>
    </div>
  );
}

function AiConsentModal({ onAnswer }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(10,16,20,.9)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: C.paper, borderRadius: 20, padding: "26px 22px", maxWidth: 380, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 30, marginBottom: 10 }}>🌙</div>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 600, color: C.ink, marginBottom: 10 }}>Let Harlo reply to your entries?</div>
        <div style={{ fontSize: 13, color: C.mid, lineHeight: 1.7, marginBottom: 12 }}>To write a reply, Harlo sends the text of your entry (and, for weekly insights, your mood notes) to Anthropic, the company behind the Claude AI. We only send it to write your reply.</div>
        <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.7, marginBottom: 18 }}>Replies can be wrong and are not medical advice. You can still journal without them, and you can change this any time in Profile. Details are in the Privacy policy.</div>
        <button className="pbtn" onClick={() => onAnswer(true)} style={{ marginBottom: 10 }}>Allow AI replies</button>
        <button onClick={() => onAnswer(false)} style={{ background: "none", color: C.mid, fontSize: 13, padding: "8px 0", fontWeight: 600 }}>Not now</button>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(undefined);
  const consentResolver = useRef(null);
  const [showConsent, setShowConsent] = useState(false);
  useEffect(() => {
    _consentAsker = () => new Promise(resolve => { consentResolver.current = resolve; setShowConsent(true); });
    return () => { _consentAsker = null; };
  }, []);
  const answerConsent = (ok) => {
    setShowConsent(false);
    if (consentResolver.current) consentResolver.current(ok);
    consentResolver.current = null;
  };
  const [splashDone, setSplashDone] = useState(true);
  useEffect(() => { setSplashDone(true); }, []);
  // Cancel reminders for features that are switched off, so hidden features never notify
  useEffect(() => {
    const LN = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.LocalNotifications;
    if (!LN) return;
    const ids = [];
    if (!FEATURES.morningReminder) ids.push({ id: 1002 });
    if (!FEATURES.medication) ids.push({ id: 1003 });
    if (ids.length) { try { Promise.resolve(LN.cancel({ notifications: ids })).catch(() => {}); } catch (e) {} }
  }, []);
  const [screen, setScreen] = useState("home");
  const [activeShareId, setActiveShareId] = useState(null);
  const [activeMyShareId, setActiveMyShareId] = useState(null);
  const [cheers, setCheers] = useState([]);
  const [rk, setRk] = useState(0);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [isReset, setIsReset] = useState(() => window.location.hash.includes("type=recovery"));
  
  const [eulaAccepted, setEulaAccepted] = useState(() => !!localStorage.getItem("harlo_eula"));

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") { setIsReset(true); return; }
      setSession(s);
      if (s) {
        if (FEATURES.cheers) loadCheers(s.user.id);
        supabase.from("profiles").select("has_onboarded").eq("id", s.user.id).single()
          .then(({ data }) => {
            if (!data?.has_onboarded) { setNeedsOnboarding(true); return; }
            registerPush(s.user.id);
            // Re-apply the evening reminder and win-back nudges on every open
            refreshReminders(s.user.id);
          });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const registerPush = async (userId) => {
    try {
      // Native (iOS/Android via Capacitor): use APNs/FCM
      if (Capacitor.isNativePlatform()) {
        const permResult = await PushNotifications.requestPermissions();
        if (permResult.receive !== "granted") {
          console.log("Push permission not granted:", permResult.receive);
          return;
        }

        // Attach listeners FIRST, then call register() so we don't miss the event
        await PushNotifications.addListener("registration", async (token) => {
          console.log("Push registration token received:", token.value.substring(0, 20) + "...");
          const platform = Capacitor.getPlatform();
          const { error } = await supabase.from("push_tokens").upsert(
            { user_id: userId, token: token.value, platform, updated_at: new Date().toISOString() },
            { onConflict: "token" }
          );
          if (error) {
            console.error("Failed to save push token to Supabase:", error);
          } else {
            console.log("Push token saved to Supabase for user:", userId);
          }
        });

        await PushNotifications.addListener("registrationError", (err) => {
          console.error("Push registration error:", err);
        });

        await PushNotifications.addListener("pushNotificationReceived", (notification) => {
          console.log("Push received in foreground:", notification);
          // When app is open in foreground, iOS hides the banner.
          // We could optionally show an in-app toast here in future.
        });

        await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
          console.log("Push tapped:", action);
          try {
            const data = action?.notification?.data || {};
            if (data.type === "mood_share" && data.share_id) {
              setActiveShareId(data.share_id);
              setScreen(safeScreen("moodShare"));
            }
          } catch (e) {
            console.error("Deep link handler error:", e);
          }
        });

        // Now register — listeners are ready to catch the registration event
        await PushNotifications.register();
        console.log("PushNotifications.register() called");

        return;
      }

      // Web fallback: use web push (only runs in browser, not in iOS/Android app)
      if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      const VAPID_PUBLIC = "BEa9hgnpolYA0W-9vWgfR5aKkBpO5tT0y1I6dsAoHVeNHPWeqVhRbuDbDejV_BfxlOCrJo5ghzG1FZe_8TK188M";
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: VAPID_PUBLIC
      });
      await supabase.from("push_subscriptions").upsert({ user_id: userId, subscription: sub.toJSON() });
    } catch (e) {
      console.error("registerPush error:", e);
    }
  };

  const getCapacitorNotifications = () => {
    try {
      const LN = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.LocalNotifications;
      return LN || null;
    } catch (e) {
      return null;
    }
  };

  const scheduleDailyPrompt = async (timeStr) => {
    try {
      const title = "Wind down with Harlo 🌙";
      const body = "A quick check-in, then a sleep story.";
      const [hours, minutes] = timeStr.split(":").map(Number);
      const now = new Date();
      const target = new Date();
      target.setHours(hours, minutes, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);

      const LN = getCapacitorNotifications();
      if (LN) {
        const perm = await LN.requestPermissions();
        await LN.cancel({ notifications: [{ id: 1001 }] });
        try {
          // Repeats every day at this time, even if the app is never reopened
          await LN.schedule({ notifications: [{
            id: 1001, title, body,
            schedule: { on: { hour: hours, minute: minutes }, allowWhileIdle: true },
            sound: "default",
          }]});
        } catch(schedErr) { }
      } else {
        const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
        if (perm !== "granted") return;
        const msUntil = target - now;
        if (window._harloJournalTimer) clearTimeout(window._harloJournalTimer);
        window._harloJournalTimer = setTimeout(() => {
          new Notification(title, { body, icon: "/logo192.png" });
          scheduleDailyPrompt(timeStr);
        }, msUntil);
      }
    } catch (e) { }
  };

  // Gentle win-back nudges for people who stop opening the app (native only).
  // Re-scheduled on every app open, so they only fire after 3 / 7 quiet days.
  // Only for people who have the evening reminder on, and only if notifications are already allowed.
  const scheduleWinBack = async (on) => {
    try {
      const LN = getCapacitorNotifications();
      if (!LN) return;
      await LN.cancel({ notifications: [{ id: 1010 }, { id: 1011 }] });
      if (!on) return;
      const perm = await LN.checkPermissions();
      if (perm.display !== "granted") return;
      const eveningIn = (days) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(19, 0, 0, 0); return d; };
      await LN.schedule({ notifications: [
        { id: 1010, title: "Your stories are here 🌙", body: "No pressure. A calm moment is waiting whenever you're ready.", schedule: { at: eveningIn(3) }, sound: "default" },
        { id: 1011, title: "Thinking of you 🌙", body: "Tonight could be a good night for a sleep story.", schedule: { at: eveningIn(7) }, sound: "default" },
      ]});
    } catch (e) { }
  };

  // One-time nudge to write a letter to your future self, about 5 days after we first schedule it.
  // Never repeats. Skipped if the person already has a letter, has reminders off, or hasn't allowed notifications.
  const scheduleLetterNudge = async (userId) => {
    try {
      const LN = getCapacitorNotifications();
      if (!LN) return;
      const key = `harlo_letter_nudge_${userId}`;
      if (localStorage.getItem(key)) return;
      const { count } = await supabase.from("letters_to_self").select("*", { count: "exact", head: true }).eq("user_id", userId);
      if (count > 0) { localStorage.setItem(key, "skipped"); return; }
      const perm = await LN.checkPermissions();
      if (perm.display !== "granted") return;
      const at = new Date();
      at.setDate(at.getDate() + 5);
      at.setHours(19, 0, 0, 0);
      await LN.schedule({ notifications: [{
        id: 1020, title: "Write a letter to future you ✉️",
        body: "A few quiet minutes tonight. It will arrive whenever you choose.",
        schedule: { at }, sound: "default",
      }]});
      localStorage.setItem(key, "scheduled");
    } catch (e) { }
  };

  // Re-applies the evening reminder and the nudges from the saved settings
  const refreshReminders = (userId) => {
    supabase.from("reminders").select("journal_on, journal_time").eq("user_id", userId).single()
      .then(({ data: r }) => {
        if (r?.journal_on && r?.journal_time) { scheduleDailyPrompt(r.journal_time); scheduleWinBack(true); scheduleLetterNudge(userId); }
        else scheduleWinBack(false);
      });
  };

  const scheduleMorningMotivation = async (timeStr, quote) => {
    try {
      const MOTIVATIONS = [
        "You are doing better than you think. Keep going. 🌿",
        "Every small step forward still counts. 💚",
        "You showed up yesterday. Show up today too. ✨",
        "It's okay to take things one moment at a time. 🌱",
        "Your feelings are valid. So is your strength. 💛",
        "You are not behind. You are exactly where you need to be. 🌤",
        "Rest is productive. Kindness to yourself is productive. 🍃",
        "Today doesn't have to be perfect to be good. 🌸",
        "You've made it through every hard day so far. 💪",
        "Small things done with care still matter. ✦",
        "Be gentle with yourself today. You deserve it. 🌿",
        "Progress isn't always visible — but it's still happening. 🌱",
        "You are enough, exactly as you are right now. 💚",
        "What you're carrying is heavy. You're still standing. ✨",
        "Today is a new chance to be kind to yourself. 🌤",
        "You matter more than you realise. 💛",
        "Take a breath. You've got this. 🍃",
        "Healing isn't linear — and that's okay. 🌸",
        "Your best looks different every day — and that's fine. 💪",
        "One good moment can change the whole day. ✦",
        "You don't have to have it all figured out. Just today. 🌿",
        "Asking for help is a sign of strength, not weakness. 💚",
        "You are allowed to outgrow old versions of yourself. ✨",
        "Whatever you're feeling right now — it's allowed. 🌱",
        "You've survived 100% of your hardest days. 💛",
        "The fact that you care so much is one of your greatest gifts. 🌤",
        "Your story isn't over. Not even close. 🍃",
        "Something good is still possible today. 🌸",
        "You are worthy of the same compassion you give others. 💪",
        "Even slow progress is still progress. ✦",
        "You don't have to earn rest. Rest is a right. 🌿",
        "It's okay if today looks different from yesterday. 💚",
        "The way you keep going — even when it's hard — is incredible. ✨",
        "You are not your worst day. 🌱",
        "There is room for joy, even in difficult seasons. 💛",
        "You are more resilient than you give yourself credit for. 🌤",
        "Today, just do what you can. That's enough. 🍃",
        "Someone today is grateful you exist. 🌸",
        "You are allowed to take up space. 💪",
        "Be patient with yourself — growth takes time. ✦",
        "You're not alone, even when it feels that way. 🌿",
        "Your sensitivity is a strength, not a flaw. 💚",
        "One breath at a time. One step at a time. ✨",
        "You are seen. You are heard. You matter. 🌱",
        "It's okay to have a quiet day. Not every day needs to be productive. 💛",
        "You are building something beautiful, even if you can't see it yet. 🌤",
        "Comparison is the thief of joy — stay in your own lane. 🍃",
        "You are allowed to change your mind, your plans, your path. 🌸",
        "What you're going through is real. And you're handling it. 💪",
        "Today, choose kindness — starting with yourself. ✦",
        "You have everything you need to get through today. 🌿",
      ];
      const dayOfYear = Math.floor(Date.now() / 86400000);
      const motivation = quote || MOTIVATIONS[dayOfYear % MOTIVATIONS.length];
      const [hours, minutes] = timeStr.split(":").map(Number);
      const now = new Date();
      const target = new Date();
      target.setHours(hours, minutes, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);

      const LN = getCapacitorNotifications();
      if (LN) {
        const perm = await LN.requestPermissions();
        await LN.cancel({ notifications: [{ id: 1002 }] });
        try {
          await LN.schedule({ notifications: [{
            id: 1002, title: "Good morning ✨", body: motivation,
            schedule: { at: target },
            sound: "default",
          }]});
        } catch(e) { }
      } else {
        const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
        if (perm !== "granted") return;
        const msUntil = target - now;
        if (window._harloMotivationTimer) clearTimeout(window._harloMotivationTimer);
        window._harloMotivationTimer = setTimeout(() => {
          new Notification("Good morning ✨", { body: motivation, icon: "/logo192.png" });
          scheduleMorningMotivation(timeStr);
        }, msUntil);
      }
    } catch (e) { }
  };

  const scheduleMedicationReminder = async (timeStr) => {
    try {
      const [hours, minutes] = timeStr.split(":").map(Number);
      const now = new Date();
      const target = new Date();
      target.setHours(hours, minutes, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);

      const LN = getCapacitorNotifications();
      if (LN) {
        await LN.requestPermissions();
        await LN.cancel({ notifications: [{ id: 1003 }] });
        await LN.schedule({ notifications: [{
          id: 1003, title: "💊 Medication reminder", body: "Have you taken your medication today? Tap to log it in Harlo.",
          schedule: { at: target },
          sound: "default",
        }]});
      } else {
        if (!("Notification" in window) || Notification.permission !== "granted") return;
        const msUntil = target - now;
        if (window._harloMedTimer) clearTimeout(window._harloMedTimer);
        window._harloMedTimer = setTimeout(() => {
          new Notification("💊 Medication reminder", { body: "Have you taken your medication today?", icon: "/logo192.png" });
          scheduleMedicationReminder(timeStr);
        }, msUntil);
      }
    } catch (e) { }
  };

  const loadCheers = async uid => {
    const { data } = await supabase.from("cheers").select("*, profiles!cheers_from_user_id_fkey(username, display_name)").eq("to_user_id", uid).order("created_at", { ascending: false });
    setCheers(data || []);
  };

  const go = useCallback(s => setScreen(safeScreen(s)), []);
  const onSave = () => setRk(k => k + 1);

  if (!splashDone) return (
    <>
      <style>{css}</style>
      <div className="phone" style={{ alignItems: "center", justifyContent: "center", background: C.roseDk }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 44, color: "white", fontWeight: 600, marginBottom: 24 }}>Harlo</div>
        <Spinner color="#F4C0D1" />
      </div>
    </>
  );

  if (isReset) return <><style>{css}</style><div className="phone"><ResetPasswordScreen onDone={() => setIsReset(false)} /></div></>;
  if (!session) return <><style>{css}</style><div className="phone"><AuthScreen /></div></>;
  if (needsOnboarding) return <><style>{css}</style><div className="phone"><OnboardingScreen user={session.user} scheduleDailyPrompt={scheduleDailyPrompt} onDone={() => { setNeedsOnboarding(false); registerPush(session.user.id); refreshReminders(session.user.id); }} /></div></>;

  const u = session.user;
  const nightNow = (() => { const h = new Date().getHours(); return h >= 19 || h < 5; })();
  return (
    <>
      <style>{css}</style>
      <div className={`phone${nightNow ? " night" : ""}`}>
        {showConsent && <AiConsentModal onAnswer={answerConsent} />}
        {screen === "home"      && <HomeScreen go={go} user={u} cheers={cheers} />}
        {screen === "entry"     && <EntryScreen go={go} user={u} onSave={onSave} />}
        {screen === "reflect"   && <ReflectScreen go={go} user={u} rk={rk} />}
        {screen === "share"     && <ShareScreen go={go} user={u} />}
        {screen === "cheers"    && <CheersScreen go={go} user={u} cheers={cheers} setCheers={setCheers} />}
        {screen === "sendCheer" && <SendCheerScreen go={go} user={u} />}
        {screen === "reminders" && <RemindersScreen go={go} user={u} scheduleDailyPrompt={scheduleDailyPrompt} scheduleMorningMotivation={scheduleMorningMotivation} scheduleMedicationReminder={scheduleMedicationReminder} scheduleWinBack={scheduleWinBack} />}
        {screen === "crisis"    && <CrisisScreen go={go} user={u} />}
        {screen === "community" && !eulaAccepted && <EulaScreen onAccept={() => { localStorage.setItem("harlo_eula","1"); setEulaAccepted(true); }} />}
        {screen === "community" && eulaAccepted && <CommunityScreen go={go} user={u} />}
        {screen === "sleep" && <SleepScreen go={go} user={u} />}
        {screen === "letter"    && <LetterScreen go={go} user={u} />}
        {screen === "insights"  && <InsightsScreen go={go} user={u} />}
        {screen === "profile"   && <ProfileScreen go={go} user={u} />}
        {screen === "circle"    && <CircleScreen go={go} user={u} />}
        {screen === "moodInbox" && <MoodSharesInboxScreen go={go} user={u} setActiveShareId={setActiveShareId} />}
        {screen === "moodShare" && <MoodShareScreen go={go} user={u} activeShareId={activeShareId} />}
        {screen === "myShares" && <MyMoodSharesScreen go={go} user={u} setActiveMyShareId={setActiveMyShareId} />}
        {screen === "myShareDetail" && <MyShareDetailScreen go={go} user={u} activeMyShareId={activeMyShareId} />}
        {screen === "privacy"   && <PrivacyScreen go={go} />}
        {screen === "terms"     && <TermsScreen go={go} />}
      </div>
    </>
  );
}
